/* Shopkeeper Transitive-Reactive Domain Model

Developer Console Debugging Features:
  - entire model available at sk.model
  - set sk.model.logging = true in production to see API req/res
  - sk.model.sysParams
  - sk.model.customerParams
  - sk.model.ppcParams
  - sk.model.lineItems[n]; // nth line item mobx observable
  - sk.model.lineItems[n].detail // show DBPIP part detail for nth line item
  - sk.model.lineItems[n].detailSupplemental // DBPIPHELPER additional info
  - sk.model.lastReq/Res // various API requests (see console debugging code section)

  Apologies to future maintainers for any naming incongruencies such as
  confirmationNo vs confirmationNumber.  This was due to the JSON I received
  from the legacy system which differed.  Typescript should assist greatly.

  Async actions + Typescript inference: https://github.com/mobxjs/mobx/issues/790

  10/2/19 - Vic clarified "non-stocking" is a bit of a misnomer as it's used at DSI since
  you can technically have a part in inventory which is not stocked due to low demand.
  The original usage of this phrase throughout the codebase (in comments, variable names, etc.)
  was to indicate an @part which is not kept in inventory (an emergency/NOW po purchase, for example).
  Most people within DSI refer to that case as a "non-stocking part", as well.  However, that
  usage of the term conflates with the aforementioned case where a part IS in inventory, but not
  stocked. Thus Vic asked to see the latter scenario described as "Non-Tracked" within the UI.
  We will not, however, change all existing code to be pedantic. Thus, in the code / comments,
  assume "non stocking" references refers to the latter case.

  3/18/20 - You may see some instances where an observable ID is disconnected from its
  supplemental detail information.  This was due to how MobX 4.0 worked (wherein an observable
  property must already exist on an object at creation time in order to be observed). Starting with
  MobX 5.0, ES6 proxy objects are utilized, and ex-post property additions are still observed.
  I'll refactor the former cases when I have nothing better to do.
*/
import _ from 'lodash';
import { action, computed, observable, runInAction } from 'mobx';
import hash from 'object-hash';
import moment from 'moment';
import Queue from 'promise-queue';

import toastr from '../../toastr';
import modal from '../typings/Modal';

import { Api, IApiResponse } from '../typings/Api';
import { DBPIPResponseType, IDbpipResponse } from '../typings/DBPIPResponse';
import CustomerLookup from '../components/lookups/CustomerLookup';
import { scrollInvoiceBuilder } from '../components/invoicebuilder/InvoiceBuilder';
import {
  IConfirmationPlayback,
  IConfirmationPlaybackDetail,
  IConfirmationStatusDetail,
  IPartOnBackorder,
  ConfirmationSaleAction,
} from '../typings/Confirmation';
import { ICouponDetail } from '../typings/CouponDetail';
import {
  ICreditPartLine,
  ICreditStateMap,
  ICustomerCoreBankItem,
  ICustomerInvoiceItem,
} from '../typings/CustomerHistory';
import { ICustomerParams } from '../typings/CustomerParameters';
import { IDefaultPart } from '../typings/DefaultPart';
import {
  IBranchInventory,
  ICrossReference,
  IDetailSupplemental,
  ISupercedenceAttachments,
  ISupercedenceDetailSupplemental,
} from '../typings/SupplementalDetail';
import { IAttachment, ILineItem, ILostSaleCandidate } from '../typings/LineItem';
import { IPartLineDetail } from '../typings/PartLineDetail';
import { ISystemParams } from '../typings/SystemParameters';
import { IVendorSuggestions } from '../typings/VendorSuggestions';
import { IBackorderSubmission, IStandalonePOSubmission } from '../typings/Backorder';
import { ILoyaltyOffer } from '../../loyalty/typings/LoyaltyOffer';
import { IRedemption } from '../../loyalty/typings/LoyaltyRedemption';
import { launchCreditSession, launchPaymentSession } from '../components/modals/PaymentSessionModalWrapper';
import { PaymentSessionCancelResponse, PaymentSessionSuccessResponse } from '../../payment/types';

// API calls are only made if stocking parts on screen change
const LOYALTY_BACKGROUND_INTERVAL_CHECK_SECS = 3;

export type SavedListType = 'Bill of Materials' | 'Pick List' | 'Quote';

export interface IBOPrintOptions {
  combineItemsOnPOs: boolean;
  emailPOsToVendors: boolean;
  emailOnlyNoPrints?: boolean;
  includePOComments: boolean;
  printByUnitPackOnPOs: boolean;
  printTwoConfirmationCopies: boolean;
  printTwoPurchaseOrderCopies: boolean;
  removeCustomerInfoFromPOs: boolean;
}

export interface IEmailOptions {
  invoiceEmails: string[];
  quoteEmails: string[];
  skipEmails: 'Y' | 'N';
}

export interface ILoyaltyCustomerDetails {
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  emailAddress: string;
  phoneNumber: string;
  company: string;
  preferredDealer: string;
}

export interface ILoyaltyCandidate {
  lineItemIndex: number; // location within the model line items for ex-post update
  loyaltyOfferIDs: string[]; // potentially already selected offers in a previous modal run - SEED
  partNumber: string; // leader dealer division will be stripped
}

export interface IScreenPrint {
  title: string;
  url: string;
}

export interface IShipToAddress {
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
}

interface IPPCParams {
  readonly authenticated: boolean;
  readonly password: string;
  ppc: string;
  readonly permissions: {
    forceParts: boolean;
    forcePartsPassword: string;
    issueCredits: boolean;
    issueCreditsPassword?: string;
    issueReturns: boolean;
    issueReturnsPassword?: string;
    maxPurchase?: string;
  };
}

interface IRepairOrderDetails {
  customerNum: string;
  customerPo: string;
  vin: string;
  unit: string;
  operationDetails: Array<{
    conditionDescLines: string[];
    isDecisivOperation: boolean;
    laborType: string;
    laborTypeDescription: string;
    locked: boolean;
    priceCode: string;
    secondaryLaborType: string;
    secondaryLaborTypeDescription: string;
    secondaryPriceCode: string;
    stdpForceSuppression: boolean;
    voided: boolean;
    warranty: boolean;
  }>;
  unitData: Array<{
    field: string;
    value: string;
  }>;
  decisiv: boolean;
  confirmationsReady: boolean;
}

interface IUIBridge {
  controlNumInputRef: HTMLInputElement;
  creditCardSaleInputRef: HTMLInputElement; // PIN1 only
  customerLookupRef: CustomerLookup;
  glNumInputRef: HTMLInputElement;
  roNumberInputRef: HTMLInputElement; // PRO1/SMM3 run mode only
  freightInputRef: HTMLInputElement;
  fuelChargeInputRef: HTMLInputElement;
  lineItemEntryRef: { clear: Function; focusPartEntry: Function };
  manualCreditAuthPpcInput: HTMLInputElement;
  manualCreditAuthPasswordInput: HTMLInputElement;
  poInputRef: HTMLInputElement;
  process: (type: 'invoice' | 'manualcredit' | 'pickticket' | 'quote') => void;
  showOperationSelectionModal: boolean;
  showSubletUnpricedPOModal: boolean;
  transferConditionNumInputRef: HTMLInputElement;
  transferRoNumInputRef: HTMLInputElement;
}

class ShopKeeperModel {
  // Observed State
  @observable activeCustomerID: string | null = null;
  @observable activeDecisivCase: boolean = false;
  @observable activeDivision: { code: string; desc: string } = { code: '', desc: '' };
  @observable activeLoyaltyCustomerID: string | null = null;
  @observable activeOperation: number = 0;
  @observable activeRONumber: string | null = null;
  @observable confirmationDetailLazyLoaded: boolean = false; // backorder status
  @observable controlNumRequired: boolean = false; // informational only - QM enforces
  @observable coreReturnDaysLazyLoaded: boolean = false; // loaded once per PIN1 load, on demand
  @observable customerHistoryLazyLoaded: boolean = false; // invoice/credit history
  @observable customerPickListsLazyLoaded: boolean = false; // browseable list of pick lists tied to customer
  @observable customerVehiclesLazyLoaded: boolean = false; // browserable list of vehicles tied to customer
  @observable expandAllAlternatives: boolean = false;
  @observable forceCashSale = false;
  @observable freight: number | null = null;
  @observable fuelCharge: number | null = null;
  @observable gstTax: number = 0.0;
  @observable hideAdditionalDetail: boolean = false;
  @observable initialized: boolean = false;
  @observable isProcessing: boolean = false; // PIN1 invoices, quotes, pick lists
  @observable isCreditInvoiceProcessing: boolean = false;
  @observable isROProcessing: boolean = false; // PRO1/SMM3 standard issue (green button)
  @observable isDecisivROProcessing: boolean = false; // PRO1/SMM3 decisiv issue (blue button)
  @observable loyaltyOfferCount = 0; // background offer check / badge display
  @observable loyaltyProcessStatus: 'PROCESSING' | 'SUCCESS' | 'ERROR' | 'CANCELED';
  @observable moveOperationsQueue = new Map<number, number>(); // itemIndex, qtyToMove
  @observable moveOperationsInProgress = false;
  @observable moveOperationsInProgressRepricing = false;
  @observable ppcAttributionReplayActive: boolean = false;
  @observable ppcPasswordNeeded: boolean = false;
  @observable ppcValidated: boolean = false;
  @observable roLoadInProcess = false;
  @observable roPickListsLazyLoaded: boolean = false; // browseable list of pick lists tied to repair order
  @observable routingMethodOverride: null | string = null;
  @observable salesTax: number = 0.0;
  @observable shipToAccount: null | ({ customerID: string } & ICustomerParams) = null;
  @observable shipToAddress: null | IShipToAddress = null;
  @observable showAttachmentsThumbnails = false;
  @observable showManualTaxCalc = false;
  @observable taxCalculationInProcess: boolean = false;

  // Tracks information received from previous backorder run
  // This is observable to enable the dynamic "Print Last Backorder Documents" feature in the footer controls
  @observable lastBackorderData: { boType: 'DAILY' | 'NOW'; confirmationNumber: string | null; poNumbers: string[] } = {
    boType: 'DAILY',
    confirmationNumber: null,
    poNumbers: [],
  };

  // Active confirmation loaded into confirmation editor
  @observable activeConfirmation: IConfirmationPlayback | null = null;

  // Backing flag for move operations mode
  @observable _moveOperationsModeActive = false;

  /* Some modals need to be popped directly by the model logic, while
     others are popped interactively by the main UI.  In the latter cases,
     reference the observables defined in Shopkeeper.tsx.  This bifurcation
     keeps the logic much simpler, though not as purely clean.
  */
  @observable showAlternateBranchesModal = false;
  @observable showBackorderPrinterSelectionModal = false;
  @observable showCashSalePrinterSelectionModal = false;
  @observable showCreditInvoiceModal = false;
  @observable showConfirmationModal = false; // confirmation editor
  @observable showConfirmationSaleShipToModal = false; // ship to selection if multiple confirmations differ
  @observable showCreditHoldPostOpModal = false;
  @observable showCrossReferencesModal = false;
  @observable showDestinationOperationSelectionModal = false;
  @observable showFleetBillingPostOpModal = false;
  @observable showCreditInvoicePrinterSelectionModal = false;
  @observable showInvoicePrinterSelectionModal = false;
  @observable showManualCreditAuthModal = false;
  @observable showPickTicketPrinterSelectionModal = false;
  @observable showProcessLoyaltyRedemptionsModal = false;
  @observable showQuotePrinterSelectionModal = false;
  @observable showScreenPrintModal = false;
  @observable showShipToOverrideModal = false;
  @observable showStandalonePOPrinterSelectionModal = false;
  @observable suspendPORules = false;

  // Global variance suppression flags applying to all line items
  // @computed reactions use these
  @observable _suppressCoreCharges = false;
  @observable _suppressFees = false;

  // MobX observable array typescript support (.clear(), etc)
  lineItems = observable.array<ILineItem>([]);
  lineItemsQueued = observable.array<String>([]); // InvoiceBuilder "queued" display; see lineItemQueue

  roAttachments = observable.array<{ key: string }>([]);
  unitAttachments = observable.array<{ key: string }>([]);

  // Non-observed State
  appState: any; // subject to change, reference redux store

  activeBranchInventory: IDetailSupplemental['branchInventory'];

  // Loaded synchronously, no need to observe
  // Re-structured part line data (bound cores/fees etc.) - see loadCreditInvoice()
  activeCreditInvoice: ICustomerInvoiceItem & {
    invoiceNum: string;
    partSearchFilterSeed: string;
  };

  activeCrossReferenceOptions: ICrossReference[] = [];
  activeRepairOrderDetails: IRepairOrderDetails;

  branchTitleDict: {
    [branch: string]: string;
  };

  confirmationsCreatedThisSession: string[] = [];

  // UI tracks confirmationDetailLazyLoaded observable as a rubric before referencing. If it's false, this data is stale.
  confirmationDetail: IConfirmationStatusDetail;

  confirmationDetailFetchLock = false;
  confirmationPartsAddedToInvoice = new Set<string>(); // Unique ID: confirmationNum-poNum-poLineNum
  confirmationPartsAddLock = false; // shared lock (confirmation status + detail modal observes)
  controlNum: string | null = null;
  coreReturnDays: {
    [stockClass: string]: string;
  };
  creditHoldPostOp: {
    actions: string[];
    defaultScreenPrintEmail: string;
    holdNumber: string;
    printer: { id: string; name: string } | null;
  };

  // UI tracks customerHistoryLazyLoaded observable as a rubric before referencing. If it's false, this data is stale.
  customerHistory: {
    coreBank: ICustomerCoreBankItem[];
    invoiceData: { [invoiceNumber: string]: ICustomerInvoiceItem };
    serverEpoch: number;
  };

  customerLoadInProcess = false;
  customerParams: ICustomerParams; // UI tracks activeCustomerID observable as a rubric before referencing

  // UI tracks customerPickListsLazyLoaded observable as a rubric before referencing. If it's false, this data is stale.
  customerPickLists: Array<{ id: string; createdAt: string }> = [];

  // UI tracks customerVehiclesLazyLoaded observable as a rubric before referencing. If it's false, this data is stale.
  customerVehicles: Array<{ vin: string; stockNumber: string; year: string; make: string; model: string }> = [];

  defaultDivision: string;
  defaultParts: Map<string, IDefaultPart> = new Map();
  fleetBillingPostOp: {
    fields: string[];
    printer: { id: string; name: string } | null;
  };
  fleetBillingPostOpFieldCache: {
    [field: string]: string;
  } = {};
  fleetBillingCCVariance: null | number = null; // null assumes id 1, otherwise id of fleet billing card to use
  freightPromptDisplayed: boolean = false;
  fuelPromptDisplayed: boolean = false;
  fuelManuallyEntered: boolean = false;
  glNum: string | null = null;
  lastBOMLoad: { listID: string; listTitle: string } | null = null;
  lastDocID = '';
  lastPIN1PickListID: string | null = null;
  lastProcessType: 'invoice' | 'manualcredit' | 'quote' | 'pickticket';
  lastProcessROType: 'issue' | 'decisivissue' | 'negativeissue' | 'pickticket' | 'decisivpickticket';
  lastROLoyaltyCustomerID: string | null = null;
  lastStandalonePOData: { poNumbers: string[] };
  lastStockingPartEntry = ''; // Fleetcross integration
  lineItemDragInProcess = false;
  lineItemQueue = new Queue(1, Infinity);
  lockoutChanges = false; // currently only used by OPC logic
  logging = false;
  logSuperChains = false;
  loyaltyCustomerDetails: ILoyaltyCustomerDetails;
  loyaltyDefaultPart: {
    partNum: string;
    priceSymbol: string;
    stockClass: string;
  };
  loyaltyInvoiceNumber: null | string = null;
  loyaltyLogging = false; // separate from standard logging since it's noisy
  loyaltyOfferBackgroundCheckIntervalID: number;
  managerOverridePersistedForSession = false;
  modelLocked: boolean = false;
  noHistorySale: boolean = false;
  nonTaxReasonCode: null | string = null;
  OPCdata: null | ILineItem = null;
  OPCtag: null | string = null; // tag verbiage to show on the main screen, prefaces "PART REQUEST FOR REFERENCE"
  partDivisions: Map<string, string> = new Map();
  po: string | null = null;
  ppcAttribution: string | null = null;
  ppcForcePasswordValidated: boolean = false;
  ppcParams: IPPCParams; // UI tracks ppcValidated
  processByBinLocation: boolean = false;
  receivedByPromptDisplayed: boolean = false;
  receivedBy: string | null = null;
  roPickLists: Array<{ id: string; createdAt: string }> = []; // same concept as customerPickLists above
  runMode: 'PIN1' | 'PIN4' | 'PRO1' | 'PSO3' | 'SRE2' | 'SMM3' | 'INVALID';
  screenPrintDocs: IScreenPrint[] = [];
  screenPrintIsInvoice: boolean = false;
  seedConfirmationNum: string | undefined;
  seedNum: string | undefined; // PIN1=CUST#, PRO1/SMM3=RO#, SRE2=EST#
  seedLoaded: boolean = false; // seed will only auto-load exactly once per page load
  shipToEmail: boolean = false;
  showToasters = true;
  subletCandidates: string[] = ['SUBL', 'SUBP']; // + any ATB3 default part with the PO flag set (see loadSysParams)
  sysParams: ISystemParams;
  transferConditionNum: string | null = null;
  transferRoNum: string | null = null;

  processLoyaltyRedemptionsPromiseIDs = new Map();
  updateTaxPromiseIDs = new Map();

  // Tracks any ship to data encountered when processing confirmation sales
  // This is reset on each "add from confirmation" process
  lastConfirmationSaleShipToData: {
    shipToAccounts: Array<{ account: string; confirmations: string[] }>;
    shipToAddresses: Array<IShipToAddress & { confirmations: string[] }>;
  };

  // Set by the credit invoice modal before credit process
  lastCreditInvoiceState: ICreditStateMap;
  lastManualCreditAuth: { ppc: string; password: string } | null = null;

  // minimize PACCAR API calls w/ this tracking info
  // editLineItemPartNum resets this tracker to null since a part swap would invalidate our dirty tracking
  loyaltyOfferBackgroundCheckTracker: null | {
    lastCheckEpoch: number; // prevent out of order results since PACCAR calls are sometimes very slow
    partCheckCount: number; // acts like a dirty flag, prevents duplicative PACCAR calls
  } = null;

  // These two rules are mutually exclusive based on parameters of the customer.
  // Both have setters which update taxes on successful application.
  _forceSalesTax = false;
  _suppressSalesTax = false;

  // PIN1 email option overrides
  invoiceEmailsOverride: string[] | null = null;
  quoteEmailsOverride: string[] | null = null;
  skipEmails: boolean = false;

  // console debugging
  lastEvalReq = {};
  lastEvalRes = {};
  lastCalcTaxReq = {};
  lastCalcTaxRes = {};
  lastCCInvoiceValidateReq = {};
  lastCCInvoiceValidateRes = {};
  lastCCInvoiceVoidReq = {};
  lastCCInvoiceVoidRes = {};
  lastProcessReq = {};
  lastProcessRes = {};
  lastProcessROReq = {};
  lastProcessRORes = {};
  lastBackorderProcessReq = {};
  lastBackorderProcessRes = {};
  lastMoveOperationsReq = {};
  lastMoveOperationsRes = {};
  lastPrintConfirmationReq = {};
  lastPrintConfirmationRes = {};
  lastPrintPurchaseOrdersReq = {};
  lastPrintPurchaseOrdersRes = {};
  lastRemoveROLineItemReq = {};
  lastRemoveROLineItemRes = {};
  lastSaveBillOfMaterialsReq = {};
  lastSaveBillOfMaterialsRes = {};
  lastSavedListReq = {};
  lastSavedListRes = {};
  lastSavedRepairOrderRes = {};
  lastStandalonePOProcessReq = {};
  lastStandalonePOProcessRes = {};

  // For the few cases the model needs to hydrate controls
  uiBridge: IUIBridge;

  /* uiBridge - main screen controls
     appState - redux store
     seedNum - CUST #, RO #, etc. to auto load
     confirmationNum - to auto load
  */
  constructor(
    uiBridge: IUIBridge,
    appState: any,
    { confirmationNum, seedNum }: { confirmationNum?: string; seedNum?: string }
  ) {
    this.uiBridge = uiBridge;
    this.appState = appState;

    const pageName = _.get(appState, 'page.pageName', '');
    switch (pageName) {
      case 'pin1':
      case 'PIN1':
        this.runMode = 'PIN1';
        this.seedNum = seedNum;
        this.seedConfirmationNum = confirmationNum;
        break;
      case 'pin4':
      case 'PIN4':
        this.runMode = 'PIN4';
        break;
      case 'pro1':
      case 'PRO1':
        this.runMode = 'PRO1';
        this.seedNum = seedNum;
        this.seedConfirmationNum = confirmationNum;
        break;
      case 'PSO3':
        this.runMode = 'PSO3';
        break;
      case 'smm3':
      case 'SMM3':
        // check for sysParams.smm3PartAccess after loadSysParams below to force PRO1 mode
        this.runMode = 'SMM3';
        this.seedNum = seedNum;
        this.seedConfirmationNum = confirmationNum; // only in PRO1 mode, see autoLoadSeed()
        break;
      case 'sre2':
      case 'SRE2':
        this.runMode = 'SRE2';
        this.seedNum = seedNum;
        break;
      default:
        console.error(`Unsupported SK Mode: ${pageName}`);
        this.runMode = 'INVALID';
        return; // skip sysParams on invalid runMode
    }

    this.loadSysParams();

    if (process.env.NODE_ENV === 'development') {
      this.logging = true;
    }
  }

  @computed
  get activeOperationDecisivEnabled() {
    return this.lineItems.some(
      lineItem =>
        !lineItem.issuedToRO && lineItem.roDetail && lineItem.roDetail.operationNumber === this.activeOperation
    );
  }

  @computed
  get activeRoutingMethod(): string | null {
    return this.routingMethodOverride
      ? this.routingMethodOverride
      : this.activeCustomerID && !this.sysParams.forceRouteSelection
      ? this.customerParams.route || null
      : null;
  }

  // If auth is required, this will tell us if the clerk's PPC can issue a credit, or if a full mgr override is required
  get canClerkPPCIssueCredit() {
    return _.get(this.ppcParams, 'permissions.issueCredits', false);
  }

  // Service side (PRO1/SRE2/SMM3) returns
  get canClerkPPCIssueReturn() {
    return _.get(this.ppcParams, 'permissions.issueReturns', false);
  }

  // Credits (PIN1) PPC auth is always required unless the PPC perms have issueCredits=true with no credit pw
  get creditPPCAuthRequired() {
    return !(
      _.get(this.ppcParams, 'permissions.issueCredits', false) &&
      _.get(this.ppcParams, 'permissions.issueCreditsPassword') === undefined
    );
  }

  // Returns (PRO1) PPC auth is always required unless the PPC perms have issueReturns=true with no returns pw
  get returnPPCAuthRequired() {
    return !(
      _.get(this.ppcParams, 'permissions.issueReturns', false) &&
      _.get(this.ppcParams, 'permissions.issueReturnsPassword') === undefined
    );
  }

  // This method is used when selling off the Backorder Status Modal
  async addConfirmationPartLineItemsFromBOStatus(
    confirmationParts: Array<IPartOnBackorder & { action?: ConfirmationSaleAction }>
  ) {
    if (this.runMode === 'PIN1' && this.lockoutChanges) {
      toastr.error('Error', 'Changes locked out.');
      return;
    }

    const isRO = ['PRO1', 'SMM3', 'SRE2'].includes(this.runMode);
    if (isRO && !this.confirmationPreIssueQueueCheck()) {
      return;
    }

    let lineItemsAdded = false;

    // Track a unique set of touched confirmations - used for selecting (possible) ship to options
    const touchedConfirmations = new Set<string>();

    // Track a unique set of touched POs to update the main screen PO accordingly
    const touchedPONums = new Set<string>();

    // Track a unique set of ship to accounts / addresses encountered based on touched confirmations
    const shipToAddresses = new Set<string>();
    const shipToAccounts = new Set<string>();

    const queuedStockingPartIndexes: number[] = [];

    confirmationParts.forEach((confirmationPart, index) => {
      // non-tracked part case
      if (confirmationPart.nonStocking === 'Y') {
        const confirmationQty = parseInt(confirmationPart.qty, 10) || 1;
        this.lineItems.push({
          fetched: true,
          partNum: 'LINEITEM_NONSTOCKINGPART',
          quantity: confirmationQty,
          nonStockingPartNum: confirmationPart.part,
          nonStockingPartNetPrice: parseFloat(confirmationPart.netPrice) || 0.0,
          nonStockingPartListPrice: parseFloat(confirmationPart.listPrice) || 0.0,
          nonStockingPartTradePrice: parseFloat(confirmationPart.tradePrice) || 0.0,
          nonStockingPartDescription: confirmationPart.description,
          priceSymbol: confirmationPart.priceSymbol,
          stockClass: confirmationPart.stockClass,
          poFreight: confirmationPart.isFreight,
          confirmationSale: {
            action: 'R', // @parts always use R
            confirmationNum: confirmationPart.confirmationNumber,
            confirmationQty,
            lineNum: confirmationPart.lineNumber,
            poNum: confirmationPart.poNumber,
            uniqueID: confirmationPart.uniqueID,
          },
          ...this.getPreissueDetails(),
        });

        lineItemsAdded = true;

        // Track this item as "added" throughout the backordering UI.
        this.confirmationPartsAddedToInvoice.add(confirmationPart.uniqueID);

        touchedConfirmations.add(confirmationPart.confirmationNumber);

        if (confirmationPart.customerPoNumber) {
          touchedPONums.add(confirmationPart.customerPoNumber);
        }

        // stocking-part case - queue all for 1 DBPIP inquiry hit (pull details, core charges, fees, etc.)
      } else {
        queuedStockingPartIndexes.push(index);
      }
    });

    if (queuedStockingPartIndexes.length > 0) {
      const bulkPartInquiry: Array<{ partNum: string; qty: string }> = [];
      let extractedLineItems: ILineItem[] = [];
      let processError = false;

      queuedStockingPartIndexes.forEach(index =>
        bulkPartInquiry.push({ partNum: confirmationParts[index].part, qty: confirmationParts[index].qty })
      );

      try {
        // For PRO1/SMM3, there's no need to query partinquirymulti with RO/operation data since we already have prices
        const req = {
          customerID: this.activeCustomerID || '',
          parts: bulkPartInquiry,
          ppc: this.ppcValidated ? this.ppcParams.ppc : '',
        };

        if (this.logging) {
          console.warn('/partinquirymulti Request:');
          console.log(req);
        }

        const res = await Api.post('/shopkeeper/partinquirymulti', req);

        if (this.logging) {
          console.warn('/partinquirymulti Response:');
          console.log(res);
        }

        const dbpipRows = _.get(res, 'data.dbpip', '').split('\n');

        /* Unlike quote processing, there's no need to extractInferredSupercedences in this case ex-post
           since we need to detect superceded cases in fleet billed scenarios as well (in which case no
           "use" trail provided by DBPIP).  If a confirmation part line has been superceded, we'll direct
           the user to force it right off of the raw confirmation (should be a very small % of cases).
        */
        extractedLineItems = (await this.extractDbpipMultiPartTrusted(dbpipRows)).extractedLineItems;

        if (this.logging) {
          console.warn('extractedLineItems:');
          console.log(extractedLineItems);
        }
      } catch (err: any) {
        toastr.error('Error', `Error processing stocking parts for confirmation sale. (${err.message})`);
        processError = true;
      }

      if (!processError) {
        /* Walk the queued stocking parts and create resultant model line items, where possible.
         - DBPIP auto-combines same partNum lines regardless of PTB2 settings.
         - Pricing is forced ex-post anyways, so qty level pricing doesn't matter.
         - Supercedence cases can occur on both fleet and non-fleet billed scenarios.
      */
        const lineItems: ILineItem[] = [];
        queuedStockingPartIndexes.forEach(partIndex => {
          const confirmationPart = confirmationParts[partIndex];
          const extractedLineItem = _.find(extractedLineItems, { partNum: confirmationPart.part });

          if (!extractedLineItem || extractedLineItem.incompleteDetailErrors) {
            console.error(`Unable to sell from bostatus: ${confirmationPart.uniqueID}:${confirmationPart.part}`);

            // This is a QM bug which occurs on corrupt data and no part number is returned in position 1.
            // Often times, a subsequent hit to QM with just the failed parts return more specific errors.
            // This is likely what a user will attempt to do.  Unfortunately QM is very buggy in these cases.
            if (!extractedLineItem) {
              console.error(`${confirmationPart.part} not detected in partinquirymulti response.`);
            }

            // if we received specific errors from QM, show them
            if (extractedLineItem && extractedLineItem.incompleteDetailErrors) {
              console.error(
                `${confirmationPart.part} Detail Errors: ${extractedLineItem.incompleteDetailErrors.join(', ')}`
              );
            }
            // toastr does NOT dupe messages in the event the partNum occurs twice
            toastr.error(
              'Error',
              `Unable to sell ${confirmationPart.part}. Try selling directly from the confirmation instead.`
            );
          } else {
            const lineItem = _.cloneDeep(extractedLineItem);

            /* DBPIP incorrectly auto-combines same partNum lines regardless of PTB2 settings.
               This manifests as a bug if 2 part lines with the same part number are sold off of
               two separate confirmations at once.  These lines patch back original quantity requested.
               Price breaks are not a concern since we force prices from the confirmation anyhow.
            */
            const confirmationQty = confirmationPart.qtyNum;
            lineItem.quantity = confirmationQty;

            // Extracted prices are per item
            const netPrice = parseFloat(confirmationPart.netPrice) || 0.0;
            const listPrice = parseFloat(confirmationPart.listPrice) || 0.0;
            const tradePrice = parseFloat(confirmationPart.tradePrice) || 0.0;

            // Apply forces with confirmation price reversion in the event the
            // counterman wishes to further force the price.
            lineItem.detail!.netPrice = netPrice;
            lineItem.detail!.listPrice = listPrice;
            lineItem.detail!.tradePrice = tradePrice;
            lineItem.forcedNetPrice = netPrice;
            lineItem.forcedListPrice = listPrice;
            lineItem.forcedTradePrice = tradePrice;
            lineItem.forcedPricing = true;
            lineItem.forcedPricingFromQuoteOrConfirmation = true;
            lineItem.forcedDescription = confirmationPart.description;

            // Apply confirmation data
            lineItem.confirmationSale = {
              action: confirmationPart.action ? confirmationPart.action : 'S',
              confirmationNum: confirmationPart.confirmationNumber,
              confirmationQty,
              lineNum: confirmationPart.lineNumber,
              poNum: confirmationPart.poNumber,
              uniqueID: confirmationPart.uniqueID,
            };

            // Add to lines to push to model
            lineItems.push({
              ...lineItem,
              ...this.getPreissueDetails(),
            });

            // Track this item as "added" throughout the backordering UI.
            this.confirmationPartsAddedToInvoice.add(confirmationPart.uniqueID);

            touchedConfirmations.add(confirmationPart.confirmationNumber);

            if (confirmationPart.customerPoNumber) {
              touchedPONums.add(confirmationPart.customerPoNumber);
            }
          }
        });

        if (lineItems.length > 0) {
          lineItemsAdded = true;

          // See loadSavedList() logic for explanation of this.
          this.lineItems.push(...lineItems);
        }
      }
    }

    if (lineItemsAdded) {
      toastr.success('Success', isRO ? 'Items added to operation (queued for issue).' : 'Items added to invoice.');

      // We can stop right here in PRO1/SMM3 cases
      if (isRO) {
        return;
      }

      this.updateTaxes();

      /* Update the main UI "PO" field to include any appended (touched) POs from this process
         This will suspend any active PO rules (blanket PO / pattern match required) since PO numbers
         created for existing confirmations may pre-date currently active rules.

         3/9/21 - Some clients use spaces in their PO #s.  The worst case side effect, if the user were
         to sell off the same PO in multiple iterations within the same invoicing session (not likely),
         would be a duplication of the PO number in the informational PO # field on the invoice.  Such
         an edgecase is not worth a refactor here.  After years of use, no one has reported that problem.
      */
      if (touchedPONums.size > 0) {
        if (this.po !== null) {
          // possible other confirmation sales have taken place (hence the split on ' ')
          const currentPOs = this.po.split(' ');
          currentPOs.forEach(po => touchedPONums.add(po));
        }

        this.suspendPORules = true;

        const updatedPO = [...touchedPONums].join(' ');
        this.po = updatedPO;
        this.uiBridge.poInputRef.value = updatedPO;
      }

      // Track which confirmations go with which ship tos
      const confirmationShipToAccountMap: { [shipToAccount: string]: string[] } = {};
      const confirmationShipToAddressMap: { [shipToAddressRaw: string]: string[] } = {};

      // Extract any possible ship to data encountered in the unique set of confirmations touched
      for (const confirmationNo of touchedConfirmations) {
        const statusMatch = _.find(this.confirmationDetail.statuses, { confirmationNo });
        if (statusMatch) {
          if (!!statusMatch.shipToAccount && !statusMatch.shipToAddress) {
            shipToAccounts.add(statusMatch.shipToAccount);
            if (statusMatch.shipToAccount in confirmationShipToAccountMap) {
              confirmationShipToAccountMap[statusMatch.shipToAccount].push(confirmationNo);
            } else {
              confirmationShipToAccountMap[statusMatch.shipToAccount] = [confirmationNo];
            }
          } else if (!!statusMatch.shipToAddress) {
            shipToAddresses.add(statusMatch.shipToAddress);
            if (statusMatch.shipToAddress in confirmationShipToAddressMap) {
              confirmationShipToAddressMap[statusMatch.shipToAddress].push(confirmationNo);
            } else {
              confirmationShipToAddressMap[statusMatch.shipToAddress] = [confirmationNo];
            }
          }
        }
      }

      this.lastConfirmationSaleShipToData = {
        shipToAccounts: Array.from(shipToAccounts).map(s => {
          return { account: s, confirmations: confirmationShipToAccountMap[s] };
        }),
        shipToAddresses: Array.from(shipToAddresses).map(s => {
          const parts = s.split('~!~'); // QM format matches DBPIP spec of ship to address line
          return {
            name: parts[0] || '',
            address: parts[1] || '',
            city: parts[2] || '',
            phone: parts[3] || '',
            zipCode: parts[6] || '',
            state: parts[7] || '',
            confirmations: confirmationShipToAddressMap[s],
          };
        }),
      };

      const x = this.lastConfirmationSaleShipToData;
      if (x.shipToAccounts.length > 0 || x.shipToAddresses.length > 0) {
        this.showConfirmationSaleShipToModal = true;
      }
    }
  }

  /* This method is used when selling off the Confirmation Modal, and references the currently "active" confirmation
     which was loaded via loadConfirmation().

     Active confirmation line items are already prepared for direct insertion into the invoice builder
     without another round trip to QM, since the data came from the row-mapped DBPIP format.

     lineItemsSelected is a set of uniqueIDs.
  */
  async addConfirmationPartLineItemsFromActiveConfirmation(lineItemsSelected: Set<string>) {
    if (this.runMode === 'PIN1' && this.lockoutChanges) {
      toastr.error('Error', 'Changes locked out.');
      return;
    }

    const isRO = ['PRO1', 'SMM3', 'SRE2'].includes(this.runMode);
    if (isRO && !this.confirmationPreIssueQueueCheck()) {
      return;
    }

    let lineItemsAdded = false;
    let taxableLineItemAdded = false;

    // No async lookups required
    runInAction(() => {
      this.activeConfirmation!.lineItems.forEach(lineItem => {
        const uniqueID = this.confirmationLineItemUniqueID(lineItem);

        // Skip anything previously added, sold, or voided (including voided within this modal run post-selection)
        if (
          this.confirmationPartsAddedToInvoice.has(uniqueID) ||
          lineItem.confirmationLineSold ||
          lineItem.confirmationLineVoided
        ) {
          return;
        }

        if (lineItemsSelected.has(uniqueID)) {
          lineItemsAdded = true;

          // Only update taxes if a part is encountered
          if (lineItem.partNum !== 'LINEITEM_COMMENT') {
            taxableLineItemAdded = true;
          }

          // Mark this line as added to the invoice
          this.confirmationPartsAddedToInvoice.add(uniqueID);

          // Check for partial on hand usage, decrease sale qty accordingly
          if (_.get(lineItem, 'confirmationSale.useOnHand', false)) {
            if (lineItem.detail!.onHand < lineItem.confirmationSale!.confirmationQty) {
              lineItem = _.cloneDeep(lineItem); // invoice line item is diverging from confirmation line item here
              lineItem.quantity = lineItem.detail!.onHand;
            }
          }

          // Add to invoice builder.  lineItem is pre-configured to be a confirmation sale.
          this.lineItems.push({
            ...lineItem,
            ...this.getPreissueDetails(),
          });
        }
      });
    });

    if (lineItemsAdded) {
      const confirmationNum = this.activeConfirmation!.details.confirmationNumber;
      const confirmationFreightUniqueID = `${confirmationNum}.FREIGHT`;
      const confirmationFuelUniqueID = `${confirmationNum}.FUEL`;

      const confirmationFreightAvailableToAdd =
        this.activeConfirmation!.details.freight &&
        !this.activeConfirmation!.details.freightSold &&
        !this.confirmationPartsAddedToInvoice.has(confirmationFreightUniqueID);
      const confirmationFuelAvailableToAdd =
        this.activeConfirmation!.details.fuel &&
        !this.activeConfirmation!.details.fuelSold &&
        !this.confirmationPartsAddedToInvoice.has(confirmationFuelUniqueID);

      if (confirmationFreightAvailableToAdd || confirmationFuelAvailableToAdd) {
        let confirmationChargesMsg: string;

        if (confirmationFreightAvailableToAdd && confirmationFuelAvailableToAdd) {
          confirmationChargesMsg = 'freight & fuel';
        } else if (confirmationFreightAvailableToAdd) {
          confirmationChargesMsg = 'freight';
        } else if (confirmationFuelAvailableToAdd) {
          confirmationChargesMsg = 'fuel';
        }

        const applyConfirmationCharges = await new Promise(resolve => {
          modal.confirm({
            title: 'Confirmation Charges',
            message: `Do you wish to charge the previously specified ${confirmationChargesMsg} for this confirmation?`,
            confirmLabels: ['Yes', 'No'],
            callback: () => resolve(true),
            onClose: () => resolve(false),
          });
        });

        /* We'll do all or nothing here of the remaining charges not previously sold.
           I can't imagine why a clerk would only want to apply one charge but not the other.
           The clerk can always remove one or the other from the main UI if they really need to do that.

           These will have the same treatment as PO FREIGHT, wherein they are non-stocking parts under the covers,
           but render similar to default parts in the main UI so that price symbols and stock classes can not be changed.
        */
        if (applyConfirmationCharges) {
          if (confirmationFreightAvailableToAdd) {
            const price = this.activeConfirmation!.details.freight;
            let priceSymbol, stockClass: string;

            /* Assume "FRT" is the default part on all clients per Ben, but fallback to best guesses if necessary.
               We'll copy the price symbol and stock class from the default part, but use X:FREIGHT for the part name
               here similar to how PO FREIGHT works when applied via PSO6, where X is the default division.
            */
            const freightDefaultPart = this.defaultParts.get('FRT');
            if (freightDefaultPart) {
              priceSymbol = freightDefaultPart.priceSymbol;
              stockClass = freightDefaultPart.stockClass;

              // Otherwise we'll use a best guess fallback
            } else {
              priceSymbol = '53';
              stockClass = this.sysParams.nonStockingDefaultStockClass;
            }

            this.lineItems.push({
              fetched: true,
              partNum: 'LINEITEM_NONSTOCKINGPART',
              quantity: 1,
              nonStockingPartNum: `${this.defaultDivision}:FREIGHT`,
              nonStockingPartNetPrice: price,
              nonStockingPartListPrice: price,
              nonStockingPartTradePrice: price,
              nonStockingPartDescription: `FREIGHT: ${confirmationNum}`,
              stockClass,
              priceSymbol,
              confirmationFreight: true,
              confirmationSale: {
                action: 'S', // ignored
                confirmationCharge: true,
                confirmationQty: 1, // ignored
                confirmationNum, // on process partline position 15 is confirmationNum.FREIGHT
                lineNum: 'FREIGHT',
                poNum: '', // ignored
                uniqueID: confirmationFreightUniqueID,
              },
              ...this.getPreissueDetails(), // RO details, empty on PIN1
            });

            // Mark this line as added to the invoice
            this.confirmationPartsAddedToInvoice.add(confirmationFreightUniqueID);
          }

          if (confirmationFuelAvailableToAdd) {
            const price = this.activeConfirmation!.details.fuel;
            let priceSymbol, stockClass: string;

            // The "FUEL" default part varies among clients, but we specify it in PTB2.
            // If fuel was added, it's reasonable to assume the fuel default part is setup.
            const fuelDefaultPart = this.defaultParts.get(this.sysParams.fuelChargeDefaultPartName);
            if (fuelDefaultPart) {
              priceSymbol = fuelDefaultPart.priceSymbol;
              stockClass = fuelDefaultPart.stockClass;

              // Fallback to default price symbol / stock class for non tracked parts.
              // This would only happen if fuel is deleted from ATB3 after charging to a confirmation.
            } else {
              priceSymbol = this.sysParams.nonStockingDefaultPriceSymbol;
              stockClass = this.sysParams.nonStockingDefaultStockClass;
            }

            this.lineItems.push({
              fetched: true,
              partNum: 'LINEITEM_NONSTOCKINGPART',
              quantity: 1,
              nonStockingPartNum: `${this.defaultDivision}:FUEL`,
              nonStockingPartNetPrice: price,
              nonStockingPartListPrice: price,
              nonStockingPartTradePrice: price,
              nonStockingPartDescription: `FUEL: ${confirmationNum}`,
              stockClass,
              priceSymbol,
              confirmationFuel: true,
              confirmationSale: {
                action: 'S', // ignored
                confirmationCharge: true,
                confirmationQty: 1, // ignored
                confirmationNum, // on process partline position 15 is confirmationNum.FUEL
                lineNum: 'FUEL',
                poNum: '', // ignored
                uniqueID: confirmationFuelUniqueID,
              },
              ...this.getPreissueDetails(), // RO details, empty on PIN1
            });

            // Mark this line as added to the invoice
            this.confirmationPartsAddedToInvoice.add(confirmationFuelUniqueID);
          }
        }
      }

      toastr.success('Success', isRO ? 'Items added to operation (queued for issue).' : 'Items added to invoice.');

      // We can stop right here in PRO1/SMM3 cases
      if (isRO) {
        return;
      }

      const shipOut = this.activeConfirmation!.details.shipOut;
      let routingMethodUpdated = false;

      // If the ship out on this confirmation is different from what's set in the invoice builder, ask
      if (!!shipOut && shipOut !== this.activeRoutingMethod) {
        const applyShipTo = await new Promise(resolve => {
          modal.confirm({
            title: 'Shipping Method',
            message: 'Do you wish to apply the ship out method set on this confirmation to the invoice?',
            confirmLabels: ['Yes', 'No'],
            callback: () => resolve(true),
            onClose: () => resolve(false),
          });
        });

        if (applyShipTo) {
          routingMethodUpdated = true;
          this.routingMethodOverride = shipOut;
        }
      }

      if (taxableLineItemAdded || routingMethodUpdated) {
        this.updateTaxes();
      }

      // If this confirmation has a customer PO num assigned, add it to the invoice builder if it's not already there
      const confirmationCustomerPo = this.activeConfirmation!.details.customerPo;
      if (!!confirmationCustomerPo) {
        const currentPOs = this.po !== null ? this.po.split(' ') : [];
        if (!currentPOs.includes(confirmationCustomerPo)) {
          const updatedPO = [...currentPOs, confirmationCustomerPo].join(' ');
          this.suspendPORules = true;
          this.po = updatedPO;
          this.uiBridge.poInputRef.value = updatedPO;
        }
      }

      const confirmationNumber = this.activeConfirmation!.details.confirmationNumber;
      const shipToAccount = this.activeConfirmation!.details.shipToAccount;
      const shipToAddress = this.activeConfirmation!.details.shipToAddress;

      if (shipToAccount || shipToAddress) {
        this.lastConfirmationSaleShipToData = {
          shipToAccounts: shipToAccount ? [{ account: shipToAccount, confirmations: [confirmationNumber] }] : [],
          shipToAddresses: shipToAddress ? [{ ...shipToAddress, confirmations: [confirmationNumber] }] : [],
        };
        this.showConfirmationSaleShipToModal = true;
      }
    }
  }

  /* Add a line item comment to the invoice.
   */
  @action
  addLineItemComment(comment: string) {
    if (this.runMode === 'PIN1' && this.lockoutChanges) {
      toastr.error('Error', 'Changes locked out.');
      return;
    }

    this.lineItems.push({
      comment,
      fetched: true,
      quantity: 0,
      partNum: 'LINEITEM_COMMENT',
      ...this.getPreissueDetails(), // empty on PIN1
    });
  }

  /* Default parts are defined on ATB3.  They do not require an eval when added to the model.
     They do, however, require the user to provide pricing based on the price fields count
     (arity) as defined by ATB3.

     defaultPartNum is the part name field within the ATB3 table.  The other fields are the
     user specified values for this default part (from the default part modal).
  */
  @action
  addLineItemDefaultPart(
    defaultPartNum: string,
    defaultPartNetPrice: number,
    defaultPartListPrice: number,
    defaultPartTradePrice: number,
    defaultPartDescription: string,
    defaultPartSubletComment: string,
    unpriced?: boolean
  ) {
    if (this.runMode === 'PIN1' && this.lockoutChanges) {
      toastr.error('Error', 'Changes locked out.');
      return;
    }

    const defaultPart = this.defaultParts.get(defaultPartNum);
    const unpricedOpts = unpriced ? { unpriced: true } : {};

    // In theory, this should not happen since we only show ATB3 default parts within the
    // default part selector modal, but who knows how someone may call this one day.
    if (!defaultPart) {
      toastr.error('Error', `Default part "${defaultPartNum}" missing from ATB3.`);
      return;
    }

    this.lineItems.push({
      fetched: true,
      quantity: 1,
      partNum: 'LINEITEM_DEFAULTPART',
      defaultPartNum,
      defaultPartNetPrice,
      defaultPartListPrice,
      defaultPartTradePrice,
      defaultPartDescription,
      defaultPartSubletComment,
      stockClass: defaultPart.stockClass,
      priceSymbol: defaultPart.priceSymbol,
      ...this.getPreissueDetails(), // empty on PIN1
      ...unpricedOpts,
    });

    this.updateTaxes();
  }

  @action
  addNonStockingPartLineItem(
    nonStockingPartNum: string,
    nonStockingPartQty: number,
    nonStockingPartNetPrice: number,
    nonStockingPartListPrice: number,
    nonStockingPartTradePrice: number,
    nonStockingPartDescription: string,
    nonStockingPartStockClass: string,
    nonStockingPartPriceSymbol: string,
    unpriced?: boolean
  ) {
    if (this.runMode === 'PIN1' && this.lockoutChanges) {
      toastr.error('Error', 'Changes locked out.');
      return;
    }

    const unpricedOpts = unpriced ? { unpriced: true } : {};

    this.lineItems.push({
      fetched: true,
      partNum: 'LINEITEM_NONSTOCKINGPART',
      quantity: nonStockingPartQty,
      nonStockingPartNum,
      nonStockingPartNetPrice,
      nonStockingPartListPrice,
      nonStockingPartTradePrice,
      nonStockingPartDescription,
      stockClass: nonStockingPartStockClass,
      priceSymbol: nonStockingPartPriceSymbol,
      ...this.getPreissueDetails(), // RO details, empty on PIN1
      ...unpricedOpts,
    });

    this.updateTaxes();
  }

  /* Add stocking part line items to the invoice.

     Note: parts are added "uninitialized", requiring a future fetch for part details.  When initially
     added in the "uninitialized state", the Invoice component observer will show the part has been
     immediatley added (in a "Fetching" status) whlie details are back filled.  This method is also an
     opportunity to filter out any parts that shouldn't be added, such as cores (clean or dirty), fees, etc..
  */
  @action
  async addStockingPartLineItem(
    partNum: string,
    division: string,
    quantity: number,
    forcedEntry: boolean = false,
    splitSetAutoAddition: boolean = false
  ): Promise<boolean> {
    if (this.runMode === 'PIN1' && this.lockoutChanges) {
      toastr.error('Error', 'Changes locked out.');
      return false;
    }

    const processLine = async (o: { quantity: number; partNum: string; forcedEntry: boolean }) => {
      // Add line item in the "Fetching" state
      // Only properties specified here are observed by MobX for line item redraws.
      const itemIndex =
        this.lineItems.push({
          fetched: false,
          quantity: o.quantity,
          partNum: o.partNum,
          extendedFetchingDetails: '',
          forcedEntry: o.forcedEntry, // *partNum syntax entered?
          forcedDescription: '',
          forcedPricing: false,
          initialForceModalRan: false,
          showPartAlternatives: false,
          ...this.getPreissueDetails(), // empty on PIN1
        }) - 1;

      /* evalLineItem with new line item.  Upon error, remove the line.
      evalLineItem may detect an existing line item with the same partNum.
      In that case, the existing line item is updated with a revised quantity and re-evaluated.
      If the re-evaluation succeeds, this line item will be removed and evalLineItem returns true.
      */
      const res = await this.evalLineItem(itemIndex);
      if (!res) {
        this.lineItems.splice(itemIndex, 1); // remove errant part
        return false;
      } else {
        this.updateTaxes(); // this is async but no need to wait
        return true;
      }
    };

    // If a detected split supercedence is driving the additions of these line items,
    // we can bypass the queue and not worry about division detection and other input tests.
    if (splitSetAutoAddition) {
      return processLine({ quantity, partNum, forcedEntry });
    }

    // Ensure division exists before adding line item.  This check may be duplicative in some cases
    // (e.g. if called from the PartLookup component), but enables other external use cases.
    if (!this.partDivisions.has(division)) {
      toastr.error('Error', `Part division "${division}" does not exist.`);
      return false;
    }

    // update active "current" division if changed based on most recent part entry
    if (this.activeDivision.code !== division) {
      this.activeDivision.code = division;
      this.activeDivision.desc = this.partDivisions.get(division) || '';
    }

    // per Tom, core parts cannot be added directly (also may be checked elsewhere for UI efficiency)
    if (partNum[partNum.length - 1] === '#') {
      toastr.error('Error', 'You may not enter add a clean core directly.');
      return false;
    }

    /* lineItem partNums are stored in DIV:PARTNUM form as this is what DBPIP considers a part number.
      We maintain the division/partnum division when calling addStockingPartLineItem in order to detect
      when it's appropriate to switch the "current" default division, henceforth no longer needed.
    */
    partNum = `${division}:${partNum}`.toUpperCase();

    // For subsequent fleetcross lookups
    this.lastStockingPartEntry = partNum;

    // UI observes this to show queued items pending fetch/eval.
    this.lineItemsQueued.push(`${quantity}.${partNum}`);

    return this.lineItemQueue.add(async () => {
      // It's possible the invoice was cleared between queued evals.
      if (!this.lineItemsQueued.length) {
        console.warn(`Pending Queue Cancelled (${quantity}.${partNum})`);
        return true;
      }

      // Item is now in a fetching/eval state, remove from UI-observed queued items
      this.lineItemsQueued.shift();

      return processLine({ quantity, partNum, forcedEntry });
    });
  }

  /* This assumes partNum is specified as DIV:PARTNUM.
     This is used to quickly load a text file of stocking parts/comments.
     It does not support forced pricing as of yet (perhaps customers may want one day).
  */
  async addMultipleLineItems(items: Array<{ partNum: string; qty: number }>) {
    if (this.modelLocked) {
      toastr.error('Wait', 'Fetch action in process. Please try again in a moment.');
      return;
    }

    this.modelLocked = true;
    for (const item of items) {
      if (item.partNum[0] === '#' && item.partNum.length > 1) {
        this.addLineItemComment(item.partNum.substring(1));
      } else {
        await this.addStockingPartLineItem(item.partNum, '', item.qty, false, true);
      }
    }
    this.modelLocked = false;
  }

  /* Supports:
     - Stocking Part Adjustments
     - Non-stocking/tracked Part Adjustments
     - Default Part Adjustments
     - Unpriced Part Adjustments

     Refreshes the RO after successful price adjustment.

     $PART ORIGINAL_ISSUE_FORMAT
     ?PART with 0,0,0 for prices on RO load
     ?PART adjust with 0,0,0 keeps it unpriced on RO load
     ?PART adjust with non-zeros will change it to @PART on next RO load

     ?PART required on adjust to price a previously unpriced part.

     Therefore, we only need to reference what the unpriced flag on the
     line item is currently set to as of the last RO load.
  */
  async adjustIssuedLineItem(opts: {
    type: 'STOCKING' | 'NONSTOCKING' | 'DEFAULTPART' | 'COMMENT';
    itemIndex: number;
    netPrice?: number;
    listPrice?: number;
    tradePrice?: number;
    description?: string;
    stockClass?: string;
    priceSymbol?: string;
    clearForce?: boolean;
    comment?: string;
    subletComment?: string;
  }) {
    const lineItem = this.lineItems[opts.itemIndex];
    const qty = lineItem.quantity;
    let partNum: string;

    switch (opts.type) {
      case 'STOCKING':
        partNum = opts.clearForce ? lineItem.partNum : `*${lineItem.partNum}`;
        break;
      case 'NONSTOCKING':
        partNum = `${lineItem.unpriced ? '?' : '@'}${lineItem.nonStockingPartNum!}`;
        break;
      case 'DEFAULTPART':
        partNum = `${lineItem.unpriced ? '?' : '@'}X:${lineItem.defaultPartNum}`;
        break;
      case 'COMMENT':
        partNum = 'LINEITEM_COMMENT';
        break;
      default:
        // API mis-used
        return;
        break;
    }

    const adjustLineItemReq: any = {
      ppc: this.ppcParams.ppc,
      roNumber: this.activeRONumber,
      roKey: lineItem.roDetail!.roKey,
      partNum,
      qty,
      netPrice: opts.netPrice,
      listPrice: opts.listPrice,
      tradePrice: opts.tradePrice,

      // Non-stocking parts only
      description: opts.description,
      stockClass: opts.stockClass,
      priceSymbol: opts.priceSymbol,

      // Stocking parts only
      clearForce: opts.clearForce,

      // Memo/Comments only (not sublet comment)
      comment: opts.comment,
    };

    if (opts.type === 'DEFAULTPART' && ['SUBL', 'SUBP'].includes(lineItem.defaultPartNum!)) {
      adjustLineItemReq.subletComment = opts.subletComment;
      adjustLineItemReq.subletCommentRoKey = lineItem.roDetail!.subletCommentRoKey;
    }

    this.isROProcessing = true;

    if (this.logging) {
      console.warn('/adjustissuedlineitem Request:');
      console.log(adjustLineItemReq);
    }

    const res = await Api.post('/shopkeeper/adjustissuedlineitem', adjustLineItemReq);

    if (this.logging) {
      console.warn('/adjustissuedlineitem Response:');
      console.log(res);
    }

    // Extract any errors that occurred during processing.
    const parsedResponse = res.data.dbpip.split('\n');
    const mainErrorMessages = _.get(parsedResponse, 12, '').trim();

    if (mainErrorMessages) {
      const processErrors = [];
      processErrors.push(...mainErrorMessages.split('ý'));
      processErrors.forEach(error => {
        toastr.error('Error', error);
      });
      console.error('Adjust Issued Line Item Errors');
      console.log(processErrors);

      // Otherwise refresh the RO data
    } else {
      await this.refreshActiveROData({ retainIssueQueue: 'ALLOPS' });
      toastr.success('Success', 'Updated!');
    }

    this.isROProcessing = false;
  }

  @action
  applyForcedPricing(
    itemIndex: number,
    netPriceForce: number,
    listPriceForce: number,
    tradePriceForce: number,
    managerOverridePPC: string | null
  ) {
    if (this.runMode === 'PIN1' && this.lockoutChanges) {
      toastr.error('Error', 'Changes locked out.');
      return;
    }

    const lineItem = this.lineItems[itemIndex];

    if (
      lineItem.detail &&
      lineItem.detail.netPrice === netPriceForce &&
      lineItem.detail.listPrice === listPriceForce &&
      lineItem.detail.tradePrice === tradePriceForce
    ) {
      return;
    }

    // Is this a service screen price adjustment on a previously issued part?
    if (['PRO1', 'SMM3', 'SRE2'].includes(this.runMode) && this.lineItems[itemIndex].issuedToRO) {
      this.adjustIssuedLineItem({
        itemIndex,
        netPrice: netPriceForce,
        listPrice: listPriceForce,
        tradePrice: tradePriceForce,
        type: 'STOCKING',
      });
    } else {
      lineItem.forcedNetPrice = netPriceForce;
      lineItem.forcedListPrice = listPriceForce;
      lineItem.forcedTradePrice = tradePriceForce;
      lineItem.forcedPricing = true;

      if (managerOverridePPC) {
        lineItem.forcedManagerOverridePPC = managerOverridePPC;
      }
    }
  }

  @action
  applyUpdateDefaultPart(
    itemIndex: number,
    netPrice: number,
    listPrice: number,
    tradePrice: number,
    description: string,
    subletComment: string,
    unpriced?: boolean
  ) {
    const lineItem = this.lineItems[itemIndex];

    if (['PRO1', 'SMM3', 'SRE2'].includes(this.runMode) && this.lineItems[itemIndex].issuedToRO) {
      this.adjustIssuedLineItem({
        itemIndex,
        netPrice: Math.abs(netPrice),
        listPrice: Math.abs(listPrice),
        tradePrice: Math.abs(tradePrice),
        description,
        type: 'DEFAULTPART',
        subletComment,
      });
    } else {
      lineItem.defaultPartNetPrice = netPrice;
      lineItem.defaultPartListPrice = listPrice;
      lineItem.defaultPartTradePrice = tradePrice;
      lineItem.defaultPartDescription = description;
      lineItem.defaultPartSubletComment = subletComment;

      if (!_.isUndefined(unpriced)) {
        lineItem.unpriced = unpriced;
      }
    }
  }

  @action
  applyUpdateNonStockingPart(
    itemIndex: number,
    partNum: string,
    netPrice: number,
    listPrice: number,
    tradePrice: number,
    description: string,
    stockClass: string,
    priceSymbol: string,
    unpriced?: boolean
  ) {
    const lineItem = this.lineItems[itemIndex];

    // Is this a service screen price adjustment on a previously issued part?
    if (['PRO1', 'SMM3', 'SRE2'].includes(this.runMode) && this.lineItems[itemIndex].issuedToRO) {
      this.adjustIssuedLineItem({
        itemIndex,
        netPrice,
        listPrice,
        tradePrice,
        type: 'NONSTOCKING',
        description,
        stockClass,
        priceSymbol,
      });
    } else {
      lineItem.nonStockingPartNum = partNum;
      lineItem.nonStockingPartNetPrice = netPrice;
      lineItem.nonStockingPartListPrice = listPrice;
      lineItem.nonStockingPartTradePrice = tradePrice;
      lineItem.nonStockingPartDescription = description;
      lineItem.stockClass = stockClass;
      lineItem.priceSymbol = priceSymbol;

      if (!_.isUndefined(unpriced)) {
        lineItem.unpriced = unpriced;
      }
    }
  }

  async autoLoadBlazePPC() {
    // FAST_REFRESH hits the model constructor twice for some reason,
    // and on the first attempt, refs aren't wired to the uibridge yet.
    if (
      (this.runMode === 'PIN1' && !this.uiBridge.customerLookupRef) ||
      (['PIN4', 'PSO3'].includes(this.runMode) && !this.uiBridge.lineItemEntryRef) ||
      (['PRO1', 'SMM3', 'SRE2'].includes(this.runMode) && !this.uiBridge.roNumberInputRef)
    ) {
      return;
    }

    const ppc = _.get(this.appState, 'app.user.ppc', '').trim();
    const seedRun = this.isInitialSeedRun; // important to cache pre-loadPPC()

    if (!!ppc) {
      // No need to validate PPC passwords if non-kiosk mode
      if (await this.loadPPC(ppc, true)) {
        switch (this.runMode) {
          case 'PIN1':
            // Checking for an auto-seed run prevents incorrect focus when closing auto-confirmation modals.
            // This issue does not afflict service screens (likely related to react-autosuggest focus logic here).
            if (!seedRun) {
              this.uiBridge.customerLookupRef.focus();
            }
            break;
          case 'PRO1':
          case 'SMM3':
          case 'SRE2':
            this.uiBridge.roNumberInputRef.focus();
            break;
          case 'PSO3':
            this.uiBridge.lineItemEntryRef.focusPartEntry();
            break;
          default:
            break;
        }
      }
    }
  }

  // Loyalty customer load automation only occurs if:
  // PIN1 - the dealer's customer has exactly 1 loyalty customer linked
  // PRO1 - the loyalty ID was specified during RO open
  async autoLoadLoyaltyCustomer() {
    if (
      (this.runMode === 'PIN1' &&
        this.activeCustomerID !== null &&
        Array.isArray(this.customerParams.loyaltyCustomers) &&
        this.customerParams.loyaltyCustomers.length === 1) ||
      (this.runMode === 'PRO1' && this.lastROLoyaltyCustomerID)
    ) {
      let res: IApiResponse;
      try {
        const activeCustomerID = this.activeCustomerID;
        const activeRONumber = this.activeRONumber;
        const loyaltyCustomerID =
          this.runMode === 'PIN1' ? this.customerParams.loyaltyCustomers[0].id : this.lastROLoyaltyCustomerID;
        const getURL = `/loyalty/customers/${loyaltyCustomerID}`;

        res = await Api.get(getURL);

        // API service throws now on non-200s, so this is a relic ... but in case things change again
        if (res.status !== 200) {
          throw new Error(`200 not received from ${getURL}`);
        }

        if (this.logging) {
          console.warn(`${getURL} Response`);
          console.log(res.data);
        }

        // In case a new customer load occurs before loyalty responds
        if (
          (this.runMode === 'PIN1' && this.activeCustomerID === activeCustomerID) ||
          (this.runMode === 'PRO1' && this.activeRONumber === activeRONumber)
        ) {
          if (!(_.isObject(res.data) && 'externalCustomerId' in res.data)) {
            throw new Error('Invalid response detected. Inspect network tab.');
          }

          // This case should be a status 400 and not hit here, but who knows
          if (_.isObject(res.data) && 'error' in res.data) {
            throw new Error(res.data['error']);
          }

          this.loadLoyaltyCustomer(res.data as any);
        }
      } catch (e) {
        // status 400: {"error":"The value '51164testtest' is not valid for Int32."} (invalid ID format)
        // status 404: Entity not found (invalid ID)
        console.error('Unable to auto-load loyalty customer.');
        console.error(e);
      }
    }
  }

  /* PIN1=/pin1//cust#/conf#? PRO1/SMM3=/pro1/ro#/conf#? SRE2=/sre2/est#
     This method of auto-loading will not pop pending confirmaitons, though backorder status is still loaded.
     Additionally, PIN1 COD input will not auto-pop going this route, but will occur on process.
  */
  async autoLoadSeed() {
    if (this.seedNum && !this.seedLoaded) {
      this.seedLoaded = true;

      if (this.seedNum.startsWith('-')) {
        toastr.error('Error', 'Pick lists not supported in autload mode.');
        return;
      }

      switch (this.runMode) {
        case 'PIN1':
          this.uiBridge.customerLookupRef.value = this.seedNum;
          if (await this.loadCustomerParams(this.seedNum)) {
            if (this.seedConfirmationNum) {
              this.loadConfirmation(this.seedConfirmationNum);
            } else {
              this.uiBridge.lineItemEntryRef.focusPartEntry();
            }
          }
          break;
        case 'PRO1':
        case 'SMM3':
        case 'SRE2':
          // at this point in the code, if PTB2:Parts Setup / "Suppress Issuing Parts" is checked,
          // the runMode is SMM3, otherwise it is PRO1

          const prefix = this.seedNum.substr(0, 3).toUpperCase();

          if (this.runMode === 'SMM3' && prefix === 'EST') {
            toastr.error('Error', 'Estimates available only in SRE2 or PRO1.');
            return;
          }

          if (this.runMode === 'SRE2' && prefix !== 'EST') {
            toastr.error('Error', 'Estimates only (ESTxxx).');
            return;
          }

          this.uiBridge.roNumberInputRef.value = this.seedNum;
          if (await this.loadRepairOrder({ roNumber: this.seedNum, includeAttachments: true })) {
            if (this.seedConfirmationNum && !['SMM3', 'SRE2'].includes(this.runMode) && !this.isEstimate) {
              this.loadConfirmation(this.seedConfirmationNum);
            } else {
              this.uiBridge.showOperationSelectionModal = true;
            }
          }
          break;
      }
    }
  }

  /* Removes forced prices from a line item.

     In the event this was a quoted line, clearing via the force part modal
     reverts the forced prices back to the original quote/pick list (but remains forced).
  */
  @action
  clearForcedPricing(itemIndex: number, revertToQuote: boolean = false) {
    const lineItem = this.lineItems[itemIndex];

    // Is this a service screen price adjustment on a previously issued part?
    if (['PRO1', 'SMM3', 'SRE2'].includes(this.runMode) && this.lineItems[itemIndex].issuedToRO) {
      this.adjustIssuedLineItem({
        itemIndex,
        netPrice: 0,
        listPrice: 0,
        tradePrice: 0,
        type: 'STOCKING',
        clearForce: true,
      });
    } else {
      // Force modal clear request on a quote/picklist/confirmation forced line item
      if (revertToQuote) {
        lineItem.forcedNetPrice = lineItem.detail!.netPrice;
        lineItem.forcedListPrice = lineItem.detail!.listPrice;
        lineItem.forcedTradePrice = lineItem.detail!.tradePrice;

        // Retain original quote MPPCs trail (if any) in the event a clerk hits "Clear Force" on a quoted force.
        // In the event a manager overrides the previous quoted force then reverts back to the quote, his/her MPPC
        // will remain instead here, but at least a manager will be involved for questioning (very unlikely scenario).
        // delete lineItem.forcedManagerOverridePPC;

        // Force modal clear request on non-quote-forced line item or
        // an edit line item (substitute, etc.) request.
      } else {
        delete lineItem.forcedNetPrice;
        delete lineItem.forcedListPrice;
        delete lineItem.forcedTradePrice;
        delete lineItem.forcedPricingFromQuoteOrConfirmation; // probably not necessary here
        delete lineItem.forcedManagerOverridePPC;
        lineItem.forcedPricing = false;
      }
    }
  }

  clearEmailOverrides() {
    this.invoiceEmailsOverride = null;
    this.quoteEmailsOverride = null;
    this.skipEmails = false;
  }

  @action
  clearLoyalty() {
    clearInterval(this.loyaltyOfferBackgroundCheckIntervalID);
    this.loyaltyOfferBackgroundCheckTracker = null; // cancel any pending request
    this.loyaltyOfferCount = 0;

    this.activeLoyaltyCustomerID = null;
    // loyaltyCustomerDetails is only referenced with active loyalty customer ID loaded, so it's stale here
  }

  @action
  clearLineItemSuppressions(itemIndex: number) {
    const lineItem = this.lineItems[itemIndex];
    delete lineItem.suppressCoreCharge;
    delete lineItem.suppressFee;
  }

  clearPPC() {
    this.ppcValidated = false;
    this.ppcPasswordNeeded = false;
    this.ppcForcePasswordValidated = false;
    this.managerOverridePersistedForSession = false;
  }

  // Clears both ship-to methods
  clearShipToOverride() {
    runInAction(() => {
      this.shipToAccount = null;
      this.shipToAddress = null;
      this.shipToEmail = false;
    });

    // this is async, if you need ex-post logic, async/awaitify this method
    this.updateTaxes();
  }

  confirmationLineItemPoNum(lineItem: ILineItem) {
    return (
      lineItem.confirmationSale ? lineItem.confirmationSale.poNum : lineItem.confirmationComment!.poNum
    ) as string;
  }

  confirmationLineItemUniqueID(lineItem: ILineItem) {
    return (
      lineItem.confirmationSale ? lineItem.confirmationSale.uniqueID : lineItem.confirmationComment!.uniqueID
    ) as string;
  }

  // Used by PRO1/SMM3 only
  confirmationPreIssueQueueCheck(): boolean {
    if (this.activeOperation === 0) {
      toastr.error('Error', 'Please select an operation to issue to first.');
      return false;
    }
    if (this.activeRepairOrderDetails.operationDetails[this.activeOperation].locked) {
      toastr.error('Error', 'Unable to issue to locked operation.');
      return false;
    }
    if (this.activeRepairOrderDetails.operationDetails[this.activeOperation].voided) {
      toastr.error('Error', 'Unable to issue to voided operation.');
      return false;
    }

    return true;
  }

  copyToClipboard(text: string, toastrMsg: string) {
    const textArea = document.createElement('textarea') as any;
    textArea.style.position = 'fixed';
    textArea.style.top = 0;
    textArea.style.left = 0;
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = 0;
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.value = text;

    document.body.appendChild(textArea);
    textArea.select();

    try {
      document.execCommand('copy');
    } catch (err: any) {
      console.log('Oops, unable to copy');
    }

    document.body.removeChild(textArea);
    toastr.success('Copied', toastrMsg);
  }

  copyLineItemsToClipboard() {
    const isRO = ['PRO1', 'SMM3', 'SRE2'].includes(this.runMode);

    const items: string[] = [];
    this.lineItems.forEach(lineItem => {
      if (isRO) {
        if (
          !(
            (this.activeOperation === 0 && lineItem.issuedToRO) ||
            (lineItem.roDetail && lineItem.roDetail.operationNumber === this.activeOperation)
          )
        ) {
          return;
        }
      }

      let partNum = '';
      switch (lineItem.partNum) {
        case 'LINEITEM_COMMENT':
          partNum = `#${lineItem.comment}`;
          break;
        case 'LINEITEM_DEFAULTPART':
          partNum = lineItem.defaultPartNum!;
          break;
        case 'LINEITEM_NONSTOCKINGPART':
          partNum = `@${lineItem.nonStockingPartNum}`;
          break;
        default:
          partNum = lineItem.partNum;
          break;
      }

      items.push(`${lineItem.quantity > 1 ? `${lineItem.quantity}.` : ''}${partNum}`);
    });

    if (items.length === 0) {
      toastr.error('Empty', 'No line items to copy.');
    } else {
      this.copyToClipboard(items.join('\n'), 'Line items copied to clipboard.');
    }
  }

  /* Used to change the part number directly on an existing line item. This will most likely be used
     to replace a line item partNum with an alternative such as a part in a different division, a
     substitution, or a supercedence. Upon failure, the caller is notified and the line item is left
     as is with the current part number within the model.
  */
  @action
  async editLineItemPartNum(itemIndex: number, newPartNum: string, newQuantity?: number): Promise<boolean> {
    if (this.runMode === 'PIN1' && this.lockoutChanges) {
      toastr.error('Error', 'Changes locked out.');
      return false;
    }

    if (this.isLineFetching()) {
      toastr.error('Wait', 'Fetch action in process. Please try again in a moment.');
      return false;
    }

    // Swapping out a part line invalidates our "dirty" check for PACCAR offer background checks
    // Since the line count would remain the same (most likely, though a split supercedence could occur)
    // Inert in many cases.
    this.loyaltyOfferBackgroundCheckTracker = null;

    // route this through the eval queue in case the same part is added in the interim of processing
    return this.lineItemQueue.add(async () => {
      let success = true;

      // otherwise update the partNum on the lineItem and re-eval
      const previousPartNum = this.lineItems[itemIndex].partNum;
      const previousQuantity = this.lineItems[itemIndex].quantity;
      const previousSupercedenceDetails = this.lineItems[itemIndex].supercedenceDetails;
      const supercedenceDetailsSupplemental = this.lineItems[itemIndex].supercedenceDetailsSupplemental;

      this.lineItems[itemIndex].fetched = false;
      this.lineItems[itemIndex].partNum = newPartNum;

      if (!_.isUndefined(newQuantity)) {
        this.lineItems[itemIndex].quantity = newQuantity;
      }

      // delete any supercedence details bound to the line item
      delete this.lineItems[itemIndex].supercedenceDetails;
      delete this.lineItems[itemIndex].supercedenceDetailsSupplemental;

      // in this case, a part number change could result in a different line item type
      // wherein coupons may simply not apply, thus we need to remove any existing bound coupons
      // and manually revert on API error

      // Pre-2020 Big Query Coupons
      const previousCouponDetail = this.lineItems[itemIndex].couponDetail;
      delete this.lineItems[itemIndex].couponDetail;

      // PACCAR Loyalty Bond Offers
      const previousLoyaltyOffers = this.lineItems[itemIndex].loyaltyOffers;
      delete this.lineItems[itemIndex].loyaltyOffers;

      // remove any forces and line item suppressions off of this line before swapping parts
      // in the event there is an API error during the new evalLineItem,
      // the user must re-do his/her forces/suppressions (system prob unusuable then anyways)
      this.lineItems[itemIndex].forcedDescription = '';
      this.clearForcedPricing(itemIndex);
      this.clearLineItemSuppressions(itemIndex);

      // remove any informational backordered quantities on the existing part number
      delete this.lineItems[itemIndex].backorderedQuantity;

      // remove any prevoiusly assigned serial numbers
      delete this.lineItems[itemIndex].serialNumbersSelected;

      /* evalLineItem with new partNum which should fetch all new details.
         evalLineItem will automatically check for existing dupes and combine / splice accordingly if
         updating an existing line item succeeds.  If an error occurs during evalLineItem, the lineItems
         were NOT mutated, thus reversion here is safe (API error most likely).
      */
      const res = await this.evalLineItem(itemIndex);
      if (!res) {
        success = false;
        runInAction(() => {
          this.lineItems[itemIndex].partNum = previousPartNum;
          this.lineItems[itemIndex].quantity = previousQuantity;

          if (previousSupercedenceDetails) {
            this.lineItems[itemIndex].supercedenceDetails = previousSupercedenceDetails;
            this.lineItems[itemIndex].supercedenceDetailsSupplemental = supercedenceDetailsSupplemental;
          }

          this.lineItems[itemIndex].couponDetail = previousCouponDetail;
          this.lineItems[itemIndex].loyaltyOffers = previousLoyaltyOffers;
          this.lineItems[itemIndex].fetched = true; // though this refers to the previous fetch
        });
      } else {
        this.updateTaxes();
      }

      return success;
    });
  }

  /* Used to change the quantity directly.  While addStockingPartLineItem updates quantities if a part
     number is re-entered as if it were a new part, this method is used to edit quantities
     directly (via an input box on the line item, etc.).  Upon failure, the caller is notified
     and the line item is left as is at the current quantity witin the model (the caller will need
     to fallback its UI itself.

     Note: This should be called when altering a quantity even in the presence of forced pricing,
     as the user may clear the force out at any time and will need to revert to custom price levels
     dependent on the quantity.
  */
  @action
  async editLineItemQuantity(itemIndex: number, newQuantity: number, updateTaxes = true): Promise<boolean> {
    if (this.runMode === 'PIN1' && this.lockoutChanges) {
      toastr.error('Error', 'Changes locked out.');
      return false;
    }

    // UI should check this before entering quantity-edit mode, so this check is duplicative
    if (this.isLineFetching()) {
      toastr.error('Wait', 'Fetch action in process. Please try again in a moment.');
      return false;
    }

    // non-stocking part case?  no need to hit D3/QM for eval, only tax calc update
    if (_.includes(['LINEITEM_DEFAULTPART', 'LINEITEM_NONSTOCKINGPART'], this.lineItems[itemIndex].partNum)) {
      this.lineItems[itemIndex].quantity = newQuantity;

      if (updateTaxes) {
        this.updateTaxes();
      }

      return true;
    }

    // otherwise, we're gonna be hitting D3/QM to re-eval
    // route this through the eval queue in case the same part is added in the interim of processing
    return this.lineItemQueue.add(async () => {
      let success = true;
      const previousQuantity = this.lineItems[itemIndex].quantity;
      this.lineItems[itemIndex].quantity = newQuantity;
      this.lineItems[itemIndex].fetched = false;

      // If this is a quote-forced stocking part, maintain the quoted
      // forced pricing "base" for revised force reversion to the quoted prices.
      let quotedListPrice: number, quotedNetPrice: number, quotedTradePrice: number;
      let patchQuotedPrices = false;
      if (this.lineItems[itemIndex].forcedPricingFromQuoteOrConfirmation && this.lineItems[itemIndex].detail) {
        patchQuotedPrices = true;
        quotedListPrice = this.lineItems[itemIndex].detail!.listPrice;
        quotedNetPrice = this.lineItems[itemIndex].detail!.netPrice;
        quotedTradePrice = this.lineItems[itemIndex].detail!.tradePrice;
      }

      // evalLineItem with new quantity which may effect pricing levels.
      // Upon error, revert quantity in model and notify caller (API error most likely).
      const res = await this.evalLineItem(itemIndex);
      if (!res) {
        success = false;
        runInAction(() => {
          this.lineItems[itemIndex].quantity = previousQuantity;
          this.lineItems[itemIndex].fetched = true; // though this refers to the previous fetch
        });
      } else {
        if (patchQuotedPrices) {
          this.lineItems[itemIndex].detail!.listPrice = quotedListPrice!;
          this.lineItems[itemIndex].detail!.netPrice = quotedNetPrice!;
          this.lineItems[itemIndex].detail!.tradePrice = quotedTradePrice!;
        }

        if (updateTaxes) {
          this.updateTaxes();
        }
      }

      return success;
    });
  }

  /* eval or re-evals a lineItem against D3/QM
     This will occur when adding new line items or updating existing ones.

     Some customers have different pricing levels based on quantity, so
     any changes resulting in quantity changes of a line item will require a
     re-eval from DBPIP to get correct prices.

     PPC and/or customer information is not -required- to evaluate a line item.
     Perhaps a walk-in simply wants a quick price and doesn't have an account, etc..
     However, supplying a customer id to DBPIP is required to obtain custom
     pricing levels.

     Good luck to anyone modifying this in the future. A written flowchart helped.
  */
  @action
  async evalLineItem(itemIndex: number, existingCheck: boolean = true): Promise<boolean> {
    let success = true;

    // First see if an existing line item has the same partNum as this itemIndex.
    const existingIndex = this.lineItems.findIndex(
      (item, index) =>
        index !== itemIndex && item.partNum === this.lineItems[itemIndex].partNum && !item.confirmationSale
    );

    // Some clients prefer to never auto-combine duplicate parts (see PTB2 to disable)
    const combineDuplicateParts = this.sysParams.combineDuplicateParts;

    /* Does a line item exist with the same part number as the index being eval'd?
       Note we sometimes know we are updating an existing line and don't care about the results of
       the existingIndex check (by default we do). Only slice out line items if you know
       existing mutations succeed.
    */
    if (existingCheck && ~existingIndex && combineDuplicateParts) {
      /* If so, we will combine this line item with the existing line item.  We need not concern
         ourselves with DBPIP auto-corrections as they would have already happened on existing lines.
      */

      /* If the existing index is an "error" part meaning it needs a sub/supr selection, simply
         update the quantity.  There's no reason to re-eval via DBPIP.  Splice out the item being
         eval'd.
      */
      if (this.lineItems[existingIndex].detail === null) {
        runInAction(() => {
          this.lineItems[existingIndex].quantity += this.lineItems[itemIndex].quantity;
          this.lineItems.splice(itemIndex, 1);
          if (this.showToasters) {
            toastr.success('Note', 'Combined with existing line item.');
          }
        });

        // Else the existing part is a normal (non-error state) line item
      } else {
        const previousQuantity = this.lineItems[existingIndex].quantity;

        // Coupons will be automatically updated in re-entrant eval / kept as-is upon failure.
        // We know the line item type won't change due to a quantity change, thus couponDetail will be set.

        // Attempt to update the existing item (combine quantities and re-eval)
        runInAction(() => {
          this.lineItems[itemIndex].extendedFetchingDetails = 'Combining with existing line item';
          this.lineItems[existingIndex].fetched = false;
          this.lineItems[existingIndex].quantity += this.lineItems[itemIndex].quantity;
        });

        // Update the existing line item.  Note, we know exactly which line item to change,
        // so tell the recursive version of evalLineItem to not check existing line items (2nd param).
        const successExistingUpdate = await this.evalLineItem(existingIndex, false);
        if (successExistingUpdate) {
          // existing index .fetched will be set to true via recursive evalLineItem
          this.lineItems.splice(itemIndex, 1);
          if (this.showToasters) {
            toastr.success('Note', 'Combined with existing line item.');
          }

          // Unlikely for a failure updating an existing line item, but in case (network error, etc.)
          // Errors would have already popped.  Revert quantity on existing line item.
        } else {
          this.lineItems[existingIndex].quantity = previousQuantity;
          this.lineItems[existingIndex].fetched = true;
          success = false; // Let caller decide what to do with this line item.
        }
      }

      // Otherwise no line item exists with the same part number as the index being eval'd.
    } else {
      try {
        // sk.model.lastEvalRequest to debug last evalLineItem request
        this.lastEvalReq = {
          customerID: this.activeCustomerID || '',
          partNum: this.lineItems[itemIndex].partNum,
          ppc: this.ppcValidated ? this.ppcParams.ppc : '',
          qty: this.lineItems[itemIndex].quantity,
          recordLostSaleOnInquiry: this.sysParams.recordLostSaleOnInquiry,
        };

        const isRO = ['PRO1', 'SMM3', 'SRE2'].includes(this.runMode);
        if (isRO && this.activeOperation) {
          this.lastEvalReq = {
            ...this.lastEvalReq,
            isRO,
            roNum: this.activeRONumber,
            operationNum: this.activeOperation,
          };
        }

        if (this.logging) {
          console.warn('/partinquiry Request:');
          console.log(this.lastEvalReq);
        }

        const res = await Api.post('/shopkeeper/partinquiry', this.lastEvalReq);
        this.lastEvalRes = res; // debugging - will be available even if parse error below

        if (this.logging) {
          console.warn('/partinquiry Response:');
          console.log(this.lastEvalRes);
        }

        // bi-frost service should throw an error in this case anyways
        if (res.status !== 200) {
          throw new Error('200 not received from partinquiry');
        }

        const detailSupplemental = JSON.parse(res.data.dbpipHelper) as IDetailSupplemental;
        const dbpipDetail = this.extractDbpipSinglePart(res.data.dbpip);
        this.processBranchInventory(detailSupplemental, false);

        /* Attachments for the partNum as entered.  
           If the result is a split supercedence in a fleet-bill, this isn't used.
           In a supercedence case, these are the attachments for the "base" part, not the constituent tails.
        */
        const attachments = res.data.attachments || [];

        switch (dbpipDetail.responseType) {
          // Check for error condition in part extraction
          case DBPIPResponseType.Error:
            const errors = dbpipDetail.errors;

            /* Check the supplemental details for alternatives in order to recover from this condition.
              This supports the case of BAD PART showing up while alternatives exist (deleted part, etc.).
              If no alternatives are found, display a toaster with the errors from D3/QM (BAD PART, etc.).

              Note, delayed supercedences from DBPIPHELPER are not considered by design.  If a suitable
              supercedence exists, DBPIP should autocorrect (but not in the case of cross reference).
            */
            const x = detailSupplemental;

            /* Is this a 1:1 cross referenced part?  Update part number and re-eval the line item.
               Make sure the cross reference part number isn't the current part number or we'll have an unfortuante loop.
            */
            if (x.crossReferences.length === 1 && x.crossReferences[0].partNum !== this.lineItems[itemIndex].partNum) {
              let crPartNum = x.crossReferences[0].partNum;

              // If division is missing, prepend current active division.
              crPartNum = /^[A-Za-z]:/.test(crPartNum) ? crPartNum : `${this.activeDivision.code}:${crPartNum}`;

              if (this.logging) {
                console.warn(`1:1 Cross Reference Detected: ${this.lineItems[itemIndex].partNum} -> ${crPartNum}`);
              }

              this.lineItems[itemIndex].partNum = crPartNum;
              return await this.evalLineItem(itemIndex);
            }

            /* Is this a cross reference with multiple options? If so, we need to pop a modal to allow the user
               to select which cross reference part they want.  We'll do so by setting a model observable the
               LineItemEntry component tracks.
            */
            if (x.crossReferences.length > 1) {
              this.activeCrossReferenceOptions = x.crossReferences;
              this.showCrossReferencesModal = true;

              setTimeout(() => {
                this.uiBridge.lineItemEntryRef.clear();
              }, 100);

              // Removes the "fetching" line for the cross-reference part.
              // TODO: Determine if cross references can ever be sub parts (doubt it). If so, editLineItemPartNum logic will need updates.
              return false;
            }

            if (!x.alternateDivisions.length && !x.branchInventory.length && !x.substitutions.length) {
              throw new Error(errors.join(' / '));
            }

            runInAction(() => {
              /* D3/QM reported an error state, but offered alternatives, so we proceed.
                In this case, lineItem.detail will be null, but detailSupplemental will hold
                the alternatives.

                If specific errors were provided, we'll attach them to the line item.
                An empty array indicates an error state without error messages provided.
              */
              this.lineItems[itemIndex].detail = null;
              this.lineItems[itemIndex].detailSupplemental = detailSupplemental;
              this.lineItems[itemIndex].fetched = true;

              // We know we have some sort of alternative part here, show them automatically.
              this.lineItems[itemIndex].showPartAlternatives = true;

              if (errors.length > 0) {
                this.lineItems[itemIndex].incompleteDetailErrors = errors;
              } else {
                this.lineItems[itemIndex].incompleteDetailErrors = null;
              }
            });

            break;

          case DBPIPResponseType.Supercedence:
            const partLinesDetails = dbpipDetail.partLinesDetails;

            // Check for the case where the requested "base" part (now superceded) was specified with the wrong division.
            // QM may hunt down the part in a different division and return a response stating what the found division was.
            // It suffices to peek at the first supercedence part line returned (in the event of a supercedence split).
            // It's much simpler to update the partnum and re-eval here in order to clear things up (correct DBPIPHELPER info, etc.).
            // We'll only do this if a SUPER tag with a part number is actually specified by QM (it should be).
            const superPhrase = _.find(partLinesDetails[0].errorText, o => /^SUPER[ ]+[^ ]+/.test(o)) as string;
            if (superPhrase) {
              const supercedencePartNum = superPhrase.match(/^SUPER[ ]+([^ ]+)/)![1];

              if (this.lineItems[itemIndex].partNum !== supercedencePartNum) {
                if (this.showToasters) {
                  toastr.success('Note', `Part number autocorrected to: ${supercedencePartNum}`);
                }

                this.lineItems[itemIndex].partNum = supercedencePartNum;
                return await this.evalLineItem(itemIndex);
              }
            }

            // detailSupplemental currently represents the DBPIPHELPER response from the original part lookup
            // We need to pull DBPIPHELPER responses for all of the supercedence "tails" for inquiry memos, etc.
            const dbpipHelperBatchItems = partLinesDetails.map(partLine => partLine.partNum);
            if (this.logging) {
              console.warn('/dbpiphelper Request (Batch Mode):');
              console.log(dbpipHelperBatchItems);
            }

            // Hit QM and Postgres in parallel here
            const [resDbpipHelper, resAttachments] = await Promise.all([
              Api.post('/shopkeeper/dbpiphelper', dbpipHelperBatchItems),
              Api.post('/shopkeeper/attachmentsmulti', dbpipHelperBatchItems),
            ]);

            if (resDbpipHelper.status !== 200 || resAttachments.status !== 200) {
              throw new Error('200 not received from dbpiphelper/attachments (batch mode)');
            }

            if (this.logging) {
              console.warn('/dbpiphelper Response (Batch Mode):');
              console.log(resDbpipHelper);
              console.warn('/attachmentsmulti (Batch Mode):');
              console.log(resAttachments);
            }

            detailSupplemental.supercedenceComments.unshift('PART SUPERCEDED');

            const dbpipHelper = resDbpipHelper.data;
            this.processBranchInventory(dbpipHelper, true);
            const supercedenceDetailsSupplemental = dbpipHelper as ISupercedenceDetailSupplemental;

            const supercedenceAttachments = resAttachments.data as ISupercedenceAttachments; // constituent "tail" attachments
            const supercedenceCouponDetail = dbpipDetail.couponDetail || []; // TS has issues with mobx actions

            runInAction(() => {
              this.lineItems[itemIndex].fetched = true;
              this.lineItems[itemIndex].detailSupplemental = detailSupplemental;
              this.lineItems[itemIndex].attachments = attachments; // "base" part attachments
              this.lineItems[itemIndex].incompleteDetailErrors = null;
              this.lineItems[itemIndex].supercedenceDetails = partLinesDetails;
              this.lineItems[itemIndex].supercedenceDetailsSupplemental = supercedenceDetailsSupplemental;
              this.lineItems[itemIndex].supercedenceAttachments = supercedenceAttachments;
              this.lineItems[itemIndex].couponDetail = supercedenceCouponDetail;
            });

            break;

          // This is specific to fleet-billed scenarios. Non-fleet uses supercedence chains in the prior case.
          case DBPIPResponseType.Split:
            if (this.logging) {
              console.log('');
              console.warn(`FORCED SPLIT DETECTED FOR: ${this.lineItems[itemIndex].partNum}. RESULTANT SET FOLLOWS.`);
              console.warn(dbpipDetail.splitList);
            }

            // Do a quick test for infinite split loops
            if (_.find(dbpipDetail.splitList, { partNum: this.lineItems[itemIndex].partNum })) {
              throw new Error(`Infinite split loop detected for ${this.lineItems[itemIndex].partNum}.`);
            }

            if (this.showToasters) {
              toastr.success('Info', 'Supercedence split detected.');
            }

            // This line is removed after the split parts are added, so no need to clear extendedFetchingDetails ex post.
            this.lineItems[itemIndex].extendedFetchingDetails = `Supercedence Split: ${dbpipDetail.splitList
              .map(item => item.partNum)
              .join(', ')}`;

            // Insert the split parts in serial async actions
            for (const item of dbpipDetail.splitList) {
              await this.addStockingPartLineItem(item.partNum, '', item.quantity, false, true);
            }

            this.lineItems.splice(itemIndex, 1);

            // Otherwise we have a "normal" case where a single part was returned, though
            // it too may have been corrected in some way (fleet billing supercedence chain,
            // or auto division correction, etc.), so check for partNum & quantity alterations.
            break;

          case DBPIPResponseType.Standard:
            const partLineDetail = dbpipDetail.partLinesDetails[0];

            /* Check for auto-division correction on errant division entry as well
              as non-delayed (forced) supercedence.

              For example if user enters X:1234, but DBPIP corrects to A:1234.
              Or, if a user enters T:SHIT which gets superceded to F:SP-5-279X.

              In this case, coupon details can be ignored.  If the auto corrected part
              has associated coupons, we will retrieve on the next eval.
            */
            if (this.lineItems[itemIndex].partNum !== partLineDetail.partNum) {
              const correctedPartNum = partLineDetail.partNum;
              const correctedQuantity = partLineDetail.quantity;

              if (this.showToasters) {
                toastr.success('Note', `Part number autocorrected to: ${correctedPartNum}`);
              }

              // At this point, DBPIP has told us to use a different part number, perhaps as a forced supercedence.
              // In order to have correct supplemental details, D3/QM comments, etc., we must recursively re-eval with
              // the corrected partNum.  The logic should proceed as if the corrected partNum was entered in the first
              // place.  As of this comment, the lineItem has only the old partNum, fetched = false, and qty set.

              this.lineItems[itemIndex].partNum = correctedPartNum;
              this.lineItems[itemIndex].quantity = correctedQuantity;
              return await this.evalLineItem(itemIndex);

              // Otherwise we received complete IPartLineDetail and the partNum does
              // not exist in any other line items.
            } else {
              const z = detailSupplemental;

              // Check for the edgecase wherein the part exists only in MASTER and has cross references defined.
              // In this case, ask the clerk if he wishes to use the cross references or add the MASTER part.
              if (
                _.get(partLineDetail, 'binLocation.0', '') === 'MASTER' &&
                Array.isArray(z.crossReferences) &&
                z.crossReferences.length > 0
              ) {
                // Make sure the cross reference part number isn't the current part number if cr.length is 1
                if (
                  z.crossReferences.length === 1 &&
                  z.crossReferences[0].partNum === this.lineItems[itemIndex].partNum
                ) {
                  // skip this no-op or it's a loop situation, defer to putting the MASTER part on the screen below
                } else {
                  const useCrossReferences: boolean = await new Promise(resolve => {
                    modal.confirm({
                      title: 'Cross References',
                      message: `${this.lineItems[itemIndex].partNum} was located in the MASTER parts list, but has cross references defined.  Do you wish to use the cross references instead?`,
                      confirmLabels: ['Yes', 'No'],
                      callback: () => resolve(true),
                      onClose: () => resolve(false),
                    });
                  });

                  if (useCrossReferences) {
                    this.activeCrossReferenceOptions = z.crossReferences;
                    this.showCrossReferencesModal = true;

                    setTimeout(() => {
                      this.uiBridge.lineItemEntryRef.clear();
                    }, 100);

                    // Removes the "fetching" line for the cross-reference part.
                    return false;
                  }
                }
              }

              const standardCouponDetail = dbpipDetail.couponDetail || []; // TS has issues with mobx actions

              runInAction(() => {
                this.lineItems[itemIndex].detail = partLineDetail;
                this.lineItems[itemIndex].detailSupplemental = detailSupplemental;
                this.lineItems[itemIndex].attachments = attachments;
                this.lineItems[itemIndex].fetched = true;
                this.lineItems[itemIndex].incompleteDetailErrors = null;
                this.lineItems[itemIndex].couponDetail = standardCouponDetail;

                // Determine if we should auto-expand the line item alternatives.
                // This happens if the qty requested is < qty currently on hand, and an alternative is available.
                if (
                  this.lineItems[itemIndex].quantity > (parseInt(z.onHand, 10) || 0) &&
                  (z.alternateDivisions.length || z.branchInventory.length || z.substitutions.length)
                ) {
                  this.lineItems[itemIndex].showPartAlternatives = true;
                }
              });
            }

            break;

          default:
            break;
        }
      } catch (err: any) {
        success = false;
        let error = err.message;

        if (error && error.includes('BAD PART NUMBER')) {
          error = `${error}: ${this.lineItems[itemIndex].partNum}`;
        }

        console.error(error); // Review error after toastr vanishes.
        toastr.error('Error', error);
      }
    }

    return success;
  }

  /* This method identifies and re-composes supercedence chains for non-fleet billed scenarios.
     When loading saved lists, DBPIP provides all part lines in a decompsed form (the constituent
     parts only, with USE and SUPER lines as "error" text to thereby infer supercedence status).
     For non-fleet billed scenarios, we submit the "base" part to the invoicing program so that
     the superedence chain correctly displays on the invoice.  This logic enables that.

     Since NASA was unable to key occurrences, they must be inferred.  If you hit upon a part line
     already grouped in a freshly built supercedence chain, it can be assumed to be the start
     of a new supercedence base part occurrence.

     Further, supercedence base part quantity must me inferred to support quantity escalation.
     For example: A:SHIT -> 2.F:SP-1030.  If the saved list had 3.A:SHIT entered originally, the
     extracted part line would read a quantity of 6 for F:SP-1030, but includes USE 2.F:SP-1030 in
     the error text.  Obtain resultant base part quantity by extracting the right numbers and dividing.

     Note DBPIP co-mingles SUPER/USE and SUBs in the "error" text field (none of which are errors).
     We can reasonably assume the presence of a SUPER and at least 1 USE somewhere within the error
     text array implies a supercedence chain.  We can also reasonably assume the final USE is the
     constitutent part from which to extract the quantity necessary to deduce base part quantity.

     dbpipHelper should logically include supplemental information for all super base parts.
     supplemental info should already be loaded onto the constituent parts at this point.
  */
  extractInferredSupercedences(
    extractedLineItems: ILineItem[],
    dbpipHelper: IDetailSupplemental[],
    attachments: IAttachment[][]
  ): ILineItem[] {
    const inferredLineItems: ILineItem[] = [];
    let supercedenceInference: null | ILineItem = null; // the next "working" supercedence chain, if any

    /* Called when non-superceded line item is encountered which signifies the termination of a
       pending supercedence chain.  Also called after all line items have been walked to close out
       any final working supercedence chain.
    */
    const resolvePendingSupercedenceChain = () => {
      if (supercedenceInference) {
        inferredLineItems.push(_.cloneDeep(supercedenceInference));
        supercedenceInference = null;
      }
    };

    /* Walk the extracted line item input and build a resultant supercedence-inferred line item array.
       This is much simpler than direct mutation and should be plenty speedy (underlying line item
       data structures are simple).
    */
    for (const item of extractedLineItems) {
      // This will dumb-copy eveything but stocking part line items, e.g. comments,
      // default parts, non-stocking parts, and "error" stocking parts w/ subs.
      if (!item.detail) {
        resolvePendingSupercedenceChain();
        inferredLineItems.push(_.cloneDeep(item));
        continue;
      }

      // Assuming we have a stocking part line item, check for a supercedence trail.
      // If there isn't one present, again, dumb-copy the line to the resultant line items.
      if (
        !~_.findIndex(item.detail!.errorText, o => /^SUPER[ ]+[^ ]+/.test(o)) ||
        !~_.findIndex(item.detail!.errorText, o => /^USE[ ]+[^ ]+/.test(o))
      ) {
        resolvePendingSupercedenceChain();
        inferredLineItems.push(_.cloneDeep(item));
        continue;
      }

      /* If we get here, we have a supercedence constituent part we need to work backwards from
         to associate with an original superceded part "base", which may or may not be a chain in process.

         A previous test above insures both SUPER and USE information exists in the correct format,
         otherwise we would have dumb copied the part line to the inference result (which would behave
         analogous to fleet-billed pricing and still result in the same parts/totals, just no supercedence
         trail on the invoice).

         Determine the base supercedence part number. This logic supports interspersed SUB lines in
         the error text field, but assumes the first SUPER phrase encountered is the supercedence
         "hint" from D3/QM.
      */
      const superPhrase = _.find(item.detail!.errorText, o => /^SUPER[ ]+[^ ]+/.test(o)) as string;
      const supercedencePartNum = superPhrase.match(/^SUPER[ ]+([^ ]+)/)![1];

      // Do we have a supercedence chain in process being built?
      if (supercedenceInference) {
        // Did we hit a constituent with a different base part number?
        if (supercedenceInference.partNum !== supercedencePartNum) {
          resolvePendingSupercedenceChain();

          // Otherwise if this constituent line already exists on a work in progress supercedenceInference with the
          // same base part number, this can be logically inferred as a new occurrence of the base part.
        } else if (~_.findIndex(supercedenceInference!.supercedenceDetails, o => o.partNum === item.partNum)) {
          resolvePendingSupercedenceChain();
        }
      }

      // Fresh working inference? Setup the inference template
      if (supercedenceInference === null) {
        supercedenceInference = {
          attachments: undefined,
          couponDetail: [],
          detailSupplemental: undefined, // extracted after template
          extendedFetchingDetails: '',
          fetched: true,
          forcedDescription: '',
          forcedEntry: false,
          forcedPricing: false,
          incompleteDetailErrors: null,
          initialForceModalRan: false,
          partNum: '', // extracted after template
          quantity: 1, // extracted after template
          showPartAlternatives: false,
          supercedenceAttachments: {}, // added per constituent (keyed on part num)
          supercedenceDetails: [], // added per constituent
          supercedenceDetailsSupplemental: {}, // added per constituent (keyed on part num)
        };

        /* Determine the original quantity from the first constituent encountered in this supercession block.
           This logic supports interspersed SUB lines, but assumes the final USE phrase encountered is the
           constituent "tail".
        */
        const useTailPhrase = _.findLast(item.detail!.errorText, o => /^USE[ ]+[^ ]+/.test(o)) as string;
        const useTailQtyEscalation = useTailPhrase.match(/^USE[ ]+(\d{1,4})\..+$/);

        // Determine the inferred base part quantity.  This will be the quantity specfiied in the
        // constituent line unless a quantity escalation has occurred such as A -> USE 2.B.
        if (useTailQtyEscalation) {
          supercedenceInference.quantity = item.quantity / (parseInt(useTailQtyEscalation[1], 10) || 1);
        } else {
          supercedenceInference.quantity = item.quantity;
        }

        // Finish template seed
        supercedenceInference.partNum = supercedencePartNum;
        supercedenceInference.attachments = attachments[supercedencePartNum];
        supercedenceInference.detailSupplemental = _.cloneDeep(dbpipHelper[supercedencePartNum]);
        supercedenceInference.detailSupplemental!.supercedenceComments.unshift('PART SUPERCEDED');
        // The clone was important here since we're modifying comments on the helper entry, and other
        // constituents may reference the same helper entry (dbpiphelper combines automatically).
      }

      // Add this constituent part to the working inference
      supercedenceInference.supercedenceAttachments![item.partNum] = item.attachments!;
      supercedenceInference.supercedenceDetails!.push(item.detail);
      supercedenceInference.supercedenceDetailsSupplemental![item.partNum] = item.detailSupplemental!;
    }

    resolvePendingSupercedenceChain();

    if (this.logSuperChains) {
      console.warn('****** EXTRACT INFERENCES DEBUG ******');
      console.warn('Extracted Line Items: ');
      console.log(extractedLineItems);
      console.warn('DBPIPHELPER: ');
      console.log(dbpipHelper);
      console.warn('Inferred Line Items: ');
      console.log(inferredLineItems);
      console.warn('****** END EXTRACT INFERENCES DEBUG ******');
    }

    return inferredLineItems;
  }

  /* Fleetcross Search
     If both a part number and manufacturer index are specified, a cross reference
     search is performed for that particular manufacturer.
  */
  async fleetcross(partnumber: string, mfrindex: string = ''): Promise<any> {
    let response;
    try {
      response = (await Api.post('/shopkeeper/fleetcross', { partnumber, mfrindex })).data;
    } catch (e) {
      response = { data: null, error: 'Network Error' };
    }
    return response;
  }

  /* Only allow NET/LIST/SELL price overrides on stocking parts if the ppcParams say it's
     allowed for the current PPC. Non-tracking / default parts always allow forces tautologically.

     Manager overrides use their own side-channel ppc permission check and do not rely on this directly.
   */
  @computed
  get forcePartsAllowed(): boolean {
    return this.ppcValidated && this.ppcParams.permissions.forceParts;
  }

  set forceSalesTax(value: boolean) {
    // This only applies if there is a loaded customer who is not set as taxable
    if (this.activeCustomerID && !this.customerParams.taxable && value !== this._forceSalesTax) {
      this._forceSalesTax = value;
      this.updateTaxes();
    }
  }
  get forceSalesTax() {
    return this._forceSalesTax;
  }

  // PIN1 email override feature
  getEmailOptions() {
    return {
      invoiceEmails:
        this.invoiceEmailsOverride ??
        (this.customerParams.invoiceEmails === '' ? [] : this.customerParams.invoiceEmails.split(',')),
      quoteEmails:
        this.quoteEmailsOverride ??
        (this.customerParams.quoteEmails === '' ? [] : this.customerParams.quoteEmails.split(',')),
      skipEmails: this.skipEmails ? 'Y' : 'N',
    };
  }

  // Extract candidate line item details for Loyalty Offer search/pairing (only "stocking" PIV1 parts will be considered)
  // This is used to seed the clerk initiated loyalty offer modal. The background offer checker has its own logic.
  getLoyaltyCandidateLineItems(): ILoyaltyCandidate[] | null {
    if (this.activeLoyaltyCustomerID === null) {
      console.warn('Loyalty Customer Not Loaded');
      return null;
    }

    if (this.lineItemsQueued.length > 0) {
      toastr.error('Wait', 'Queued items pending fetch. Please try again in a moment.');
      return null;
    }

    let anyLineFetching = false; // queue may be empty with 1 pending fetch
    const candidateLineItems: ILoyaltyCandidate[] = [];
    this.lineItems.forEach((lineItem, i) => {
      if (!lineItem.fetched) anyLineFetching = true;

      /* TODO: support supercedence cases (w/ escalation and splits) - Ben says ok to skip for now
         loyaltyOfferIDs seeds the selection control

         As of 6/2020, this will be 1 offer max per part line.
      */
      if (lineItem.fetched && lineItem.detail && !lineItem.supercedenceDetails && !lineItem.issuedToRO) {
        candidateLineItems.push({
          lineItemIndex: i,
          loyaltyOfferIDs: lineItem.loyaltyOffers ? lineItem.loyaltyOffers.map(o => o.OfferPartId) : [],
          partNumber: lineItem.partNum.slice(2), // PACCAR doesn't need leading dealer divisions
        });
      }
    });

    if (anyLineFetching) {
      toastr.error('Wait', 'Fetch action in process. Please try again in a moment.');
      return null;
    }

    return candidateLineItems;
  }

  // The invoicing system ultimately just cares about the price symbol
  getLoyaltyDefaultPart() {
    const couponDefaultPartName = this.sysParams.couponDefaultPartName;

    // Per DSI QM testbox 3/20/20 - most likely universal default value
    const defaultValues = {
      partNum: 'COUPON',
      priceSymbol: '160',
      stockClass: '9999',
    };

    // Many systems will not have this set (requires manual intervention on PTB2)
    // Ensure the name specified (if set) within PTB2 actually exists in the ATB3 price symbol table
    if (!!couponDefaultPartName) {
      return this.defaultParts.get(couponDefaultPartName.toUpperCase()) || defaultValues;
    } else {
      return defaultValues;
    }
  }

  /*  Contrived once per invoicing session - relies on server time w/ local client Fallback
      Contrived invoice number is DEALERCUSTNUM-UNIXEPOCHSECS, which has a realistic collision probability of 0
  
      On the service side, the contrived invoice number is reset after each issue.  It makes sense to
      still contrive proxy numbers vs. using the known RO number for validation purposes.  If any stepwise
      redemption fails and is reattempted later, or other loyalty offers are redeemed on new parts issued later,
      using proxies makes the validation logic more straightforward vs using the same number (RO number) for all
      attempts and issues.
  */
  async getLoyaltyInvoiceNumber() {
    if (!this.loyaltyInvoiceNumber) {
      this.loyaltyInvoiceNumber = `${this.activeCustomerID}-${Math.round(
        _.get(await Api.get('/shopkeeper/now'), 'data', Date.now()) / 1000
      )}`;
    }
    return this.loyaltyInvoiceNumber;
  }

  // When a new part is pre-issued to an operation, setup some config for proper display
  getPreissueDetails() {
    return ['PIN1'].includes(this.runMode)
      ? {}
      : {
          issuedToRO: false,
          roDetail: {
            operationNumber: this.activeOperation,
            lineNumber: 0, // indicates pre-issue
            roKey: '', // indicates pre-issue
          },
        };
  }

  /* Determines a unique set of stock classes and returns suggested vendor information.
     In addition, any miscellaneous vendors from ATB13 are returned under the ATB13MISC key.

     Note: by NASA API design, an empty array value for any stock class key indicates no suggested
     vendors have been defined for that stock class.  A MISSING stock class key in the response
     indicates the specified stock class (key) is invalid.
  */
  async getVendorSuggestions(stockClasses: string[]): Promise<IVendorSuggestions> {
    let res: IApiResponse;
    try {
      res = await Api.post('/shopkeeper/vendorsuggestions', _.uniq(stockClasses));
      if (res.status !== 200) {
        throw new Error('200 not received from vendorsuggestions');
      }
    } catch (err: any) {
      console.error(err.message);
      toastr.error('Error', 'Unable to fetch vendor suggestions.');
      return { ATB13MISC: [] }; // this shouldn't be a fatal event to callers
    }
    return res.data;
  }

  @computed
  get isEstimate(): boolean {
    return (
      ['PRO1', 'SMM3', 'SRE2'].includes(this.runMode) &&
      this.activeRONumber !== null &&
      this.activeRONumber.substr(0, 3).toUpperCase() === 'EST'
    );
  }

  // On serivce screens, line items located on a locked/voided op should not be editable.
  isLineItemCellEditable(itemIndex: number): boolean {
    let editable = true;

    if (['PRO1', 'SMM3', 'SRE2'].includes(this.runMode)) {
      const lineItem = this.lineItems[itemIndex];
      if (lineItem.issuedToRO) {
        const operationNum = lineItem.roDetail!.operationNumber;
        const operationDetails = this.activeRepairOrderDetails.operationDetails[operationNum];
        if (operationDetails.locked || operationDetails.voided) {
          editable = false;
        }
      }
    }

    return editable;
  }

  // It's possible an auto-seed run session hits a cancel/reset event to start a new session.
  // This only returns true on the inital seed pass.  Used to manage various auto focus scenarios.
  get isInitialSeedRun(): boolean {
    return !!this.seedNum && !this.seedLoaded;
  }

  // Used by PIN1 to transfer stock to a RO.  This is used by lease & rental heavy locations.
  @computed
  get isROTransferCustomerLoaded(): boolean {
    return !!(
      this.activeCustomerID &&
      _.find(
        this.sysParams.transferAccounts,
        o => o.account.toUpperCase() === this.activeCustomerID!.toUpperCase() && o.type === 'ro'
      )
    );
  }

  // See shopkeeper.controller.js:customerParams() for explanation.
  // Does not apply if isROTransferCustomerLoaded is true.
  @computed
  get isLedgerDetailRequired(): boolean {
    return !!(
      this.activeCustomerID &&
      (this.customerParams.internalCustomerGLAccountInputRequired ||
        this.customerParams.internalCustomerGLAccountWithControlRequired !== '')
    );
  }

  // Is any line item in the model currently in a fetching state?
  isLineFetching(): boolean {
    return _.some(this.lineItems, o => !o.fetched);
  }

  /* Logic to display the invoice credit modal
     partSearchFilterSeed will seed the invoice credit part number filter (but not enable it automatically)

     If adding from the invoice history tab, partSearchFilterSeed will be any part number serach currently active.
     If adding from the core bank tab, partSearchFilterSeed will be the core part number.
  */
  loadCreditInvoice(invoiceNum: string, partSearchFilterSeed: string) {
    // Make sure this invoice exists in our data
    if (!this.customerHistory.invoiceData[invoiceNum]) {
      toastr.error('Error', 'Invoice Data Not Found');
      return;
    }

    // It doesn't make sense to credit a credit invoice
    if (this.customerHistory.invoiceData[invoiceNum].creditInvoice) {
      toastr.error('Error', 'Cannot Credit a Credit Invoice');
      return;
    }

    // Don't mutate the original data structure, in case we need to debug on a client
    const invoiceDetails = _.cloneDeep(this.customerHistory.invoiceData[invoiceNum]);

    /* Core/Fee data from QM is not logically linked in the JSON data structure from QM.
       Base Part, Clean Core, and Dirty Core lines all appear as separate part lines with no linking.
       We require the logical linking for mutually exclusive credit control implementation.
       For example, if crediting a dirty core or fee, the base part should not be creditable.
       The logic assumes clean/dirty cores and fees come directly after base parts.

       Note the clean core part logs a credit both when a dirty core is returned, as well as
       when a base part is credited, so the same clean core could potentially be credited twice.

       As a result, the credit quantities reported on the core parts are too ambiguous for
       determining  the true number of outstanding dirty cores, thus we must rely on core bank
       data to determine if a clean core is directly creditable.

       Additionally, dirty cores credit history (* parts) are only tracked directly with an
       invoice if they are returned at the time of the sale via core suppression, thus we cannot
       rely on dirty core data at all to draw any reasonable conclusions (at least as of 07/2020).

       Per Phil, if trade2/3/4 damage levels are all 0, the user can input a value or % up to
       the trade(1) price.  However, if any level is non-0, the user must select a level, which might
       also possibly be 0.

       Notes:
       If a dirty core is credited at invoice time (via core suppression variance), the invoice
       data will show a +1 clean core credit and a -1 dirty core credit.  Crediting the base part
       after that adds a -1 clean core credit and a +1 dirty core credit.  We should be able to
       use absolute values for our purposes.
    */
    let lastBasePartIndex: number | null = null; // last base part index found
    for (let i = 0; i < invoiceDetails.partLines.length; i++) {
      const partLine = invoiceDetails.partLines[i]; // active part line scan
      const partNumLastChar = partLine.partNum.slice(-1);

      // Base part link candidate
      const linkCandidate = lastBasePartIndex === null ? null : invoiceDetails.partLines[lastBasePartIndex];

      // Strip force part * leading indicator if present
      partLine.partNum = partLine.partNum[0] === '*' ? partLine.partNum.slice(1) : partLine.partNum;

      // Force credit qtys to positive values, recalc creditQty accordingly
      let creditQtyOverride = 0;
      partLine.creditDetails.forEach(creditDetail => {
        creditDetail.qty = Math.abs(creditDetail.qty);
        creditQtyOverride += creditDetail.qty;
      });
      partLine.creditQty = creditQtyOverride;

      // Dirty core data is not utilized as of 07/2020, no need to display, mark for sweep ex-post.
      // Update 10/2021: Some clients directly sell dirty cores to customers (City Bus, Kenworth NE, etc.)
      // if (partNumLastChar === '*') {
      //   partLine['softDelete'] = true;
      //   continue;
      // }

      // Is this a clean core?
      if (partNumLastChar === '#') {
        // reference to the core data structure we'll mutate
        let x: ICreditPartLine['coreData'];

        // Move the data to the base part data structure if we have a matching base part
        if (linkCandidate && partLine.partNum === `${linkCandidate.partNum}#`) {
          linkCandidate.coreData = partLine;
          linkCandidate.separateCleanCoreInvoice = false;

          partLine['softDelete'] = true; // core line is now linked to base part
          x = linkCandidate.coreData;

          // Otherwise this is a dedicated core invoice case
        } else {
          x = partLine;
          x.corePartUnlinked = true;
          x.separateCleanCoreInvoice = false;
        }

        // Look to the core bank to determine the # of outstanding cores
        let dirtyCoresOutstanding = 0;
        this.customerHistory.coreBank.forEach(item => {
          if (item.invoiceNum === invoiceNum && partLine.partNum === `${item.partNum}#`) {
            dirtyCoresOutstanding += item.qty;
          }
        });
        x.dirtyCoresOutstanding = dirtyCoresOutstanding;

        // If any damage level value is set (PIV1), use drop down selection.
        // Otherwise, show an input box for % or exact amount.
        x.useDamageLevels = x.trade2 !== 0 || x.trade3 !== 0 || x.trade4 !== 0;

        // Calculate coreReturnExceeded if the core's stock class has a configured value for core return days
        x.coreReturnDaysExceeded = false;
        if (dirtyCoresOutstanding > 0 && x.stockClass !== '' && this.coreReturnDays[x.stockClass]) {
          const stockClassCoreReturnDays = parseInt(this.coreReturnDays[x.stockClass], 10) || -1;
          if (~stockClassCoreReturnDays) {
            // Compare YYYY-MM-DD strings directly to negate timezone shift in epoch calcs
            const serverDateEpoch = new Date(moment(this.customerHistory.serverEpoch).format('YYYY-MM-DD')).getTime();
            const invoiceDateEpoch = new Date(invoiceDetails.invoiceDate).getTime();

            // Make sure QM data isn't wack or missing
            if (!Number.isNaN(invoiceDateEpoch)) {
              const oneDay = 24 * 60 * 60 * 1000;
              const diffDays = Math.floor((serverDateEpoch - invoiceDateEpoch) / oneDay);

              if (diffDays > stockClassCoreReturnDays) {
                x.coreReturnDaysExceeded = true;
                x.coreReturnDaysForStockClass = stockClassCoreReturnDays;
                x.daysSinceInvoiceDate = diffDays;
              }
            }
          }
        }

        continue; // cores & fees don't update last found BPI
      }

      // Is this a fee part?
      if (partNumLastChar === '&') {
        // Move the data to the base part data structure
        if (linkCandidate) {
          if (partLine.partNum === `${linkCandidate.partNum}&`) {
            linkCandidate.feeData = partLine;
            partLine['softDelete'] = true;
          }
          // else leave it as a stand alone non-linked line (should not happen)
        }
        continue; // cores & fees don't update last found BPI
      }

      // Otherwise we've encountered a new base part candidate for (possible) core/fee control linkage
      lastBasePartIndex = i;
    }

    // sweep soft deleted part lines from invoice top-level partLines array (cores, fees, etc.)
    invoiceDetails.partLines = invoiceDetails.partLines.filter(partLine => !partLine['softDelete']);

    // Logic to extract invoice data and re-org it into a bound state
    this.activeCreditInvoice = {
      partSearchFilterSeed, // initial seed only
      invoiceNum,
      ...invoiceDetails,
    };

    this.showCreditInvoiceModal = true;
  }

  async loadConfirmation(confirmationNum: string) {
    if (this.confirmationDetailFetchLock) {
      toastr.error('Error', 'Confirmation Load in Progress. Try Again Momentarily.');
      return;
    }

    this.confirmationDetailFetchLock = true;
    this.activeConfirmation = null;
    this.showConfirmationModal = true; // will show loading indicator for snappy UI response

    const isRO = ['PRO1', 'SMM3', 'SRE2'].includes(this.runMode);

    let res: any;
    let dbpip: string;
    let dbpipRows: string[];

    try {
      res = await Api.post('/shopkeeper/loadconfirmation', { confirmationNum, ppc: this.ppcParams.ppc, isRO });
      if (this.logging) {
        console.warn('/loadconfirmation Response');
        console.log(res.data);
      }

      if (res.status !== 200) {
        throw new Error('200 not received from loadconfirmation');
      }

      dbpip = _.get(res, 'data.dbpip', '');
      dbpipRows = dbpip.split('\n');
      if (dbpipRows.length < 61 || dbpipRows[60] === '') {
        throw new Error('Invalid Confirmation Response');
      }

      // Confirmation previously voided / invalid confirmation #
      if (dbpipRows[12] !== '') {
        throw new Error(dbpipRows[12]);
      }
    } catch (err: any) {
      console.error(err.message);
      toastr.error('Error', err.message);
      this.confirmationDetailFetchLock = false;
      this.showConfirmationModal = false;
      return;
    }

    /* We will utilize exactly what the confirmation produces rather than inferring supercedencces ex post.
       In the worst case, this will behave identical to fleet-billed customers where "USE" trails are ignored.

       Flagging confirmationExtraction as true should produce lineItems ready for direct insertion from the
       confirmation modal, though the action type on the confirmationSale structure will need to be set,
       depending on the user's desires (force receive vs sell).
    */
    let extractedLineItems = (await this.extractDbpipMultiPartTrusted(dbpipRows, true)).extractedLineItems;

    /* As of March 2021, a QM bug exists wherein duplicated rows / unique ids are being returned by QM, which should not happen.
       The legacy team is unable to figure out why.  So we'll filter and alert here for manual inspection as it occurs.
       This patch prevents the ConfirmationModal from crashing in various scenarios where unique ids are assumed.
       Hopefully over time, legacy will track down the cause of the duplicate lines.
    */
    const dupeUniqueIDMap: { [uniqueID: string]: true } = {};
    const errors: string[] = [];
    let problemDetected = false;

    extractedLineItems = extractedLineItems.filter(o => {
      const uniqueID = this.confirmationLineItemUniqueID(o);

      if (uniqueID === '') {
        problemDetected = true;
        const error = `Confirmation line item missing unique ID: ${o.partNum}`;
        errors.push(error);
        console.error(error);
        return false;
      } else if (dupeUniqueIDMap[uniqueID]) {
        problemDetected = true;
        const error = `Duplicate confirmation line item key detected: ${uniqueID}`;
        errors.push(error);
        console.error(error);
        return false;
      } else {
        dupeUniqueIDMap[uniqueID] = true;
        return true;
      }
    });

    if (problemDetected) {
      console.error('Confirmation data issue detected. Please alert DSI programming.');
      Api.post('/shopkeeper/loginvalidconfirmationdata', {
        confirmationNum,
        customerNum: this.activeCustomerID,
        dbpip,
        errors,
        roNum: this.activeRONumber || '', // only used by PRO1/SMM3
      });
    }

    let shipToAccount: string | undefined;
    let shipToAddress: IShipToAddress | undefined;

    if (dbpipRows[6] !== '' && dbpipRows[6] !== '0') {
      shipToAccount = dbpipRows[6];
    } else if (dbpipRows[7] !== '') {
      const x = dbpipRows[7].split('ý');
      shipToAddress = {
        name: x[0] || '',
        address: x[1] || '',
        city: x[2] || '',
        phone: x[3] || '',
        zipCode: x[6] || x[8] || '',
        state: x[7],
      };
    }

    this.activeConfirmation = {
      details: {
        confirmationNumber: dbpipRows[60] || '',
        contactName: dbpipRows[63] || '',
        contactPhone: dbpipRows[64] || '',
        contactEmail: dbpipRows[66] || '',
        contactFax: dbpipRows[65] || '',
        customerPo: dbpipRows[8] || '',
        purchasingComments: dbpipRows[67],
        shipIn: dbpipRows[61] || '',
        shipOut: dbpipRows[62] || '',
        shipToAccount,
        shipToAddress,
        timestamp: dbpipRows[72] || '',
      },
      lineItems: extractedLineItems,
    };

    // Check for freight/fuel charges tied to the CONFIRMATION (not PSO6 added PO freight)
    // In test I found we shouldn't depend on the presence of the value mark on either line
    if (dbpipRows[75] !== '') {
      let freight: null | number = null;
      let freightSold = false;
      let fuel: null | number = null;
      let fuelSold = false;
      let rowParts: string[];

      rowParts = dbpipRows[75].split('ý');
      freight = parseFloat(rowParts[0]) || 0;
      fuel = parseFloat(rowParts[1]) || 0;

      // Check for sold flags
      if (dbpipRows[76] !== '') {
        rowParts = dbpipRows[76].split('ý');
        freightSold = rowParts[0] === 'S';
        fuelSold = rowParts[1] === 'S';
      }

      if (freight) {
        this.activeConfirmation.details.freight = freight;
        this.activeConfirmation.details.freightSold = freightSold;
      }

      if (fuel) {
        this.activeConfirmation.details.fuel = fuel;
        this.activeConfirmation.details.fuelSold = fuelSold;
      }
    }

    this.confirmationDetailFetchLock = false;
  }

  // This need only be called once per PIN1 load, as it might map thousands of stock classes.
  async loadCoreReturnDays() {
    let res: IApiResponse;

    try {
      res = await Api.get('/shopkeeper/corereturndays');
      if (res.status !== 200) {
        throw new Error('200 not received from corereturndays');
      }

      if (this.logging) {
        console.warn('/corereturndays Response');
        console.log(res.data);
      }

      // Axios will auto-parse this as JSON
      if (typeof res.data === 'string') {
        throw new Error('Invalid JSON received from corereturndays.');
      }

      this.coreReturnDays = res.data;
    } catch (err: any) {
      console.error(err.message);
      // Failed loading/parsing core return days, is not fatal to invoicing session, but alert user
      toastr.error('Error', 'Error Parsing Core Return Days.  Alert DSI.');
      return;
    }

    // We'll only try once.  If it fails to load/parse, no return limits will be set.
    this.coreReturnDaysLazyLoaded = true;
  }

  // Load confirmation status/detail (usually in a non-awaited lazy fashion, other than prebills).
  // This should be fast since any 1 customer shouldn't have a huge amount of confirmations.
  async loadConfirmationStatusDetail() {
    this.confirmationDetailLazyLoaded = false;

    let res: IApiResponse;
    try {
      const isRO = ['PRO1', 'SMM3', 'SRE2'].includes(this.runMode);
      const id = isRO ? this.activeRONumber : this.activeCustomerID;

      res = await Api.post('/shopkeeper/confirmationstatusdetail', { id, isRO });
      if (res.status !== 200) {
        throw new Error('200 not received from confirmationstatusdetail');
      }

      if (this.logging) {
        console.warn('/confirmationstatusdetail Response');
        console.log(res.data);
      }

      const confirmationStatus = JSON.parse(res.data.status);
      const confirmationDetail = JSON.parse(res.data.detail);

      // If the above parsed and the active id value hasn't changed during this lazy load
      const activeId = isRO ? this.activeRONumber : this.activeCustomerID;
      if (id === activeId) {
        this.confirmationDetail = {
          partsOnBackorder: confirmationDetail,
          statuses: confirmationStatus,
        };

        for (const part of this.confirmationDetail.partsOnBackorder) {
          part.uniqueID = `${part.confirmationNumber}-${part.poNumber}-${part.lineNumber}`;
          part.onHandNum = parseInt(part.onHand, 10) || 0;
          part.qtyNum = parseInt(part.qty, 10) || 1;

          // Phil confirmed 1/2/20 he hardcodes the part number as "FREIGHT" on PSO6 PO freight
          // Pedantically speaking, this *might* diverge from the ATB3 "invoice title" set for PSY 53,
          // but in this case that's a distinction without a difference..
          if (/:FREIGHT$/.test(part.part)) {
            part.description = `FREIGHT: ${part.poNumber}`;
            part.isFreight = true;
          }
        }

        this.confirmationDetailLazyLoaded = true;
      }
    } catch (err: any) {
      console.error(err.message);
      // Failed loading/parsing confirmaitons, is not fatal to invoicing session
      return;
    }
  }

  /* Load customer history (invoice history, core bank)
     This might be slow for customers with a huge history, so only load on demand.
     By default, loads # of days specified on PTB2 / Blaze / Customer History Days.
     The clerk can use the "Full History" feature on the customer history modal to show all history.
  */
  async loadCustomerHistory(all = false) {
    this.customerHistoryLazyLoaded = false;

    let res: IApiResponse;
    try {
      const activeCustomerID = this.activeCustomerID;

      res = await Api.post('/shopkeeper/customerhistory', { all, id: this.activeCustomerID });
      if (res.status !== 200) {
        throw new Error('200 not received from customerhistory');
      }

      if (this.logging) {
        console.warn('/customerhistory Response');
        console.log(res.data);
      }

      // QM returns all fields as strings
      let coreBank = JSON.parse(res.data.coreBank);
      let invoiceData = JSON.parse(res.data.invoiceHistory);
      const serverEpoch = res.data.serverEpoch;

      // Cleanup the core bank
      if (Array.isArray(coreBank)) {
        for (const item of coreBank) {
          item.qty = parseInt(item.qty, 10) || 0;
          item.serials = _.get(item, 'details', [])
            .map((x: any) => x.serialNumber)
            .join(', ');
        }
      } else {
        coreBank = [];
      }

      // Cleanup invoice data - not TS safe here just yet, so be careful
      if (_.isPlainObject(invoiceData)) {
        for (const invoiceNum of Object.keys(invoiceData)) {
          const item = invoiceData[invoiceNum];
          item.cashSale = item.cashSale === 'Y';
          item.ccPendingCredit = item.ccPendingCredit === 'Y';
          item.creditInvoice = item.creditInvoice === 'Y';
          item.creditCardSale = item.creditCardSale === 'Y';
          item.legacyCreditCardSale = item.legacyCreditCardSale === 'Y';
          item.invoiceTotal = parseFloat(item.invoiceTotal) || 0;

          if (Array.isArray(item.partLines)) {
            for (const partLine of item.partLines) {
              partLine.qty = parseInt(partLine.qty, 10) || 0;
              partLine.sellPrice = parseFloat(partLine.sellPrice) || 0;
              partLine.deleted = partLine.deleted === 'true';
              partLine.noTrackCores = partLine.noTrackCores === 'true';

              if (partLine.hasOwnProperty('trade2')) partLine.trade2 = parseFloat(partLine.trade2) || 0;
              if (partLine.hasOwnProperty('trade3')) partLine.trade3 = parseFloat(partLine.trade3) || 0;
              if (partLine.hasOwnProperty('trade4')) partLine.trade4 = parseFloat(partLine.trade4) || 0;

              /* Phil: "The separateCleanCoreTrade field will be an empty string ("") if there is no corresponding core part on the next invoice."

                 This has the very unlikely potential of false positive if 2 consecutive single core invoices have the same core part.
                 We'll detect that edge case in the loadCreditInvoice() when core parts are linked to base parts and adjust flags accordingly.
              */
              partLine.separateCleanCoreTrade = parseFloat(partLine.separateCleanCoreTrade);
              partLine.separateCleanCoreInvoice = !Number.isNaN(partLine.separateCleanCoreTrade); // preliminary rubric 99% case

              partLine.creditQty = parseInt(partLine.creditQty, 10) || 0;

              partLine.loyaltyCoupons.forEach((loyaltyCoupon: any) => {
                loyaltyCoupon.qty = parseInt(loyaltyCoupon.qty, 10) || 0;
                loyaltyCoupon.sellPrice = parseFloat(loyaltyCoupon.sellPrice) || 0;
              });
            }
          }
        }
      } else {
        invoiceData = {};
      }

      // Has core return data been loaded in this PIN1 session?
      if (!this.coreReturnDaysLazyLoaded) await this.loadCoreReturnDays();

      // If the above parsed and the active customer hasn't changed during this lazy load (full TS after this)
      if (activeCustomerID === this.activeCustomerID) {
        this.customerHistory = {
          coreBank,
          invoiceData,
          serverEpoch,
        };
        this.customerHistoryLazyLoaded = true;
      }
    } catch (err: any) {
      console.error(err.message);
      // Failed loading/parsing customer history, is not fatal to invoicing session
      return;
    }
  }

  // PIN1 load customer (this is NOT used by service screens PRO1/SMM3)
  async loadCustomerParams(customerID: string): Promise<boolean> {
    if (this.modelLocked) {
      toastr.error('Wait', 'Fetch action in process. Please try again in a moment.');
      return false;
    }

    let res: IApiResponse;

    this.customerLoadInProcess = true;

    try {
      res = await Api.post('/shopkeeper/customerparams', { id: customerID });
      if (res.status !== 200) {
        throw new Error('200 not received from customerparams');
      }
    } catch (err: any) {
      this.customerLoadInProcess = false;
      console.error(err.message);
      toastr.error('Error', `Unable to load customer: ${customerID}`);
      return false; // leave customer/invoice status as is
    }

    this.customerLoadInProcess = false;

    if (this.logging) {
      console.warn(`/customerparams Response (ID:${customerID}):`);
      console.log(res.data);
    }

    const customerParams: ICustomerParams = res.data.params;

    if (customerParams.name1 === 'Customer not found') {
      toastr.error('Error', `Customer ID not found: ${customerID}`);
      return false; // leave customer/invoice status as is
    } else if (customerParams.statusInactive) {
      toastr.error('Error', `Customer: ${customerID} is INACTIVE.`);
      return false; // leave customer/invoice status as is
    } else if (customerParams.isProspect) {
      toastr.error('Error', `Customer: ${customerID} is currently flagged as only a prospect.`);
      return false; // leave customer/invoice status as is
    } else if (!customerParams.invoicing) {
      toastr.error('Error', `${customerID} is not invoiceable.  Select a ship-to account for this customer.`);
      return false; // leave customer/invoice status as is
    } else {
      this.customerParams = customerParams;
      this.customerParams.isCodAccount = !!~_.findIndex(
        this.sysParams.codAccounts,
        o => o.codAccount === customerID.toUpperCase()
      );

      // Used for real-time over limit visual feedback
      if (this.customerParams.availableCreditMsg.startsWith('Available Credit $')) {
        const creditLimit = parseFloat(this.customerParams.availableCreditMsg.substring(18).replaceAll(',', ''));
        if (creditLimit) {
          this.customerParams.creditLimit = creditLimit;
        }
      }

      runInAction(() => {
        this.clearLoyalty();
        this.activeCustomerID = customerID;
        this.confirmationsCreatedThisSession = [];
        this.confirmationDetailLazyLoaded = false;
        this.customerHistoryLazyLoaded = false;
        this.customerPickListsLazyLoaded = false;
        this.customerVehiclesLazyLoaded = false;
        this.fleetBillingCCVariance = null;
        this.loyaltyInvoiceNumber = null;
        this.confirmationPartsAddedToInvoice.clear();
        this.po = null;
        this.shipToAccount = null;
        this.shipToAddress = null;
        this.suspendPORules = false;
        this.routingMethodOverride = null;

        // Variances are reset on customer change.  There's too many edge cases not to.
        this.forceCashSale = false;
        this._forceSalesTax = false;
        this._suppressSalesTax = false;
        this._suppressCoreCharges = this.sysParams.coreChargeSuppression && this.sysParams.suppressCoreChargeByDefault;
        this._suppressFees = false;
        this.noHistorySale = false;
        this.nonTaxReasonCode = null;
        this.fleetBillingCCVariance = null;

        // Reset internal customer values
        this.transferRoNum = null;
        this.transferConditionNum = null;
        this.controlNumRequired = false;
        this.controlNum = null;
        this.glNum = null;

        // Reset email options
        this.invoiceEmailsOverride = null;
        this.quoteEmailsOverride = null;
        this.skipEmails = false;
      });

      // Must run mobx action above for ui bridge to be initialized
      this.uiBridge.poInputRef.value = '';

      // Initialize fuel charge where applicable
      if (this.promptForFuel) {
        const shippingCode = this.sysParams.shippingCodes.find(o => o.code === this.activeRoutingMethod);
        if (shippingCode && shippingCode.fuelDefault) {
          this.fuelManuallyEntered = false;
          this.fuelCharge = shippingCode.fuelDefault;
          this.uiBridge.fuelChargeInputRef.value = this.fuelCharge.toFixed(2);

          // Explicit clear could be necessary here with a customer change
        } else {
          this.fuelManuallyEntered = false;
          this.fuelCharge = null;
          this.uiBridge.fuelChargeInputRef.value = '';
        }
      }

      // Re-init various controls (supports active customer switch)
      // RO transfer customer reqs always dominate internal sales
      if (this.isROTransferCustomerLoaded) {
        this.uiBridge.transferRoNumInputRef.value = '';
        this.uiBridge.transferConditionNumInputRef.value = '';
      } else if (this.isLedgerDetailRequired) {
        if (customerParams.internalCustomerGLAccountWithControlRequired !== '') {
          this.uiBridge.glNumInputRef.value = customerParams.internalCustomerGLAccountWithControlRequired;
        } else {
          this.uiBridge.glNumInputRef.value = '';
        }
        this.uiBridge.controlNumInputRef.value = '';
      }
    }

    // Async fetch newly loaded customer's confirmation details and pick lists.
    // There's no reason to wait for these to complete before re-evaling the invoice and unfreezing the UI.
    // We do these in serial to keep the QM license consumption down since all 3 hit QM for data.
    this.loadConfirmationStatusDetail()
      .then(() => this.loadCustomerPickLists())
      .then(() => this.loadCustomerVehicles());

    // Auto-load loyalty customers in 1:1 scenarios, if configured
    if (this.sysParams.loyalty && this.sysParams.loyaltyAutomate) {
      this.autoLoadLoyaltyCustomer();
    }

    // Invoice history is loaded only on demand since it could potentially be very big.

    // Re-eval any existing line items on customer change.
    // Different customers have different pricing levels and tax rates.
    this.reEvalInvoice();
    return true;
  }

  /* This routine loads the non-quote pick lists list for display/selection on Load Pick List.

     Unlike quotes, this is side-loaded async after loading customer params. There's a historical reason for this.
     Originally, only quote data was linked to the customer and provided with the customer params data.
     Over time, customers requested the same linkage with standard pick lists so that a clerk can quickly see all
     quotes and pick lists linked to a loaded customer.  Rather than include the data in the customerParams alongside
     the quote data, Phil created a dedicated call.
  */
  async loadCustomerPickLists(): Promise<void> {
    this.customerPickListsLazyLoaded = false;

    let res: IApiResponse;
    try {
      const id = this.activeCustomerID;

      res = await Api.post('/shopkeeper/getpicklists', { id, type: 'customer' });
      if (res.status !== 200) {
        throw new Error('200 not received from getpicklists');
      }

      if (this.logging) {
        console.warn('/getpicklists Response');
        console.log(res.data);
      }

      // If the active id value hasn't changed during this lazy load
      const activeId = this.activeCustomerID;
      if (id === activeId) {
        this.customerPickLists = [];
        const pickLists = res.data.pickLists;

        if (pickLists.trim() !== '') {
          const lines = pickLists.split('\n');
          for (const line of lines) {
            if (line.trim() === '') {
              continue;
            }
            const data = line.split('ý');
            this.customerPickLists.push({
              id: data[0],
              createdAt: data[1] || '',
            });
          }
        }

        this.customerPickListsLazyLoaded = true;
      }
    } catch (err: any) {
      console.error(err.message);
      // Failed loading/parsing customer pick lists, is not fatal to invoicing session
      return;
    }
  }

  /* Customer quotes are loaded with the customerParams data, but need to be manually refreshed
     after creating a new quote in order to see the new quote in the list of quotes available to load
     (without having to reload the customer).

     For historical reasons, this data is not provided in a standalone QM call, but rather it is included
     in the customerParams data.  So we'll simply refresh that piece of the customer params data.
  */
  async loadCustomerQuotes(): Promise<void> {
    if (this.activeCustomerID === null) {
      return;
    }

    let res: IApiResponse;
    try {
      const id = this.activeCustomerID;

      res = await Api.post('/shopkeeper/customerparams', { id });
      if (res.status !== 200) {
        throw new Error('200 not received from customerparams');
      }

      if (this.logging) {
        console.warn(`/customerparams Response (Quotes Refresh) (ID:${id}):`);
        console.log(res.data);
      }

      const customerParams: ICustomerParams = res.data.params;

      // If the active id value hasn't changed during this lazy load
      const activeId = this.activeCustomerID;
      if (id === activeId) {
        // Refresh the quotes data
        this.customerParams.quotes = customerParams.quotes;
      }
    } catch (err: any) {
      console.error(err.message);
      // Not fatal to invoicing session
    }
  }

  // Customer vehicles are side-loaded async after loading customer params.
  async loadCustomerVehicles(): Promise<void> {
    this.customerVehiclesLazyLoaded = false;

    let res: IApiResponse;
    try {
      const id = this.activeCustomerID;

      res = await Api.post('/shopkeeper/getvehicles', { id });
      if (res.status !== 200) {
        throw new Error('200 not received from getvehicles');
      }

      if (this.logging) {
        console.warn('/getvehicles Response');
        console.log(res.data);
      }

      // If the active id value hasn't changed during this lazy load
      const activeId = this.activeCustomerID;
      if (id === activeId) {
        this.customerVehicles = [];

        // JSON response is autoparsed here by axios
        if (Array.isArray(res.data.vehicles)) {
          this.customerVehicles = res.data.vehicles;
        }

        this.customerVehiclesLazyLoaded = true;
      }
    } catch (err: any) {
      console.error(err.message);
      // Failed loading/parsing customer vehicles, is not fatal to invoicing session
      return;
    }
  }

  loadLoyaltyCustomer(loyaltyCustomer: { externalCustomerId: number | string } & ILoyaltyCustomerDetails) {
    this.loyaltyCustomerDetails = {
      address1: loyaltyCustomer.address1 || '',
      address2: loyaltyCustomer.address2 || '',
      city: loyaltyCustomer.city || '',
      country: loyaltyCustomer.country || '',
      firstName: loyaltyCustomer.firstName || '',
      lastName: loyaltyCustomer.lastName || '',
      company: loyaltyCustomer.company || '',
      emailAddress: loyaltyCustomer.emailAddress || '',
      phoneNumber: loyaltyCustomer.phoneNumber || '',
      preferredDealer: loyaltyCustomer.preferredDealer || '',
      state: loyaltyCustomer.state || '',
      zipCode: loyaltyCustomer.zipCode || '',
    };

    this.activeLoyaltyCustomerID = null; // re-render trigger
    this.activeLoyaltyCustomerID = loyaltyCustomer.externalCustomerId.toString(); // force string

    clearInterval(this.loyaltyOfferBackgroundCheckIntervalID); // if already running
    this.loyaltyOfferBackgroundCheckIntervalID = window.setInterval(() => {
      // window used for proper TS typings
      this.loyaltyOfferBackgroundCheck();
    }, LOYALTY_BACKGROUND_INTERVAL_CHECK_SECS * 1000);

    this.loyaltyOfferBackgroundCheck(); // do first check immediately
  }

  // This differs enough from loadSavedList to necessitate its own routine, though the two are similar.
  // Inspect model.activeRepairOrder for debugging
  async loadRepairOrder({
    roNumber,
    refreshActiveROData = false,
    includeAttachments = false,
    retainIssueQueue = 'NONE',
  }: {
    roNumber: string;
    refreshActiveROData?: boolean;
    includeAttachments?: boolean;
    retainIssueQueue?: 'NONE' | 'ALLOPS' | 'ALLNONACTIVEOPS';
  }) {
    roNumber = roNumber.toUpperCase();

    if (!refreshActiveROData && roNumber === this.activeRONumber) {
      return false;
    }

    if (this.modelLocked) {
      toastr.error('Wait', 'Fetch action in process. Please try again in a moment.');
      return false;
    }

    runInAction(() => {
      this.modelLocked = true;
      this.roLoadInProcess = true;
      this.customerLoadInProcess = true;
    });

    const releaseLocks = () => {
      runInAction(() => {
        this.modelLocked = false;
        this.roLoadInProcess = false;
        this.customerLoadInProcess = false;
      });
    };

    let res: IApiResponse;

    try {
      res = await Api.post('/shopkeeper/loadrepairorder', { ppc: this.ppcParams.ppc, roNumber });
      this.lastSavedRepairOrderRes = res;

      if (res.status !== 200) {
        throw new Error('200 not received from loadRepairOrder');
      }
    } catch (err: any) {
      releaseLocks();
      console.error(err.message);
      toastr.error('Error', `Unable to load RO: ${roNumber}`);
      return false;
    }

    if (this.logging) {
      console.warn('/loadrepairorder Response:');
      console.log(this.lastSavedRepairOrderRes);
    }

    const dbpipRows = res.data.dbpip.split('\n');

    // RO Closed, RO Locked otherwise, etc.
    if (_.get(dbpipRows, 12, '') !== '') {
      const errors = dbpipRows[12].split('ý');
      console.log('Load Repair Order Errors:');
      console.log(errors);
      toastr.error('Error', errors[0]);
      releaseLocks();
      return false;
    }

    // See if this was a RO-linked pick list entered via -R0123 syntax in RO num field, load the linked RO instead
    const returnedRoNumber = _.get(dbpipRows, 4, '');
    if (!!returnedRoNumber && returnedRoNumber !== roNumber) {
      if (this.logging) {
        console.warn(`RO Number Input (${roNumber}) returned RO Number (${returnedRoNumber}) on RO load.`);
      }

      if (this.runMode === 'SRE2') {
        const prefix = returnedRoNumber.substr(0, 3).toUpperCase();
        if (prefix !== 'EST') {
          toastr.error('Error', 'Only pick lists linked to estimates are allowed.');
          releaseLocks();
          return false;
        }
      }

      roNumber = returnedRoNumber;
      this.uiBridge.roNumberInputRef.value = roNumber;
    }

    const { extractedLineItems } = await this.extractDbpipMultiPartTrusted(dbpipRows, false, true); // tslint:disable-line

    // TODO: Possibly need to extract inferred supercedences on non-fleet billled.
    // TODO: Possibly need to consider NOCORECHARGE/NOFEE.

    const operationDetails: IRepairOrderDetails['operationDetails'] = []; // keyed on operation #
    const unitData: IRepairOrderDetails['unitData'] = [];

    // seed roDetails
    const roDetails: IRepairOrderDetails = {
      customerNum: '',
      customerPo: '',
      vin: '',
      unit: '',
      operationDetails,
      unitData,
      decisiv: false,
      confirmationsReady: false,
    };

    // CUSTOMER ASSOCIATED WITH RO
    roDetails.customerNum = _.get(dbpipRows, 3, '');
    roDetails.customerPo = _.get(dbpipRows, 8, '');

    // VARIOUS FLAGS
    roDetails.confirmationsReady = _.get(dbpipRows, 86, 'N') === 'Y';
    roDetails.decisiv = _.get(dbpipRows, 87, 'N') === 'Y';

    // VIN/UNIT
    {
      const [vin, unit] = _.get(dbpipRows, 80, '').split('ý');
      roDetails.vin = vin || '';
      roDetails.unit = unit || '';
    }

    // Operations Data: LABOR TYPES / PRICE CODES / LOCK/VOID FLAGS
    // This must go before CONDITION TEXT extraction to properly configure operationDetails
    if (_.get(dbpipRows, 81, '') !== '') {
      const operationsData = dbpipRows[81].split('ý');
      const isDecisivOperationData: string[] = _.get(dbpipRows, 88, '').split('ý');

      for (let i = 0; i < operationsData.length; i++) {
        const operationNumber = i + 1;
        const data = operationsData[i].split('-');

        // Check for bad data
        if (data.length !== 7) {
          const error = `RO:${roNumber} Operation:${operationNumber} has invalid operation detail data (DBPIP line 82)`;
          console.error(error);
        }

        operationDetails[operationNumber] = {
          conditionDescLines: [], // backfilled below
          isDecisivOperation: (isDecisivOperationData[i] || 'N') === 'Y',
          laborTypeDescription: '', // backfilled below
          laborType: data[0] || '',
          priceCode: data[1] || '',
          secondaryLaborType: data[2] || '',
          secondaryPriceCode: data[3] || '',
          secondaryLaborTypeDescription: '', // backfilled below
          locked: data[4] === 'LOCKED',
          voided: data[4] === 'VOID',
          warranty: data[5] === 'Y',
          stdpForceSuppression: data[6] === 'Y',
        };
      }
    } else {
      toastr.error('Error', `RO:${roNumber} Operation Data Missing!`);
      releaseLocks();
      return false;
    }

    // Operations Data: CONDITION TEXT - non fatal if missing
    if (_.get(dbpipRows, 82, '') !== '') {
      const conditionsData = dbpipRows[82].split('ý');
      for (let i = 0; i < conditionsData.length; i++) {
        const operationNumber = i + 1;
        if (operationDetails[operationNumber]) {
          operationDetails[operationNumber].conditionDescLines = conditionsData[i].split('\\n');
        }
      }
    } else {
      console.error(`RO:${roNumber} Condition Information Missing!`);
    }

    // UNIT DETAILS (varies by dealership and/or vehicle type)
    if (_.get(dbpipRows, 83, '') !== '') {
      const fieldsData = dbpipRows[83].split('ý');
      for (const fieldData of fieldsData) {
        const [field, value] = fieldData.split(':');
        unitData.push({ field, value });
      }
    }

    // LABOR TYPE DESCRIPTIONS - non fatal if missing
    if (_.get(dbpipRows, 85, '') !== '') {
      const laborTypeDescs = dbpipRows[85].split('ý');
      for (let i = 0; i < laborTypeDescs.length; i++) {
        const operationNumber = i + 1;
        if (operationDetails[operationNumber]) {
          const data = laborTypeDescs[i].split('ü');
          operationDetails[operationNumber].laborTypeDescription = data[0] || '';
          operationDetails[operationNumber].secondaryLaborTypeDescription = data[1] || '';
        }
      }
    }

    // LOAD ASSOCIATED CUSTOMER INTO MODEL (simpler load than loadCustomerParams())
    let customerID: string | null = null;
    let customerParams: ICustomerParams;

    if (roDetails.customerNum !== '') {
      const id = roDetails.customerNum;
      try {
        res = await Api.post('/shopkeeper/customerparams', { id });
        if (res.status !== 200) {
          throw new Error('200 not received from customerparams');
        }
      } catch (err: any) {
        console.error(err.message);
        toastr.error('Error', `Unable to load customer: ${id}`);
        releaseLocks();
        return false;
      }

      if (this.logging) {
        console.warn(`/customerparams Response (ID:${id}):`);
        console.log(res.data);
      }

      customerParams = res.data.params;

      if (customerParams.name1 === 'Customer not found') {
        toastr.error('Error', `Associated Customer ID not found: ${id}`);
        releaseLocks();
        return false;
      }

      // INACTIVE customer accounts probably ok?

      // this.customerParams is not directly observable, this.activeCustomerID is
      this.customerParams = customerParams;
      this.customerParams.isCodAccount = !!~_.findIndex(
        this.sysParams.codAccounts,
        o => o.codAccount === id.toUpperCase()
      );
      customerID = id;
    } else {
      toastr.error('Warning', 'No customer attached to RO.');
    }

    /* Here we set aside queued items to add back post issue/return depending on the situation after refresh
       Item Return: retain all queued items
       Manual RO Refresh: retain all queued items
       Standard Issue: retain nothing / full refresh
       Decisiv Issue: retain all but non-active operation items (the active op was just issued)
    */
    let retainedIssueQueueItems: ILineItem[] = [];

    // retainIssueQueue of 'NONE' does precisely that, retains no queued items (DSI standard issue)
    if (refreshActiveROData && ['ALLOPS', 'ALLNONACTIVEOPS'].includes(retainIssueQueue)) {
      retainedIssueQueueItems = this.lineItems.filter(o => {
        let retainItem = false;

        // Start with items queued on all ops
        if (!o.issuedToRO) {
          retainItem = true;
        }

        // Exclude active op items if necessary (Decisiv issue)
        if (retainIssueQueue === 'ALLNONACTIVEOPS' && o.roDetail?.operationNumber === this.activeOperation) {
          retainItem = false;
        }

        // Ensure the queued item's operation still exists on the re-load (edge case)
        if (!roDetails.operationDetails[o.roDetail?.operationNumber || -1]) {
          console.error(
            `Operation ${o.roDetail?.operationNumber} no longer exists on RO: ${roNumber}.  Queued item ${o.partNum} removed.`
          );
          retainItem = false;
        }

        return retainItem;
      });
    }

    runInAction(() => {
      if (!refreshActiveROData) {
        // Clear loyalty stuff in the event a fresh RO number is entered
        clearInterval(this.loyaltyOfferBackgroundCheckIntervalID); // inert in many cases
        this.lastROLoyaltyCustomerID = null;
        this.activeLoyaltyCustomerID = null;
        this.loyaltyInvoiceNumber = null;

        this.confirmationPartsAddedToInvoice.clear();
      }

      this.activeCustomerID = customerID;
      this.po = roDetails.customerPo;
      this.suspendPORules = false;

      this.activeRONumber = roNumber;
      this.activeRepairOrderDetails = roDetails;
      this.activeOperation =
        refreshActiveROData && roDetails.operationDetails.length >= this.activeOperation ? this.activeOperation : 0;
      this.activeDecisivCase = roDetails.decisiv;
      this.lineItems.clear();
      this.lineItems.push(...extractedLineItems);

      if (refreshActiveROData) {
        this.lineItems.push(...retainedIssueQueueItems);
      }
    });

    // Vic says PRO1 doesn't need taxes

    releaseLocks();

    // Load any SMM1/SRE1/SUF2 attachments
    if (includeAttachments) {
      this.loadServiceAttachments();
    }

    // Async fetch newly loaded RO's confirmation details.
    this.loadConfirmationStatusDetail().then(() => this.loadROPickLists());

    // Loyalty customers must be assigned during RO open (for now, at least) to prevent different loyalty customer IDs
    // being loaded between various part issues while working with the RO.  We don't need this on SRE2/SMM3.
    if (this.runMode === 'PRO1' && !this.isEstimate && !refreshActiveROData) {
      this.lastROLoyaltyCustomerID = _.get(dbpipRows, 89, '').trim() || null;
      if (this.lastROLoyaltyCustomerID) {
        this.autoLoadLoyaltyCustomer();
      }
    }

    return true;
  }

  // This routine loads the pick lists list for display on Load Pick List
  async loadROPickLists(): Promise<void> {
    this.roPickListsLazyLoaded = false;

    let res: IApiResponse;
    try {
      const id = this.activeRONumber;

      res = await Api.post('/shopkeeper/getpicklists', { id, type: 'ro' });
      if (res.status !== 200) {
        throw new Error('200 not received from getpicklists');
      }

      if (this.logging) {
        console.warn('/getpicklists Response');
        console.log(res.data);
      }

      // If the active id value hasn't changed during this lazy load
      const activeId = this.activeRONumber;
      if (id === activeId) {
        this.roPickLists = [];
        const pickLists = res.data.pickLists;

        if (pickLists.trim() !== '') {
          const lines = pickLists.split('\n');
          for (const line of lines) {
            if (line.trim() === '') {
              continue;
            }
            const data = line.split('ý');
            this.roPickLists.push({
              id: data[0],
              createdAt: data[1] || '',
            });
          }
        }

        this.roPickListsLazyLoaded = true;
      }
    } catch (err: any) {
      console.error(err.message);
      // Failed loading/parsing ro pick lists, is not fatal to service session
      return;
    }
  }

  // Used for loading quotes, pick lists, bill of materials
  async loadSavedList(listID: string, listType: SavedListType): Promise<boolean> {
    if (this.modelLocked) {
      toastr.error('Wait', 'Fetch action in process. Please try again in a moment.');
      return false;
    }

    if (listType === 'Quote' && listID.toUpperCase().startsWith('QOT') && !this.activeCustomerID) {
      toastr.error('Error', 'Long term quotes require customer to be loaded first.');
      return false;
    }

    const isRO = ['PRO1', 'SMM3', 'SRE2'].includes(this.runMode);
    const isPSO3 = this.runMode === 'PSO3';
    const isPIN1 = this.runMode === 'PIN1';
    const isPIN4 = this.runMode === 'PIN4';

    this.modelLocked = true;

    let res: IApiResponse;

    try {
      this.lastSavedListReq = {
        listID,
        listType,
        ppc: this.ppcValidated ? this.ppcParams.ppc : '',

        // Front Counter
        customerID: this.activeCustomerID || '',

        // Back Counter (potentially could view the picklist without loading the RO first)
        isRO,
        roNum: this.activeRONumber || '',
        activeOperation: this.activeOperation,
      };

      // This method was added 9/14/22 to give us more information about the BOM
      if (listType === 'Bill of Materials' && isPIN4) {
        this.lastSavedListReq['useNewBOM'] = true;
      }

      if (this.logging) {
        console.warn('/loadsavedlist Request:');
        console.log(this.lastSavedListReq);
      }

      res = await Api.post('/shopkeeper/loadsavedlist', this.lastSavedListReq);
      this.lastSavedListRes = res;

      if (res.status !== 200) {
        throw new Error('200 not received from loadSavedList');
      }
    } catch (err: any) {
      this.modelLocked = false;
      console.error(err.message);
      toastr.error('Error', `Unable to load list: ${listID}`);
      return false;
    }

    if (this.logging) {
      console.warn('/loadsavedlist Response:');
      console.log(this.lastSavedListRes);
    }

    const dbpipRows = res.data.dbpip.split('\n');

    // attachments & dbpipHelper data are automatically attached to lineItems by the extract method here,
    // but supercedences which still need to be inferred below will need to reference the attachments/dbpipHelper data again
    let { attachments, dbpipHelper, extractedLineItems } = await this.extractDbpipMultiPartTrusted(dbpipRows);

    // a "good" response is indicated by at least 1 valid part line
    // the absence of an associated customer on a short term pick list IS allowed, per Vic

    if (extractedLineItems.length) {
      if (listType === 'Bill of Materials') {
        if (isPIN4) {
          // This may or may not exist, I'm not sure if it's useful for much other than the PIN6 report
          // QM refers to these as ID and Title.  PIN6 uses Name and Description.
          this.lastBOMLoad = {
            listID,
            listTitle: _.get(dbpipRows[15].split('ý'), 2, ''),
          };
        } else {
          // Inject BOM header, per Vic 6/10/22, not needed on PIN4
          extractedLineItems.unshift({
            comment: `BOM: ${listID}`,
            fetched: true,
            quantity: 0,
            partNum: 'LINEITEM_COMMENT',
          });
        }
      }

      if (isPIN1 && ['Pick List', 'Quote'].includes(listType)) {
        // Clear any possible previously loaded OPC params first before loading a new pick list or quote.
        // These values are otherwise cleared on a model reset.
        // For now I'll allow these to cross customer changes in case the clerk needs to load a ship to account.
        this.OPCdata = null;
        this.OPCtag = null;
        this.lockoutChanges = false;

        // This is saved so the pick list / quote can be cleared on invoice process
        // Pick lists can be loaded in Blaze without the leading 0s.  We need them here for it to work.
        // Quotes always start with QOTxxx.
        this.lastPIN1PickListID = listType === 'Pick List' ? _.padStart(listID, 4, '0') : listID;

        if (listType === 'Pick List') {
          // Is this an OPC generated pick ticket?
          const x = extractedLineItems;
          if (x[0].partNum === 'LINEITEM_COMMENT' && x[0].comment!.includes('PARTS REQUEST FOR REFERENCE')) {
            this.OPCtag = `${_.get(x[0].comment!.match(/^(.*) PARTS REQUEST FOR REFERENCE/), 1, '')} - ${listID}`;

            // Set the line item to the side so the clerk can't remove or edit the required 1st line OPC memo.
            // We'll inject it back when processing the invoice.
            this.OPCdata = extractedLineItems.shift() as ILineItem;
            this.lockoutChanges = this.sysParams.lockoutOPC;
          }
        }
      }

      // PRO1 / SMM3
      if (isRO) {
        // Assign preissue details to the extracted pick list items
        extractedLineItems = extractedLineItems.map(o => ({ ...o, ...this.getPreissueDetails() }));
        this.lineItems.push(...extractedLineItems);

        // PIN1 / PIN4 / PSO3
      } else {
        // do a partial model reset for pick lists and quotes (keep PPC loaded)
        // bill of materials will simply tack on the part list to the existing invoice
        // PIN4/PSO3 will behave like loading a pick list on a RO, cumulatively
        if (_.includes(['Pick List', 'Quote'], listType) && !isPIN4 && !isPSO3) {
          this.uiBridge.customerLookupRef.clear();
          this.lineItems.clear();

          this.activeCustomerID = null;
          this.loyaltyInvoiceNumber = null;
          this.shipToAccount = null;
          this.shipToAddress = null;
          this.shipToEmail = false;
          this.freight = this.sysParams.setFreightToZero ? 0.0 : null;
          this.freightPromptDisplayed = false;
          this.fuelCharge = null;
          this.fuelManuallyEntered = false;
          this.fuelPromptDisplayed = false;
          this.po = null;

          this.lineItemDragInProcess = false;

          this.salesTax = 0.0;
          this.gstTax = 0.0;
          this.updateTaxPromiseIDs.clear();

          // hydrate various controls, if utilized on this pick ticket

          const customerID = _.get(dbpipRows, 3, '');
          const shipToAccountRaw = _.get(dbpipRows, 6, '');
          const shipToAddressRaw = _.get(dbpipRows, 7, '');
          const shipToAccountFound = shipToAccountRaw !== '' && shipToAccountRaw !== '0';
          const shipToAddressFound = shipToAddressRaw !== '';

          if (customerID !== '' && customerID !== 'Invalid Customer') {
            /* The "default" COD accounts automatically ask for cash sale information on load.
               Some clients link pick lists and quotes directly to default COD accounts rather than
               automatically creating new customer accounts.  In the event ship to information is
               specified when a quote/pick list is saved directly to a default COD account, there's
               no reason to pop the cash sale information on quote/pick list load.
            */
            const reqCashSaleInfoOnCODAccountLoad = !(shipToAccountFound || shipToAddressFound);

            // loadCustomer requires an unlocked model
            // it will briefly lock the model via reEvalInvoice, but at this point, the invoice is empty
            this.modelLocked = false;
            await this.uiBridge.customerLookupRef.loadCustomer(customerID, reqCashSaleInfoOnCODAccountLoad);

            /* loadCustomer will assign default fuel charges based on the customer's route type for
               systems that enable the feature, but we may override it below via the routing method variance
               or via a previously saved fuel charge line item on the list/quote (detected as a DEL line).
            */
          }

          const po = _.get(dbpipRows, 8, '');
          if (po !== '') {
            this.suspendPORules = true;
            this.po = po;
            this.uiBridge.poInputRef.value = po;
          }

          const freightRaw = _.get(dbpipRows, 30, '');
          if (freightRaw !== '') {
            this.freight = parseFloat(freightRaw) || 0.0;
          }

          // Supports clients who default freight to 0.00, as well as freight stored with lists
          if (this.freight !== null) {
            this.uiBridge.freightInputRef.value = this.freight.toFixed(2);
          }

          // see shopkeeper.controller.js for various positional flag descriptions
          const varianceFlags = _.get(dbpipRows, 5, '').trim();

          // routing method
          if (varianceFlags[1]) {
            // verify this routing method still exists before hydrating the reactive routing control
            if (~_.findIndex(this.sysParams.shippingCodes, o => o.code === varianceFlags[1])) {
              this.routingMethodOverride = varianceFlags[1];
              // taxes will be calculated below after part lines are extracted,
              // taking this routing method into account
            }
          }

          if (varianceFlags[4] === 'H') {
            this.noHistorySale = true;
          }

          // force cash sale / force sales tax / suppress sales tax
          switch (varianceFlags[2]) {
            case 'B':
              this.forceCashSale = true;
              this._forceSalesTax = true;
              break;
            case 'C':
              this.forceCashSale = true;
              break;
            case 'N':
              this._suppressSalesTax = true;
              break;
            case 'T':
              this._forceSalesTax = true;
              break;
            case 'X':
              this.forceCashSale = true;
              this._suppressSalesTax = true;
              break;
            default:
              // no-op
              break;
          }

          // global core/fee suppression
          switch (varianceFlags[3]) {
            case 'B':
              this._suppressCoreCharges = true;
              this._suppressFees = true;
              break;
            case 'F':
              this._suppressFees = true;
              break;
            case 'N':
              this._suppressCoreCharges = true;
              break;
            default:
              // no-op
              break;
          }

          // if suppress tax is used and this client collects a reason for reporting purposes
          const nonTaxReasonCode = _.get(dbpipRows, 32, '');
          if (nonTaxReasonCode !== '') {
            this.nonTaxReasonCode = nonTaxReasonCode;
          }

          // If either ship to method is utilized, taxes will be calculated below
          // taking the routing method and override zip code into account
          if (shipToAccountFound) {
            const shipToAccount = shipToAccountRaw.split('ý');
            this.shipToAccount = {
              customerID: shipToAccount[0],
              name1: 'SHIPTO ACCOUNT:',
              name2: shipToAccount[0],
              name3: '',
              address: '',
              city: '',
              email: '',
              phone: '',
              state: '',
              zipcode: '',
            } as { customerID: string } & ICustomerParams; // cast to intersection to hush TS

            // Backfill ship to account display details from ES if available.
            // If ES hit not found, the display UI will show the ship to account number only.
            let resEs;
            try {
              resEs = await Api.post('/shopkeeper/esshiptodetail', { id: shipToAccount[0] });
              if (resEs.status !== 200) {
                throw new Error('200 not received from esshiptodetail');
              }
              if (Array.isArray(resEs.data) && resEs.data.length >= 1) {
                // This should be a result set of 1 on match, but just in case ...
                const shipToDetails = _.find(resEs.data, o => o.itemid === shipToAccount[0]);
                if (shipToDetails) {
                  // Phone numbers seem to be stored in ES directly as NPANXXXXXX,
                  // we want (NPA) NXX-XXXX
                  let phone = (shipToDetails.Phone || '').match(/^(\d{3})(\d{3})(\d{4})$/);
                  phone = phone ? `(${phone[1]}) ${phone[2]}-${phone[3]}` : shipToDetails.Phone || '';

                  this.shipToAccount = {
                    customerID: shipToAccount[0],
                    name1: shipToDetails.Name || '',
                    name2: '', // Unavailable via ES - ok on list loads for now for speed
                    name3: '', // Unavailable via ES - ok on list loads for now for speed
                    address: shipToDetails.Address || '',
                    city: shipToDetails.City || '',
                    email: '',
                    phone,
                    state: shipToDetails.State || '',
                    zipcode: shipToDetails.Zip || '',
                  } as { customerID: string } & ICustomerParams; // cast to intersection to hush TS
                }
              }
            } catch (err: any) {
              // This is not a fatal operation since we're only backfilling display details.
              console.error(err.message);
            }

            if (shipToAccount[1] === 'Y') {
              // email ship to flag
              this.shipToEmail = true;
            }
          } else if (shipToAddressFound) {
            const shipToAddress = shipToAddressRaw.split('ý');
            this.shipToAddress = {
              name: shipToAddress[0] || '',
              address: shipToAddress[1] || '',
              city: shipToAddress[2] || '',
              phone: shipToAddress[3] || '',
              zipCode: shipToAddress[6] || shipToAddress[8] || '',
              state: shipToAddress[7],
            };
          }

          // Check if fuel was previously added to this pick list / quote and replay it.  If not, it's possible
          // a route variance we replayed which will need its default fuel charge set.  We accept the first match.
          if (this.promptForFuel) {
            let fuelChargeIndex = -1; // -1 = not found

            for (let i = 0; i < extractedLineItems.length; i++) {
              if (
                extractedLineItems[i].defaultPartNum &&
                extractedLineItems[i].defaultPartNum === this.sysParams.fuelChargeDefaultPartName
              ) {
                fuelChargeIndex = i;
                break;
              }
            }

            // If a fuel charge was found in the line items, extract it and apply it to the UI control.
            if (fuelChargeIndex !== -1) {
              this.fuelManuallyEntered = true;
              this.fuelCharge = extractedLineItems[fuelChargeIndex].defaultPartTradePrice!;
              this.uiBridge.fuelChargeInputRef.value = this.fuelCharge.toFixed(2);

              extractedLineItems.splice(fuelChargeIndex, 1);

              // Otherwise fallback to a possible route variance application
            } else {
              const shippingCode = this.sysParams.shippingCodes.find(o => o.code === this.activeRoutingMethod);
              if (shippingCode && shippingCode.fuelDefault) {
                this.fuelManuallyEntered = false;
                this.fuelCharge = shippingCode.fuelDefault!;
                this.uiBridge.fuelChargeInputRef.value = this.fuelCharge.toFixed(2);
              } else {
                this.fuelManuallyEntered = false;
                this.fuelCharge = null;
                this.uiBridge.fuelChargeInputRef.value = '';
              }
            }
          }
        }

        /* Fleet billed customers are good to go at this point (supercedence parts remain decomposed)
         Non-fleet billed customers require supercedence chain inference / extraction and "base" part
         supercedence comments retrieval from DBPIPHELPER to give a fully replayed experience.
         We need the customer loaded first to determine the fleet billing status.

         NASA was unable to logically key multiple occurrences of the same supercedence chain because
         their code is "too hard to follow" (sigh).  So, we infer multiple occurrences on our side by
         assuming duplicate base->superceded chains must imply duplicate entries on systems not set to
         combine like line items.
        */
        if (!(this.activeCustomerID && this.customerParams.fleetBilled)) {
          const preSupercedenceInference = _.cloneDeep(extractedLineItems);
          try {
            extractedLineItems = this.extractInferredSupercedences(extractedLineItems, dbpipHelper, attachments);
          } catch (e) {
            // Inference failed, revert to fleet billed experience (decomposed part lines)
            console.error('Supercedence inference extraction failed.  Reverting to decomposed part lines.');
            extractedLineItems = preSupercedenceInference;
          }
        }

        /* 1. Scan for any per line item core/fee suppressions, which QM sends as part line comments.
              In the event NOCORECHARGE or NOFEE is found, enable the suppression and remove the comment.
              This is an unfortunate way for QM to send these flags back to Blaze, but it's how it works.

           2. If a managerial override was determined, apply it to any *forced parts.  In all but very rare
              cases, any site using managerial overrides will apply the same override to every *forced part.
              In the rare event multiple managers were involved in the original force overrides, we'll use
              the first encountered MPCC. It's pretty unlikely more than one manager override would be involved.
              QM squashes the relations of manager overrides on list save and loses the MPPC<->LineItem mapping,
              so there's no way to play it back deterministically.  This will satisfy the 99.99% case.

              If any line is forced with a managerial override, QM logs it for the entire invoice/list on line
              3 of DBPIP as PPC-MPPC. On invoice process, we only include unique MPPC encounters. So on quote playback
              (where all lines are forced) if originally a MPPC only applied to one line, the MPPC would apply
              to all *part lines here since all lines are forced on a quote.  The end result after invoicing the
              quote would still be PPC-MPPC on line 3 since dupes are removed, though, so this strategy works.
        */
        const managerOverridePPC = _.get(dbpipRows, 2, '').split('-')[1];

        for (const lineItem of extractedLineItems) {
          // Both 1 and 2 above only apply to non-supercedence "standard" part lines
          // PIN4/PSO3 does not drop cores/fees, but we still need to clean these from the comments if they exist

          // 1
          if (lineItem.detail) {
            const cleanedComments: string[] = [];
            lineItem.detail.comments.forEach(comment => {
              if (/NOCORECHARGE|NOFEE/.test(comment)) {
                if (comment.includes('NOCORECHARGE') && !isPIN4 && !isPSO3) {
                  lineItem.suppressCoreCharge = true;
                }
                if (comment.includes('NOFEE') && !isPIN4 && !isPSO3) {
                  lineItem.suppressFee = true;
                }
              } else {
                cleanedComments.push(comment);
              }
            });
            lineItem.detail.comments = cleanedComments;
          }

          // 2
          if (lineItem.forcedPricing && managerOverridePPC) {
            lineItem.forcedManagerOverridePPC = managerOverridePPC;
          }
        }

        /* Push the lineItems into the active model (no async eval necessary - so no model lock necessary).
         Fully extracted items pushed onto the linesItems observable this way will have more MobX wrappers
         on properties than items added manually, but that shouldn't cause any deleterious effect.
         Sidenote: this could be achieved in mobx like this as well:
         this.lineItems.replace(this.lineItems.slice().concat(extractedLineItems))
        */
        this.lineItems.push(...extractedLineItems);

        // Give sale attribution to the quote / pick list creator (if feature enabled on PTB2) (PIN1 only)
        if (
          this.runMode === 'PIN1' &&
          ((listType === 'Quote' && this.sysParams.quoteReplayPPC) ||
            (listType === 'Pick List' && this.sysParams.pickListReplayPPC))
        ) {
          const creatorPPC = _.get(dbpipRows, 2, '').trim().split('-')[0];

          if (!!creatorPPC && creatorPPC.toUpperCase() !== this.ppcParams.ppc.toUpperCase()) {
            // Verify the quoteCreatorPPC is still a legit PPC
            try {
              const ppcAuthRes = await Api.post('/shopkeeper/ppcauth', { ppc: creatorPPC });

              if (ppcAuthRes.status !== 200) {
                throw new Error('200 not received from ppcauth');
              }

              if (this.logging) {
                console.warn(`/ppcauth Request (PPC:${creatorPPC}):`);
                console.log(ppcAuthRes.data);
              }

              if (ppcAuthRes.data.authenticated) {
                // Note the currently active PPC's permission set and force part password remains the same
                this.ppcAttribution = creatorPPC;
                this.ppcAttributionReplayActive = true;
              }
            } catch (err: any) {
              console.error(err.message);
              console.error(`Unable to validate QuotePPC: ${creatorPPC}`);
            }
          } else {
            /* It's possible the clerk first loads a list where an attribution is applied, then realizes
               they loaded the wrong list, and the next list doesn't need additional attribution if it's their PPC.
            */
            this.ppcAttribution = null;
            this.ppcAttributionReplayActive = false;
          }
        }
      }

      this.updateTaxes();

      // If no parts are in the list, check for error text
      // (Quote Expired, etc.) before crafting generic error.
    } else {
      let errors = [`Invalid ${listType} ID`];

      // Quotes have custom errors such as expiration messages, quote not found, etc..
      if (listType === 'Quote') {
        const errorsOverride = _.get(dbpipRows, 12, '');
        if (errorsOverride !== '') {
          errors = errorsOverride.split('ý');
        }
      }

      errors.forEach(error => {
        toastr.error('Error', error);
      });
    }

    this.modelLocked = false;
    return true;
  }

  async loadPPC(ppc: string, skipPasswordCheck = false) {
    ppc = ppc.trim();
    if (ppc === '') {
      return false;
    }

    let res: IApiResponse;

    try {
      res = await Api.post('/shopkeeper/ppcauth', { ppc });
      if (res.status !== 200) {
        throw new Error('200 not received from ppcauth');
      }
    } catch (err: any) {
      console.error(err.message);
      toastr.error('Error', 'Unable to load PPCAuth response.');
      return false;
    }

    if (this.logging) {
      console.warn(`/ppcauth Request (PPC:${ppc}):`);
      console.log(res.data);
    }

    this.ppcParams = res.data;

    // Some clients set true/false flags on PPCs for force override, but do NOT use force passwords, so pre-validate
    if (this.ppcParams.permissions.forceParts && !this.ppcParams.permissions.hasOwnProperty('forcePartsPassword')) {
      this.ppcForcePasswordValidated = true;
    }

    /* D3/QM API uses "authenticated" to indicate if the PPC exists. It does
       NOT validate the password, which is delivered in a separate field of the response.
       We "validate" the PPC if the user is "authenticated" and no PPC password exists,
       otherwise we display a PPC password input and "validate" the PPC only after a
       matching password is provided.

       Since ppcValidated is a mobx observable, changes will prompt a re-render.
    */

    if (!this.ppcParams.authenticated) {
      toastr.error('Error', `Invalid PPC Provided: ${ppc}`);
      this.ppcPasswordNeeded = false;
      this.ppcValidated = false;
    } else {
      // We have an authenticated PPC here.  Now check password status.
      this.ppcParams.ppc = ppc;
      if (this.ppcParams.password === '' || skipPasswordCheck) {
        // No password required, PPC has been validated.
        this.ppcPasswordNeeded = false;
        this.ppcValidated = true;
        this.autoLoadSeed();
      } else {
        this.ppcPasswordNeeded = true;
        this.ppcValidated = false;
      }
    }

    return this.ppcParams.authenticated;
  }

  async loadSysParams(): Promise<void> {
    let res: IApiResponse;

    try {
      res = await Api.get('/shopkeeper/systemparams');
      if (res.status !== 200) {
        throw new Error('200 not received from systemparams');
      }
    } catch (err: any) {
      console.error(err.message);
      toastr.error('Error', 'Unable to load system params.');
      return;
    }

    this.sysParams = res.data.sysParams;

    if (this.sysParams.systemDivisions.length) {
      // set current division to first in list (per Ben) if divisions exist
      // the active division will change as parts are entered if they have a different division
      [this.activeDivision.code, this.activeDivision.desc] = this.sysParams.systemDivisions[0].split('-');
      this.activeDivision.code = this.activeDivision.code.toUpperCase();

      // the default division does not change - it's used by non-stocking part entries
      this.defaultDivision = this.activeDivision.code;

      // build part division lookup map
      this.sysParams.systemDivisions.forEach(division => {
        const [code, desc] = division.split('-');
        this.partDivisions.set(code.toUpperCase(), desc);
      });
    }

    if (this.sysParams.defaultParts.length) {
      this.sysParams.defaultParts.forEach(defaultPart => {
        const partNum = defaultPart.partNum.toUpperCase();
        const priceSymbolInt = parseInt(defaultPart.priceSymbol, 10);

        // Not specifying a price symbol on an @ line will result in the default trade price symbol applied.
        // Price symbol is ATB3(PSU) * 10 as far as the invoicing program is concerned (per Tom).
        const priceSymbol = isNaN(priceSymbolInt) ? '' : String(priceSymbolInt * 10);

        const stockClass =
          defaultPart.stockClass.startsWith('#') || defaultPart.stockClass.startsWith('*')
            ? defaultPart.stockClass.slice(1)
            : defaultPart.stockClass;

        this.defaultParts.set(partNum, {
          arity: defaultPart.arity,
          defaultNetPrice: defaultPart.defaultNetPrice,
          desc: defaultPart.desc,
          po: defaultPart.po,
          priceSymbol,
          reverseSign: defaultPart.reverseSign,
          stockClass,
          partNum,
        });

        // Add faux-sublets to candidates array (ATB3 default part with PO flag set)
        if (defaultPart.po && !['SUBL', 'SUBP'].includes(partNum)) {
          this.subletCandidates.push(partNum);
        }
      });
    }

    if (this.sysParams.shippingCodes.length) {
      this.sysParams.shippingCodes.forEach(shippingCode => {
        shippingCode.fuelDefault = parseFloat(shippingCode.fuelDefault as any) || undefined;
      });
    }

    // Credit card terminal integration is defined per branch
    this.sysParams.creditCardIntegration = _.get(this.appState, 'app.branch.payment.enabled', false);

    // This will simply be ignored on non-loyalty clients
    this.loyaltyDefaultPart = this.getLoyaltyDefaultPart();

    // build branch title lookup dictionary
    this.branchTitleDict = {};
    this.sysParams.branchDescriptions.forEach(branchDescription => {
      this.branchTitleDict[branchDescription.mvAccount] = branchDescription.name;
    });

    // some clients want freight pre-populated to 0.00
    if (this.sysParams.setFreightToZero) {
      this.freight = 0.0;
    }

    // some clients (CRTS) want the suppress core charge global variance enabled by default
    if (this.sysParams.coreChargeSuppression && this.sysParams.suppressCoreChargeByDefault) {
      this._suppressCoreCharges = true; // bypass setter; no need for tax calculation here
    }

    // Some clients want thumbnails showing by default, others will need to manually enable per session
    this.showAttachmentsThumbnails = this.sysParams.showThumbnailsByDefault;

    // When building bill of materials, we probably don't need additional detail showing
    if (this.runMode === 'PIN4') {
      this.hideAdditionalDetail = true;
    }

    // some small shops run their SMM3 screen in PRO1 mode (typically the same person)
    if (this.runMode === 'SMM3' && this.sysParams.smm3PartAccess) {
      this.runMode = 'PRO1';
    }

    // ensure fuel charge config is correct, if enabled
    if (['PIN1'].includes(this.runMode) && ['Y', 'F'].includes(this.sysParams.promptForFuel)) {
      if (this.sysParams.fuelChargeDefaultPartName === '') {
        this.sysParams.promptForFuel = 'N';
        console.error('Fuel charge default part name is undefined.');
      } else if (!this.defaultParts.has(this.sysParams.fuelChargeDefaultPartName)) {
        this.sysParams.promptForFuel = 'N';
        console.error('Fuel charge default part name does not exist in price symbol table.');
      }
    }

    if (['PRO1', 'SMM3', 'SRE2'].includes(this.runMode)) {
      // PRO1/SMM3 should not combine line items regardless of PTB2 setting (for now, at least)
      this.sysParams.combineDuplicateParts = false;

      // PTB2 now has separate Front Counter / Back Counter settings (May 2021)
      this.sysParams.displayGrossProfitOnInquiry = this.sysParams.displayGrossProfitOnROInquiry;
      this.sysParams.displayNetOnPartsInquiry = this.sysParams.displayNetOnROPartsInquiry;
      this.sysParams.partsInquiryDisplayOptions = this.sysParams.partsROInquiryDisplayOptions;
      this.sysParams.publishedNetForGrossProfit = this.sysParams.publishedNetForROGrossProfit;
    }

    // PIN4/PSO3 has hard defaults for showing cost regardless of PTB2 settings
    if (['PIN4', 'PSO3'].includes(this.runMode)) {
      this.sysParams.displayGrossProfitOnInquiry = false;
      this.sysParams.displayNetOnPartsInquiry = true;
      this.sysParams.partsInquiryDisplayOptions = 'N';
    }

    // We need main UI init before possibly auto-loading the PPC from the Blaze user's profile
    this.initialized = true;

    if (this.sysParams.autoLoadBlazePPC) {
      this.autoLoadBlazePPC();
    }
  }

  // Service screens only
  async loadServiceAttachments() {
    if (!this.activeRONumber) return;

    runInAction(() => {
      this.roAttachments.clear();
      this.unitAttachments.clear();
    });

    const ro = this.activeRONumber;
    const { vin } = this.activeRepairOrderDetails;

    try {
      const res: IApiResponse = await Api.post('/shopkeeper/loadserviceattachments', { ro, vin });

      if (this.logging) {
        console.warn('/loadserviceattachments Response:');
        console.log(res);
      }

      if (res.status !== 200) {
        throw new Error('200 not received from loadserviceattachments.');
      }

      const { roAttachments, unitAttachments } = res.data;

      runInAction(() => {
        if (Array.isArray(roAttachments) && roAttachments.length > 0) {
          this.roAttachments.push(...roAttachments);
        }

        if (Array.isArray(unitAttachments) && unitAttachments.length > 0) {
          this.unitAttachments.push(...unitAttachments);
        }
      });
    } catch (err: any) {
      console.error(err.message);
      // No need to toast attachment errors, non critical.
    }
  }

  get lostSaleCandidateParts() {
    const candidateParts: ILostSaleCandidate[] = [];
    let nextUniqueID = 0;

    for (const lineItem of this.lineItems) {
      if (lineItem.issuedToRO || !lineItem.fetched) {
        continue;
      }

      // Non-fleet billed supercedence case, we care about recording constitutents here
      if (lineItem.supercedenceDetails && lineItem.supercedenceDetailsSupplemental && lineItem.detailSupplemental) {
        // Each "supercedence detail" is a terminating constituent of the supercedence chain. >=1 constituents.
        for (const supercedenceDetail of lineItem.supercedenceDetails) {
          const supercedenceDetailSupplemental = _.get(
            lineItem.supercedenceDetailsSupplemental,
            `${supercedenceDetail.partNum}`
          );

          const partNum = supercedenceDetail.partNum;
          const qtyRequested = supercedenceDetail.quantity; // qty escalations are already applied here
          const confirmationSale = false; // by deduction, the base part of the super would not be from a confirmation
          const uniqueID = nextUniqueID++;

          // Support fallbacks when long descriptions are not present
          // Long description will be used for title hovers
          const description =
            this.sysParams.printLongDescriptionOnInquiry && supercedenceDetail.longDescription
              ? supercedenceDetail.longDescription
              : supercedenceDetail.shortDescription;
          const longDescription = supercedenceDetail.longDescription || supercedenceDetail.shortDescription;

          let onHand: null | number = null;
          // inexhaustible part?  on-hand = qty requested
          if (supercedenceDetail.priceSymbol.endsWith('3')) {
            onHand = qtyRequested;
          } else if (supercedenceDetailSupplemental) {
            onHand = parseInt(_.get(supercedenceDetailSupplemental, 'onHand', ''), 10) || null;
          }

          let qtyInsufficient = false;
          if (onHand !== null) {
            // 1. Line item has 0 or negative qty on hand?
            // 2. Will the requested quantity cause the part stock to go negative?
            // (1 || 2): Style line item with pink background and red/bold on hand value.
            if (onHand <= 0 || onHand < qtyRequested) {
              qtyInsufficient = true;
            }
          }

          candidateParts.push({
            confirmationSale,
            description,
            longDescription,
            onHand,
            partNum,
            qtyInsufficient,
            qtyRequested,
            uniqueID,
          });
        }

        // Standard stocking part
      } else if (lineItem.detail) {
        const partNum = lineItem.partNum;
        const qtyRequested = lineItem.quantity;
        const confirmationSale = Boolean(lineItem.confirmationSale);
        const uniqueID = nextUniqueID++;

        // Support fallbacks when long descriptions are not present
        // Long description will be used for title hovers
        const description =
          this.sysParams.printLongDescriptionOnInquiry && lineItem.detail.longDescription
            ? lineItem.detail.longDescription
            : lineItem.detail.shortDescription;
        const longDescription = lineItem.detail.longDescription || lineItem.detail.shortDescription;

        let onHand: null | number = null;
        // on-hand not displayed on confirmation sales since the part was held for the customer,
        // but we may wanna log the lost sale before voiding the confirmation line item anyways.
        if (!lineItem.confirmationSale) {
          // inexhaustible part?  on-hand = qty requested
          if (lineItem.detail.priceSymbol.endsWith('3')) {
            onHand = qtyRequested;
          } else if (lineItem.detailSupplemental) {
            onHand = parseInt(lineItem.detailSupplemental.onHand, 10);
          }
        }

        let qtyInsufficient = false;
        if (onHand !== null) {
          // 1. Line item has 0 or negative qty on hand?
          // 2. Will the requested quantity cause the part stock to go negative?
          // (1 || 2): Style line item with pink background and red/bold on hand value.
          if (onHand <= 0 || onHand < qtyRequested) {
            qtyInsufficient = true;
          }
        }

        candidateParts.push({
          confirmationSale,
          description,
          longDescription,
          onHand,
          partNum,
          qtyInsufficient,
          qtyRequested,
          uniqueID,
        });
      }

      // We don't care about logging default parts or non-tracked parts.
    }

    return candidateParts;
  }

  async loyaltyOfferBackgroundCheck() {
    if (this.activeLoyaltyCustomerID === null) return; // should not happen
    if (this.lineItemsQueued.length > 0) return; // wait for parts to load (will try again next interval)

    // we only care about non-superceded stocking parts
    const candidateParts: string[] = [];
    this.lineItems.forEach(lineItem => {
      if (lineItem.fetched && lineItem.detail && !lineItem.supercedenceDetails && !lineItem.issuedToRO) {
        candidateParts.push(lineItem.partNum.slice(2)); // PACCAR doesn't need leading dealer divisions
      }
    });

    const partCheckCount = candidateParts.length;

    // no parts to check
    if (partCheckCount === 0) {
      this.loyaltyOfferBackgroundCheckTracker = null; // cancel any possible pending API requests
      this.loyaltyOfferCount = 0;
      return;
    }

    /* We only make a call to PACCAR if we absolutely need to. If the candidate part count hasn't
       changed since our last hit to PACCAR, there's no need to check again. Certain edge cases exist
       such as part line substitutions (which keep the same part count), and in such cases, the tracker
       is manually cleared and therefore prompts a check.
    */
    if (
      this.loyaltyOfferBackgroundCheckTracker === null ||
      (this.loyaltyOfferBackgroundCheckTracker &&
        this.loyaltyOfferBackgroundCheckTracker.partCheckCount !== partCheckCount)
    ) {
      const epochTime = Date.now();
      this.loyaltyOfferBackgroundCheckTracker = {
        lastCheckEpoch: epochTime,
        partCheckCount,
      };

      const params = {
        externalCustomerId: this.activeLoyaltyCustomerID,
        partNumbers: candidateParts.join(','),
        offerType: ['M', 'Y'], // Only include monthly & yearly part offers
      };

      if (this.loyaltyLogging) {
        console.warn('PACCAR Loyalty Offer Check Request');
        console.log(params);
      }

      const res = await Api.get(`loyalty/customers/${this.activeLoyaltyCustomerID}/offers/best`, {
        params,
      });

      // Check for cancellation or API out of order responses
      if (
        this.loyaltyOfferBackgroundCheckTracker === null ||
        this.loyaltyOfferBackgroundCheckTracker.lastCheckEpoch !== epochTime
      ) {
        if (this.loyaltyLogging) {
          console.warn('PACCAR Loyalty Check Cancelled or API overrun detected - Response Disarded');
        }
        return;
      }

      if (this.loyaltyLogging) {
        console.warn('PACCAR Loyalty Offer Check Response');
        console.log(res.data);
      }

      this.loyaltyOfferCount = _.isObject(res.data) && !res.data.error ? Object.keys(res.data).length : 0;

      // auto bind the offers if loyalty automation is turned on via PTB2
      // errors and invalid responses have already been filtered here
      if (this.sysParams.loyaltyAutomate && this.loyaltyOfferCount > 0) {
        const loyaltyOffers = res.data; // object keyed on part numbers without leading division

        // since this is a background process, it's possible, though unlikely, the items on screen changed (slow API)
        for (const loyaltyPartNum of Object.keys(loyaltyOffers)) {
          const loyaltyOffer: ILoyaltyOffer = loyaltyOffers[loyaltyPartNum];

          // we need net negative as a coupon
          if (loyaltyOffer.Amount && typeof loyaltyOffer.Amount === 'number') {
            loyaltyOffer.Amount = -loyaltyOffer.Amount;
          } else {
            loyaltyOffer.Amount = 0;
          }

          // necessary for loyalty modal re-entrant auto selection
          loyaltyOffer.OfferPartId = `${loyaltyOffer.OfferUniqueId}:${loyaltyPartNum}`;

          // apply the offers wherever they fit with the current snapshot of the line items
          this.lineItems.forEach(lineItem => {
            if (
              lineItem.fetched &&
              lineItem.detail &&
              !lineItem.supercedenceDetails &&
              !lineItem.skipLoyaltyOfferAutoAttach &&
              !lineItem.issuedToRO
            ) {
              if (lineItem.partNum.slice(2) === loyaltyPartNum) {
                lineItem.loyaltyOffers = [loyaltyOffer];
              }
            }
          });
        }
      }
    }
  }

  // UI tracks this flag to determine when to engage move operations mode, which makes line items selectable
  get moveOperationsModeActive() {
    return this._moveOperationsModeActive;
  }

  set moveOperationsModeActive(active: boolean) {
    this._moveOperationsModeActive = active;
    this.moveOperationsQueue.clear();
  }

  // When moveOperationsModeActive is true, this method tracks the line items which have been clicked.
  moveOperationsLineItemClicked(itemIndex: number) {
    // De-select if already selected
    if (this.moveOperationsQueue.has(itemIndex)) {
      this.moveOperationsQueue.delete(itemIndex);
    } else {
      // Ensure the item is not on a locked or voided operation before adding to queue.
      const lineitemOperationNumber = this.lineItems[itemIndex].roDetail!.operationNumber;
      const operationDetails = this.activeRepairOrderDetails.operationDetails;

      if (operationDetails[lineitemOperationNumber].locked) {
        toastr.error('Error', 'Unable to move item from locked operation.');
      } else if (operationDetails[lineitemOperationNumber].voided) {
        toastr.error('Error', 'Unable to move item from voided operation.');
      } else {
        this.moveOperationsQueue.set(itemIndex, this.lineItems[itemIndex].quantity);
      }
    }
  }

  // Sometimes clerks may wish to select all the items on an operation to move in one shot
  moveOperationsSelectAllOnActiveOperation() {
    const operationDetails = this.activeRepairOrderDetails.operationDetails;
    const operation = this.activeOperation;

    if (operationDetails[operation].locked) {
      toastr.error('Error', 'Unable to move items from locked operation.');
    } else if (operationDetails[operation].voided) {
      toastr.error('Error', 'Unable to move items from voided operation.');
    } else {
      runInAction(() => {
        this.lineItems.forEach((lineItem, itemIndex) => {
          if (lineItem.roDetail!.operationNumber === operation) {
            this.moveOperationsQueue.set(itemIndex, this.lineItems[itemIndex].quantity);
          }
        });
      });
    }
  }

  // This routine does the QM work to move the selected line items to the destination operation number.
  async moveOperationsQueueToOperation(newOp: number) {
    const numericSort = (a: number, b: number) => a - b;
    const issuedItemIndexes: number[] = [];
    const preIssuedItemIndexes: number[] = [];
    const lineItems = this.lineItems;

    this.moveOperationsInProgress = true;

    // First separate issued from pre-issued items.
    for (const [itemIndex] of this.moveOperationsQueue) {
      // No need to move items already on destination op (causes issues with RO close - issue 3822)
      if (lineItems[itemIndex].roDetail!.operationNumber === newOp) {
        continue;
      }

      if (lineItems[itemIndex].issuedToRO) {
        issuedItemIndexes.push(itemIndex);
      } else {
        preIssuedItemIndexes.push(itemIndex);
      }
    }

    // Map is ordered by insertion.  We'll sort by line index to maintain the same relative order post QM move.
    issuedItemIndexes.sort(numericSort);

    // preIssuedItemIndexes will maintain relative order regardless of map insertion order since we're only
    // updating the destination operation number in the line item data structure which has not yet been issued.

    if (preIssuedItemIndexes.length > 0) {
      this.moveOperationsInProgressRepricing = true;

      // Move the pre-issued items first then re-price (different ops could have different pricing)
      // Price forces are maintained if the clerk already forced the price to something else.
      // This logic is very similar to reEvalInvoice(), but specific enough to require reimplementation.
      runInAction(() => {
        preIssuedItemIndexes.forEach(itemIndex => {
          const lineItem = lineItems[itemIndex];
          lineItem.roDetail!.operationNumber = newOp;

          // Re-eval if necessary to get accurate pricing on the new operation
          if (
            !_.includes(['LINEITEM_COMMENT', 'LINEITEM_DEFAULTPART', 'LINEITEM_NONSTOCKINGPART'], lineItem.partNum) &&
            !(lineItem.confirmationSale || lineItem.confirmationComment)
          ) {
            lineItem.fetched = false;
            delete lineItem.detail;
            delete lineItem.detailSupplemental;
            delete lineItem.incompleteDetailErrors;
            delete lineItem.supercedenceDetails;
            delete lineItem.supercedenceDetailsSupplemental;
          }
        });
      });

      // Re-eval each non comment / default part / nonstocking part line item / confirmation item
      // TODO: Decide what to do on API errors
      this.modelLocked = true;
      this.showToasters = false;
      for (let i = 0; i < lineItems.length; i++) {
        if (!lineItems[i].fetched) {
          const lengthPreEval = this.lineItems.length;

          await this.evalLineItem(i);

          // evalLineItem could potentially modify the line item count (splits, etc.)
          // if so, start the re-eval back at 0 (already fetched items will be skipped)
          // this should not occur on PRO1/SMM3, but just in case a new split super is defined in the interim
          if (lengthPreEval !== this.lineItems.length) {
            i = -1; // post iteration i++ will reset to 0
          }
        }
      }
      this.modelLocked = false;
      this.showToasters = true;
      this.moveOperationsInProgressRepricing = false;
    }

    // Now move the previously issued items
    if (issuedItemIndexes.length > 0) {
      const opMoves: Array<{ roKey: string; newOp: number; qty: number }> = [];

      for (let i = 0; i < issuedItemIndexes.length; i++) {
        const itemIndex = issuedItemIndexes[i];
        const lineItem = lineItems[itemIndex];
        const qty = this.moveOperationsQueue.get(itemIndex)!; // could be a partial move (editable when qty > 1)

        opMoves.push({
          roKey: lineItem.roDetail!.roKey,
          newOp,
          qty,
        });

        // Also move any attached loyalty coupons which were previously redeemed
        // It's (hypothetically) possible PACCAR gave us fewer redemptions than part qty issued
        if (lineItem.detail && lineItem.detail.loyaltyCouponsApplied) {
          opMoves.push({
            roKey: lineItem.detail.loyaltyCouponsRoKey,
            newOp,
            qty: Math.min(qty, lineItem.detail.loyaltyCouponsQuantity),
          });
        }
      }

      this.lastMoveOperationsReq = {
        ppc: this.ppcParams.ppc,
        roNumber: this.activeRONumber,
        opMoves,
      };

      if (this.logging) {
        console.warn('/moveoperations Request:');
        console.log(this.lastMoveOperationsReq);
      }

      try {
        const res: IApiResponse = await Api.post('/shopkeeper/moveoperations', this.lastMoveOperationsReq);
        this.lastMoveOperationsRes = res;

        if (this.logging) {
          console.warn('/moveoperations Response:');
          console.log(this.lastMoveOperationsRes);
        }

        if (res.status !== 200) {
          throw new Error('200 not received from moveoperations.');
        }

        // { dbpip, dbpipReq } DBPIP response/request dialog files
        const dbpip = res.data.dbpip.split('\n');
        const dbpipError: string = _.get(dbpip, 12, '');

        if (dbpipError) {
          // Legacy unexpectedly returns multiple errors sometimes.
          const errors = dbpipError.split('ý');
          console.error(errors); // expand on console
          throw new Error(errors[0]); // show the first error (there SHOULD only be 1, but legacy)
        }
      } catch (err: any) {
        console.error(err.message);
        toastr.error('Error', err.message);
      }

      // This will retain the pre-issued items we already moved and repriced, as well as other pre-issued items in the queue.
      await this.refreshActiveROData({ retainIssueQueue: 'ALLOPS' });
    }

    this.moveOperationsInProgress = false;
    this.moveOperationsModeActive = false;
  }

  // Called by the destination operation selector modal.
  async moveOperationsSetDestinationOperationNumber(operationNumber: number) {
    const continueWithMove: boolean = await new Promise(resolve => {
      modal.confirm({
        title: 'Are you sure?',
        message: `You're about to move ${this.moveOperationsQueue.size} items to operation ${operationNumber}.  Are you sure?`,
        confirmLabels: ['Yes', 'No'],
        callback: () => resolve(true),
        onClose: () => resolve(false),
      });
    });

    if (continueWithMove) {
      this.moveOperationsQueueToOperation(operationNumber);
    }
  }

  /* Attach any selected PACCAR Loyalty offers to exsiting candidate line items.

     The candidateLineItems data structure has part numbers pre-stripped of their leading division (for direct pairing)
     Each offer can reference multiple part numbers.  Each candidate line item can have multiple offers.
     QM partNums are already all-cap normalized.

     Ex-post, results in a single re-render pass after all proxy objects are updated.

     6/2020:
     Our original implementation allowed multiple offers per line.  As of 06/2020, Paccar has stated
     only 1 offer is allowed per part line.

     3/2022:
     Loyalty automation is now optional. The following logic is utilized only via the clerk loaded loyalty offer modal.
     The background offer checker (and optional automatic offer application) uses slightly different logic since the clerk
     could still be adding lines, splits could be re-evaluating etc., all in real time.
     See loyaltyOfferBackgroundCheck().
  */
  pairOffersToLoyaltyCandidateLineItems(candidateLineItems: ILoyaltyCandidate[], loyaltyOffers: ILoyaltyOffer[]) {
    if (this.logging) {
      console.warn('Selected Loyalty Offers');
      console.log(loyaltyOffers);
    }

    // Don't trust 3rd party data structure / API response!
    if (Array.isArray(loyaltyOffers) && loyaltyOffers.length > 0) {
      runInAction(() => {
        for (const loyaltyOffer of loyaltyOffers) {
          // we need net negative as a coupon
          if (loyaltyOffer.Amount && typeof loyaltyOffer.Amount === 'number') {
            loyaltyOffer.Amount = -loyaltyOffer.Amount;
          } else {
            loyaltyOffer.Amount = 0;
          }

          for (const candidateLineItem of candidateLineItems) {
            if (loyaltyOffer.SelectedPart === candidateLineItem.partNumber) {
              this.lineItems[candidateLineItem.lineItemIndex].loyaltyOffers = [loyaltyOffer];
            }
          }
        }
      });

      this.updateTaxes();
    }
  }

  // Individual items across all operations pending issue, does not consider quantity per item
  @computed
  get preIssueQueueLength() {
    return this.lineItems.reduce((acc, lineItem) => (!lineItem.issuedToRO ? acc + 1 : acc), 0);
  }

  /* PIN1 invoice process, quote creation, and pick ticket creation

     Returns { success: boolean; redirect: element to focus to on fail (for quick input) }

     type determines what we're processing here: invoice, quote, pick list, etc.

     Note process() is re-entrant for several different reasons, all of which pop a modal to collect more info:
     - Credit hold on invoice process (which will populate creditHoldPostOpFields on re-entant call)
     - Fleet Billing post op fields (which will populate fleetBillingPostOpFields on re-entant call)
     - Printer selection in cases where it has not been selected yet / no defaultDivision
     - PACCAR Bond Loyalty offer redemption which must occur before fleet billing / credit checks

     Also note certain process options such as expirationDate and hidePartNumbers are only touched in quote situations.
  */
  async process({
    creditHoldPostOpFields = null,
    expirationDate = '',
    fleetBillingPostOpFields = null,
    hidePartNumbers = false,
    printer = null,
    quoteTitle = '',
    type,
  }: {
    creditHoldPostOpFields?: {
      action: string;
      holdNumber: string;
      screenPrintEmail: string;
    } | null;
    expirationDate?: string;
    fleetBillingPostOpFields?: string[] | null;
    hidePartNumbers?: boolean;
    printer?: { id: string; name: string } | null;
    quoteTitle?: string;
    type: 'invoice' | 'manualcredit' | 'pickticket' | 'quote';
  }): Promise<{ success: boolean; redirect: string }> {
    this.lastProcessType = type;

    // Make sure required UI fields are populated.
    // Order of tests matters here: PPC->CUST->PO->FREIGHT

    const managerOverridePPCs = new Set<string>();

    const noRedirect = {
      success: false,
      redirect: '',
    };

    let creditCardSale = false;

    if (!this.ppcValidated) {
      toastr.error('Error', 'A validated PPC must be entered.');
      return {
        success: false,
        redirect: 'PPC',
      };
    }

    if (['invoice', 'manualcredit', 'quote'].includes(type) && !this.activeCustomerID) {
      toastr.error('Error', 'A customer must be loaded.');
      return {
        success: false,
        redirect: 'CUST',
      };
    }

    // one last check pending po input is valid (do this before this.po check below)
    if (this.activeCustomerID && this.uiBridge.poInputRef.value.trim() !== '') {
      if (!this.validatePo(this.uiBridge.poInputRef.value.trim())) {
        toastr.error('Error', 'Invalid Customer PO format.', { toastId: 'invalidPo' });
        return {
          success: false,
          redirect: 'PO',
        };
      }
    }

    // make sure po is set if required (# or *pattern set for PO on PCU2 for this customer)
    if (type === 'invoice' && this.customerParams.purchaseOrderRequired && this.po === null) {
      toastr.error('Error', 'PO required by this customer.');
      return {
        success: false,
        redirect: 'PO',
      };
    }

    // one last check pending freight input is valid
    if (this.activeCustomerID && this.uiBridge.freightInputRef.value.trim() !== '') {
      if (isNaN(parseFloat(this.uiBridge.freightInputRef.value))) {
        toastr.error('Error', 'Freight Invalid Format', { toastId: 'freightInput' });
        return {
          success: false,
          redirect: 'FREIGHT',
        };
      }
    }

    // one last check pending fuel charge input is valid (when applicable)
    if (this.activeCustomerID && this.promptForFuel && this.uiBridge.fuelChargeInputRef.value.trim() !== '') {
      if (isNaN(parseFloat(this.uiBridge.fuelChargeInputRef.value))) {
        toastr.error('Error', 'Fuel Charge Invalid Format', { toastId: 'fuelChargeInput' });
        return {
          success: false,
          redirect: 'FUELCHARGE',
        };
      }
    }

    // prompt for freight if configured via PTB2
    if (
      ['invoice', 'quote'].includes(type) &&
      ['Y', 'F'].includes(this.sysParams.promptForFreight) && // "Y" = at least 1 request, "F" = force a value
      this.freight === null &&
      !this.lineItems.some(o => o.confirmationFreight) && // Confirmation freight, most likely the only freight needed
      !this.lineItems.some(o => o.poFreight) // Confirmation PO freight from PSO6, also probably good enough
    ) {
      if (!this.freightPromptDisplayed || this.sysParams.promptForFreight === 'F') {
        this.freightPromptDisplayed = true;
        toastr.error('Error', 'Please specify a freight.');
        return {
          success: false,
          redirect: 'FREIGHT',
        };
      }
    }

    // prompt for received by configured via PTB2
    if (type === 'invoice' && this.sysParams.promptForReceivedBy && this.receivedBy === null) {
      if (!this.receivedByPromptDisplayed) {
        this.receivedByPromptDisplayed = true;
        toastr.error('Error', 'Please specify received by.');
        return {
          success: false,
          redirect: 'RECEIVEDBY',
        };
      }
    }

    // prompt for shipping route configured via PTB2
    if (
      type === 'invoice' &&
      (this.sysParams.shippingCodePrompt || this.sysParams.forceRouteSelection) &&
      this.activeRoutingMethod === null
    ) {
      toastr.error('Error', 'Please specify a routing method.');
      return {
        success: false,
        redirect: 'ROUTE',
      };
    }

    // prompt for fuel charge if configured via PTB2 & customer file doesn't suppress
    if (
      ['invoice', 'quote'].includes(type) &&
      this.promptForFuel &&
      this.fuelCharge === null &&
      !this.lineItems.some(o => o.confirmationFuel)
    ) {
      if (!this.fuelPromptDisplayed || this.sysParams.promptForFuel === 'F') {
        this.fuelPromptDisplayed = true;
        toastr.error('Error', 'Please specify a fuel charge.');
        return {
          success: false,
          redirect: 'FUELCHARGE',
        };
      }
    }

    // If a RO transfer customer is being used (defined in PCR6), clerk must specifiy RO# & COND#
    if (['invoice', 'manualcredit'].includes(type) && this.isROTransferCustomerLoaded) {
      if (this.transferRoNum === null) {
        toastr.error('Error', 'Please specify a RO# for this transfer.');
        return {
          success: false,
          redirect: 'TRANSFERRO',
        };
      }

      if (this.transferConditionNum === null) {
        toastr.error('Error', 'Please specify a condition # for this transfer.');
        return {
          success: false,
          redirect: 'TRANSFERCONDITION',
        };
      }
    }

    // check for COD free form address entry completeness (all fields but phone are required)
    if (['invoice', 'manualcredit'].includes(type) && this.customerParams.isCodAccount) {
      if (
        !(
          this.shipToAddress &&
          !!this.shipToAddress.name &&
          !!this.shipToAddress.address &&
          !!this.shipToAddress.city &&
          !!this.shipToAddress.state &&
          !!this.shipToAddress.zipCode
        )
      ) {
        this.showShipToOverrideModal = true;
        toastr.error('Error', 'Required Fields Empty');
        return noRedirect;
      }
    }

    if (
      ['invoice', 'manualcredit'].includes(type) &&
      this.sysParams.creditCardIntegration &&
      (this.customerParams.customerCashOnly || this.forceCashSale) &&
      this.uiBridge.creditCardSaleInputRef
    ) {
      creditCardSale = this.uiBridge.creditCardSaleInputRef.checked;
    }

    if (type === 'manualcredit' && creditCardSale) {
      toastr.error('Error', 'Manual credits to credit cards are not supported.');
      return noRedirect;
    }

    // Now check for a series of "not process ready" model states before processing the invoice

    if (this.taxCalculationInProcess) {
      toastr.error('Wait', 'Tax calculation in process.  Please try again in a moment.');
      return noRedirect;
    }

    if (this.isProcessing) {
      toastr.error('Wait', 'Processing.  Please wait.');
      return noRedirect;
    }

    if (this.modelLocked) {
      toastr.error('Wait', 'Action in process. Please try again in a moment.');
      return noRedirect;
    }

    if (this.lineItemsQueued.length > 0) {
      toastr.error('Wait', 'Queued items pending fetch. Please try again in a moment.');
      return noRedirect;
    }

    for (const lineItem of this.lineItems) {
      if (!lineItem.fetched) {
        toastr.error('Wait', 'Fetch action in process. Please try again in a moment.');
        return noRedirect;
      }
    }

    // Make sure all line items requiring serial numbers match on quantity requested
    if (type === 'invoice') {
      for (const lineItem of this.lineItems) {
        if (
          lineItem.detail &&
          lineItem.detail.serialNumberRequired &&
          (!lineItem.serialNumbersSelected || lineItem.serialNumbersSelected.length !== lineItem.quantity)
        ) {
          toastr.error('Error', 'Serial numbers required.  Please inspect line items.');
          return noRedirect;
        }
      }
    }

    // It doesn't make sense to issue a manual credit to a pending confirmation sale.
    if (type === 'manualcredit') {
      for (const lineItem of this.lineItems) {
        if (lineItem.confirmationComment || lineItem.confirmationSale) {
          toastr.error('Error', 'Manual credits can not be applied to pending confirmation sales.');
          return noRedirect;
        }
      }
    }

    /* Process PACCAR Loyalty redemptions (if any non-redeemed offers still exist in model).
       Only allow process pass through if all lines with bound PACCAR offers have been redeemed
       or otherwise handled (via the clerk removing failed redemptions, etc.).  The PACCAR loyalty
       modal should make ex-post actions such as "remove all failed offers" seamless and fast.
       Also note lines with qty > 1 will automatically demote the coupon quantity to whatever amount
       is actually redeemed by PACCAR so that our numbers reconcile.
    */
    if (['invoice', 'manualcredit'].includes(type) && this.sysParams.loyalty) {
      let offersFound = false;
      let pendingRedemptions = false;

      for (const lineItem of this.lineItems) {
        if (Array.isArray(lineItem.loyaltyOffers) && lineItem.loyaltyOffers.length > 0) {
          offersFound = true;
          for (const offer of lineItem.loyaltyOffers) {
            if (!offer.Redemptions) {
              pendingRedemptions = true;
            }
          }
        }
      }

      if (type === 'manualcredit' && offersFound) {
        toastr.error('Error', 'Loyalty offers and redemptions are not supported in manual credit mode.');
        return noRedirect;
      }

      /* This edge case would happen under the following possible (but highly unlikely) scenario:
         1. Clerk loads loyalty customer
         2. Clerk binds loyalty offers to line items (which requires loaded loyalty customer)
         3. Clerk hits error state on first redemption pass (API timeout, missing redemption, etc.)
         4. Clerk closes redemption modal and clears loyalty customer, but not loyalty offers.
            (The proper action would be to select "Remove unredeemed offers and process invoice.")
         5. Clerk tries to process again.  This would lead to unredeemed offers with no loyalty customer ID.
      */
      if (offersFound && this.activeLoyaltyCustomerID === null) {
        toastr.error('Error', 'Loyalty customer must be loaded to redeem loyalty offers.');
        return noRedirect;
      }

      if (pendingRedemptions) {
        /* Here we process any remaining offers that do not have a redemption attached.
           Note this could be due to a first pass, or a retry attempt.  An invoice will only
           be processed if all selected offers have been redeemed or removed.

           Do not await this call. The method will pop a modal and handle ex-post actions
           (such as invalid offers, partial redemptions, etc.), then re-entrantly call process()
           again after any necessary action resolution.
        */
        this.processLoyaltyRedemptions();
        return noRedirect;
      }
    }

    // lastManualCreditAuth is set if credentials are validated by the manual credit auth modal
    if (type === 'manualcredit' && this.creditPPCAuthRequired && !this.lastManualCreditAuth) {
      this.showManualCreditAuthModal = true;
      setTimeout(() => {
        if (this.canClerkPPCIssueCredit) {
          this.uiBridge.manualCreditAuthPasswordInput.focus();
        } else {
          this.uiBridge.manualCreditAuthPpcInput.focus();
        }
      }, 100); // convenience focus
      return noRedirect;
    }

    if (!printer) {
      switch (type) {
        // manual credits use the same printer selection logic as invoices
        // the printer selection modals reference model.lastProcessType for re-entrance
        case 'manualcredit':
        case 'invoice':
          // if this is a cash sale, force a cash sale printer selection if cash printers are defined
          if (
            (this.customerParams.customerCashOnly || this.forceCashSale) &&
            this.sysParams.cashSalePrinters.length > 0
          ) {
            // if only one cash printer is defined, no need to pop modal
            if (this.sysParams.cashSalePrinters.length === 1) {
              printer = this.sysParams.cashSalePrinters[0];
              break;
              // otherwise, pop modal for printer selection
            } else {
              this.showCashSalePrinterSelectionModal = true;
              return noRedirect;
            }
          }

          if (this.sysParams.invoicePrinters.length > 0) {
            // if only one invoice printer is defined, no need to pop modal
            if (this.sysParams.invoicePrinters.length === 1) {
              printer = this.sysParams.invoicePrinters[0];
              break;
              // otherwise, pop modal for printer selection
            } else {
              this.showInvoicePrinterSelectionModal = true;
              return noRedirect;
            }
          }
          // if no printer is defined, a "default" invoice printer should be used as specified in QM setup
          break;

        case 'pickticket':
          if (this.sysParams.pickTicketPrinters.length > 0) {
            if (this.sysParams.pickTicketPrinters.length === 1) {
              printer = this.sysParams.pickTicketPrinters[0];
              break;
              // otherwise, pop modal for printer selection
            } else {
              this.showPickTicketPrinterSelectionModal = true;
              return noRedirect;
            }
          } else {
            // no "default" pick ticket printer exists, must be defined in QM setup
            toastr.error('Error', 'No Pick Ticket Printers Defined.');
            return noRedirect;
          }

        case 'quote':
          /* Quotes use the confirmationQuote printer list if available, otherwise we
             fallback to invoice printers.  However, in the case of quotes, we must
             always show the modal since we need the expiration date. In the event no
             printers are defined, no printer list will be presented, and printer.id
             will end up as an empty string, which is fine for DBPIP.
          */
          this.showQuotePrinterSelectionModal = true;
          return noRedirect;

        default:
          // should not occur
          break;
      }
    }

    // Make sure the clerk realizes they are processing a manual CREDIT, not a sale
    if (type === 'manualcredit') {
      const continueWithManualCredit: boolean = await new Promise(resolve => {
        modal.confirm({
          title: 'Are you sure?',
          message: 'You are about to process a manual CREDIT.  Are you sure?',
          confirmLabels: ['Yes', 'No'],
          callback: () => resolve(true),
          onClose: () => resolve(false),
        });
      });

      if (!continueWithManualCredit) {
        this.lastManualCreditAuth = null;
        return noRedirect;
      }
    }

    // End "not process ready" model states

    const linesToProcess: Array<{
      partNum: string;
      qty: number;
      comment?: string;
      forcedDescription?: string;
      forcedPricing?: {
        forcedNetPrice: number;
        forcedListPrice: number;
        forcedTradePrice: number;
      };
      priceSymbol?: string;
      stockClass?: string;
      skipPOCheck?: boolean;
      serialNumbersSold?: string[];
      suppressCoreCharge?: boolean;
      suppressFee?: boolean;
      backorderedQty?: number;
      confirmationComment?: ILineItem['confirmationComment'];
      confirmationSale?: ILineItem['confirmationSale'];
      loyaltyRedemptionTracking?: {
        couponCode: string;
        couponDesc: string;
        invoiceNumProxy: string;
        offerID: string;
        loyaltyCustomerID: string;
        redemptionIDs: string[]; // if qty > 1
        redemptionApprovalCodes: string[]; // if qty > 1
      };
    }> = [];

    // Used for ex-post validation on successful invoice process
    const loyaltyRedemptionIDs: string[] = [];

    for (const lineItem of this.lineItems) {
      // Is this line item a comment?
      if (lineItem.partNum === 'LINEITEM_COMMENT') {
        linesToProcess.push({
          partNum: 'LINEITEM_COMMENT',
          qty: 1,
          comment: lineItem.comment,
          confirmationComment: ['quote', 'pickticket'].includes(type) ? undefined : lineItem.confirmationComment, // undefined if not a confirmation comment
        });
      } else if (lineItem.partNum === 'LINEITEM_DEFAULTPART') {
        // Note confirmationSale data isn't necessary on LINEITEM_DEFAULTPART since this line type
        // would never occur when selling off of a confirmation.  They would be converted to non-stocking/non-tracked lines.

        linesToProcess.push({
          partNum: `@X:${lineItem.defaultPartNum}`,
          qty: lineItem.quantity,
          forcedDescription: lineItem.defaultPartDescription,
          // DBPIP accounts for reverse signs
          forcedPricing: {
            forcedNetPrice: Math.abs(lineItem.defaultPartNetPrice!),
            forcedListPrice: Math.abs(lineItem.defaultPartListPrice!),
            forcedTradePrice: Math.abs(lineItem.defaultPartTradePrice!),
          },
          priceSymbol: lineItem.priceSymbol,
          stockClass: lineItem.stockClass,
          skipPOCheck: true,
        });

        /* We keep sublet comments attached to the line item within the data structure so that
           the sublet default part and sublet comment are tightly coupled on drags.  When we process it
           via DBPIP, the attached comment simply becomes its own line item comment.
        */
        if (lineItem.defaultPartSubletComment !== '') {
          linesToProcess.push({
            partNum: 'LINEITEM_COMMENT',
            qty: 1,
            comment: lineItem.defaultPartSubletComment!,
          });
        }
      } else if (lineItem.partNum === 'LINEITEM_NONSTOCKINGPART') {
        // If user included a division, use it, otherwise, apply a default division of X.
        // The non-tracked (non-stocking) part entry modal auto-prepends the default division if not specified.
        const partNum = /^[A-Za-z]:/.test(lineItem.nonStockingPartNum!)
          ? `@${lineItem.nonStockingPartNum}`
          : `@X:${lineItem.nonStockingPartNum}`;

        linesToProcess.push({
          partNum,
          qty: lineItem.quantity,
          forcedDescription: lineItem.nonStockingPartDescription === '' ? partNum : lineItem.nonStockingPartDescription,
          forcedPricing: {
            forcedNetPrice: Math.abs(lineItem.nonStockingPartNetPrice!),
            forcedListPrice: Math.abs(lineItem.nonStockingPartListPrice!),
            forcedTradePrice: Math.abs(lineItem.nonStockingPartTradePrice!),
          },
          priceSymbol: lineItem.priceSymbol,
          stockClass: lineItem.stockClass,
          skipPOCheck: true,
          confirmationSale: ['quote', 'pickticket'].includes(type) ? undefined : lineItem.confirmationSale, // undefined if not a confirmation sale
        });

        /* Is this a supercedence base part for non-fleet billing? (no forces will apply)
           See subTotal() for explanation of the lack of per line core/fee suppression support.
           Like default parts, confirmationSale data isn't necessary on superceded parts since this line type would
           never occur when selling off of a confirmation.  They would be converted to the constiuent part lines.
        */
      } else if (lineItem.supercedenceDetails) {
        linesToProcess.push({
          partNum: lineItem.partNum,
          qty: lineItem.quantity,
          backorderedQty: lineItem.backorderedQuantity, // undefined in all but "INVOICE ON HAND" cases
        });

        // Standard line item eligible for force overrides and core/fee suppressions
      } else if (lineItem.detail) {
        let forcedPricing = lineItem.forcedPricing
          ? {
              forcedNetPrice: lineItem.forcedNetPrice!,
              forcedListPrice: lineItem.forcedListPrice!,
              forcedTradePrice: lineItem.forcedTradePrice!,
            }
          : undefined;

        // Collect unique manager override PPC, if any
        if (lineItem.forcedPricing && lineItem.forcedManagerOverridePPC) {
          managerOverridePPCs.add(lineItem.forcedManagerOverridePPC);
        }

        // On quotes, if the line item is not explicitly forced, force it implicitly to retain pricing.
        if (type === 'quote' && !forcedPricing) {
          forcedPricing = {
            forcedNetPrice: lineItem.detail.netPrice,
            forcedListPrice: lineItem.detail.listPrice,
            forcedTradePrice: lineItem.detail.tradePrice,
          };
        }

        // If we're invoicing a price force on a stocking part which isn't a confirmation sale, default net to FIFO.
        // This prevents clerks from trying to game the commission system, per Vic.
        if (type === 'invoice' && forcedPricing && !lineItem.confirmationSale) {
          forcedPricing.forcedNetPrice = lineItem.detail.netPrice;
        }

        // If PIV1/Core/Core Flag = 'S', we've forced the clerk to tell us which serial #s are being sold.
        let serialNumbersSold;
        if (type === 'invoice' && lineItem.serialNumbersSelected) {
          serialNumbersSold = lineItem.serialNumbersSelected;
        }

        linesToProcess.push({
          partNum: lineItem.partNum,
          qty: lineItem.quantity,
          // It's possible the next fields are undefined if not set, which simply won't be sent to the API.
          forcedDescription: lineItem.forcedDescription,
          forcedPricing,
          serialNumbersSold,
          suppressCoreCharge: lineItem.suppressCoreCharge,
          suppressFee: lineItem.suppressFee,
          backorderedQty: lineItem.backorderedQuantity, // undefined in all but "INVOICE ON HAND" cases
          confirmationSale: ['quote', 'pickticket'].includes(type) ? undefined : lineItem.confirmationSale, // undefined if not a confirmation sale
        });
      }

      // Are there any bound coupons on this part? (standard stocking parts & supercedence cases)
      // Pre-2020 Big Query coupons DO get saved with pick tickets and quotes.  Loyalty Bond Offer Redemptions do not.
      if (Array.isArray(lineItem.couponDetail)) {
        lineItem.couponDetail.forEach(coupon => {
          linesToProcess.push({
            partNum: `@X:${coupon.defaultPartNum}`,
            qty: coupon.quantity,
            forcedDescription: coupon.defaultPartDescription,
            // DBPIP accounts for reverse signs
            forcedPricing: {
              forcedNetPrice: Math.abs(coupon.defaultPartNetPrice),
              forcedListPrice: Math.abs(coupon.defaultPartListPrice),
              forcedTradePrice: Math.abs(coupon.defaultPartTradePrice),
            },
            priceSymbol: coupon.priceSymbol,
            stockClass: coupon.stockClass,
          });
        });
      }

      // Are there any bound PACCAR Loyalty Bond offers on this part?  We already ensured loyalty customer loaded.
      // Manual credits fail to execute if there's any pending offers attached to a line item.
      if (type === 'invoice' && Array.isArray(lineItem.loyaltyOffers) && lineItem.loyaltyOffers.length > 0) {
        // We won't know the DSI invoice # until post credit check, fleet billing, and dealer invoice processing,
        // however, we must know redemption results before those things in order to bill the customer correctly.
        // Thus legacy will link the eventually created dealer invoice # to this proxy invoice #.
        const invoiceNumProxy = await this.getLoyaltyInvoiceNumber();

        lineItem.loyaltyOffers.forEach(offer => {
          // Logically if we get here within process(), all offers should all be at least partially redeemed (or removed).
          if (offer.Redemptions) {
            // DBPIP accounts for reverse signs
            const amount = Math.abs(offer.Amount);
            const couponCode = offer.CouponCode;
            const couponDesc = typeof offer.Title === 'string' ? offer.Title.replace(/ /g, '_') : '';
            const loyaltyCustomerID = this.activeLoyaltyCustomerID!;
            const offerID = offer.OfferUniqueId;
            const redemptionIDs = offer.Redemptions.map(r => r.UniqueId);
            const redemptionApprovalCodes = offer.Redemptions.map(r => r.RedeemApprovalCode);

            // used for validation after invoice process success
            loyaltyRedemptionIDs.push(...redemptionIDs);

            linesToProcess.push({
              partNum: `@X:${this.loyaltyDefaultPart.partNum}`,
              qty: offer.Redemptions.length,
              forcedDescription: 'PACCAR LOYALTY COUPON',
              forcedPricing: {
                forcedNetPrice: amount,
                forcedListPrice: amount,
                forcedTradePrice: amount,
              },
              priceSymbol: this.loyaltyDefaultPart.priceSymbol,
              stockClass: this.loyaltyDefaultPart.stockClass,
              loyaltyRedemptionTracking: {
                couponCode,
                couponDesc,
                invoiceNumProxy,
                loyaltyCustomerID,
                offerID,
                redemptionIDs,
                redemptionApprovalCodes,
              },
            });
          }
        });
      }

      // Note other line items could exist such as "BAD PARTS" with possible subs.
    }

    if (linesToProcess.length > 0) {
      // This Fuel Charge is a default part defined on PTB2:Invoicing
      if (this.promptForFuel && this.fuelCharge) {
        const fuelPart = this.defaultParts.get(this.sysParams.fuelChargeDefaultPartName);

        // We confirmed this exists during sysparams load, but double check
        if (fuelPart) {
          linesToProcess.push({
            partNum: `@X:${this.sysParams.fuelChargeDefaultPartName}`,
            qty: 1,
            forcedDescription: fuelPart.desc,
            // DBPIP accounts for reverse signs
            forcedPricing: {
              forcedNetPrice: Math.abs(this.fuelCharge),
              forcedListPrice: Math.abs(this.fuelCharge),
              forcedTradePrice: Math.abs(this.fuelCharge),
            },
            priceSymbol: fuelPart.priceSymbol,
            stockClass: fuelPart.stockClass,
            skipPOCheck: true,
          });
        } else {
          console.error('Missing default fuel part.');
        }
      }

      // Append received by if specified
      if (this.receivedBy) {
        linesToProcess.push({
          partNum: 'LINEITEM_COMMENT',
          qty: 1,
          comment: `RECEIVED BY: ${this.receivedBy}`,
        });
      }

      // Inject OPC memo back when applicable, needs to be line 1
      if (type === 'invoice' && this.OPCdata) {
        linesToProcess.unshift({
          partNum: 'LINEITEM_COMMENT',
          qty: 1,
          comment: this.OPCdata.comment,
        });
      }

      /* March 2021, we detected MULTISERVICE asks for fields in multiple API passes rather than all at once.
         Here we cache the values between passes, ensuring each field only occurs once in the final submission.
         The cache is automatically reset between invoicing sessions.
      */
      if (Array.isArray(fleetBillingPostOpFields)) {
        fleetBillingPostOpFields.forEach(field => {
          const fieldParts = field.split(':');
          const key = fieldParts[0];
          const value = fieldParts.slice(1).join(':'); // supports ':' in the values
          this.fleetBillingPostOpFieldCache[key] = value;
        });

        // Fold previously cached (if any) / unique field values into submission array
        fleetBillingPostOpFields = [];
        for (const [key, value] of Object.entries(this.fleetBillingPostOpFieldCache)) {
          fleetBillingPostOpFields.push(`${key}:${value}`);
        }
      } else {
        // Otherwise this isn't fleet bill post op pass / user possibly canceled post op data collection
        this.fleetBillingPostOpFieldCache = {};
      }

      let ppc = this.ppcParams.ppc;

      if (type === 'invoice' && this.ppcAttributionReplayActive) {
        ppc = this.ppcAttribution as string;
      }

      // Per Phil, submit manager overrides as PPC-managerPPC
      // It's possible multiple managers approved various lines.  We'll only submit unique PPCs.
      if (managerOverridePPCs.size > 0) {
        for (const o of managerOverridePPCs) {
          ppc += `-${o}`;
        }
      }

      // Used only on invoice by fleet accounts that have more than one billing card defined,
      // and the clerk has selected an option other than the first in the list, which is default.
      let fleetBillingCC: undefined | string; // last 4 if id != 1
      if (
        this.fleetBillingCCVariance &&
        this.fleetBillingCCVariance !== 1 &&
        !this.customerParams.customerCashOnly &&
        !this.forceCashSale &&
        Array.isArray(this.customerParams.fleetCards)
      ) {
        const match = this.customerParams.fleetCards.find(o => o.id === this.fleetBillingCCVariance);
        if (match) {
          fleetBillingCC = match.last4;
        }
      }

      this.modelLocked = true;
      let success = false;

      this.lastProcessReq = {
        creditCardSale,
        creditHoldPostOpFields,
        customerID: this.activeCustomerID,
        expirationDate,
        fleetBillingPostOpFields,
        freight: this.freight, // dedicated DBPIP row, not within line items
        hidePartNumbers,
        linesToProcess,
        po: this.po,
        ppc,
        ppcManualCreditAuth:
          type === 'manualcredit'
            ? this.lastManualCreditAuth
              ? this.lastManualCreditAuth.ppc
              : this.ppcParams.ppc
            : undefined, // note PPC-PPC format not supported not supported on this line
        printer: printer ? printer.id : null,
        processByBinLocation: this.processByBinLocation,
        quoteTitle,
        type,
        variances: {
          fleetBillingCC,
          forceCashSale: this.forceCashSale,
          forceSalesTax: this._forceSalesTax,
          noHistorySale: this.noHistorySale,
          nonTaxReasonCode: this.nonTaxReasonCode,
          /* The UI / model logic should enforce only one of these being set.
             The API will prioritize shipToAccount in the event both are erroneously provided.
          */
          shipToAccount: this.shipToAccount ? this.shipToAccount.customerID : null,
          shipToAddress: this.shipToAddress ? this.shipToAddress : null,

          shipToEmail: this.shipToEmail, // used only in shipToAccount usage scenarios
          shipToRoute: this.activeRoutingMethod,
          suppressCoreCharges: this._suppressCoreCharges,
          suppressFees: this._suppressFees,
          suppressSalesTax: this._suppressSalesTax,
        },
      };

      // Internal sales support
      if (['invoice', 'manualcredit'].includes(type)) {
        // RO transfer customers ignore internal/warranty gl/control# requirements.
        if (this.isROTransferCustomerLoaded) {
          this.lastProcessReq['transferRoNum'] = this.transferRoNum;
          this.lastProcessReq['transferConditionNum'] = this.transferConditionNum;
        } else if (this.isLedgerDetailRequired) {
          this.lastProcessReq['glNum'] = this.glNum;
          this.lastProcessReq['controlNum'] = this.controlNum;
        }
      }

      // PIN1 email override support
      // SKIP functionality checks for SKIP checkbox OR all values removed
      if (['invoice', 'manualcredit'].includes(type)) {
        if (this.skipEmails) {
          this.lastProcessReq['variances']['email'] = 'SKIP';
        } else if (this.invoiceEmailsOverride) {
          this.lastProcessReq['variances']['email'] =
            this.invoiceEmailsOverride.length === 0 ? 'SKIP' : this.invoiceEmailsOverride.join(',');
        }
      }

      // Pick lists and quotes should be cleared out after they are sold off in PIN1 (Issue #4444)
      if (type === 'invoice' && this.lastPIN1PickListID) {
        this.lastProcessReq['pickListIDToClear'] = this.lastPIN1PickListID;
      }

      // Used for customers that want B/O info printed on invoices
      // Sometimes sessions create a backorder then invoice something having nothing to do with the confirmation.
      // This allows QM to track created confirmations in the session to print B/O info on the invoice.
      if (type === 'invoice') {
        this.lastProcessReq['confirmationsCreatedThisSession'] = this.confirmationsCreatedThisSession;
      }

      if (type === 'quote') {
        if (this.skipEmails) {
          this.lastProcessReq['variances']['email'] = 'SKIP';
        } else if (this.quoteEmailsOverride) {
          this.lastProcessReq['variances']['email'] =
            this.quoteEmailsOverride.length === 0 ? 'SKIP' : this.quoteEmailsOverride.join(',');
        }
      }

      if (this.logging) {
        console.warn('/process Request:');
        console.log(this.lastProcessReq);
      }

      try {
        this.isProcessing = true;
        const res = await Api.post('/shopkeeper/process', this.lastProcessReq);
        this.isProcessing = false;
        this.lastProcessRes = res;

        if (this.logging) {
          console.warn('/process Response:');
          console.log(this.lastProcessRes);
        }

        // Extract any errors that occurred during processing.
        let parsedResponse = res.data.dbpip.split('\n');
        let mainErrorMessages = _.get(parsedResponse, 12, '').trim();
        const nonStockErrorMessages = _.get(parsedResponse, 72, '').trim();

        // If any errors occurred, do not clear the screen, pop the error.
        if (mainErrorMessages || nonStockErrorMessages) {
          // Clear auth on error in case manager walked away.
          if (type === 'manualcredit') {
            this.lastManualCreditAuth = null;
          }
          const processErrors = [];
          const nonStockErrors = [];

          if (mainErrorMessages) {
            // Check for post-op additional fleet billing fields required here
            if (mainErrorMessages.startsWith('ADDITIONALDATAREQUIRED:')) {
              // Fleet billing not supported on manual credits (should not happen)
              if (type === 'manualcredit') {
                console.error(mainErrorMessages);
                toastr.error('Error', 'Fleet billing fields required. Not supported on manual credits.');
                return noRedirect;
              }

              // Seed a fleet billing post-op request object and render re-entrant modal.
              // Non-AUTH fields only occur in certain instances.
              const fields = mainErrorMessages.slice('ADDITIONALDATAREQUIRED:'.length).split('ý');

              // Test required fields for support
              for (const field of fields) {
                if (
                  !_.includes(
                    [
                      'IBSREQ:AUTH',
                      'MULTISERVICE:UNIT',
                      'MULTISERVICE:VIN',
                      'MULTISERVICE:AUTH',
                      'VOLVOINQ:OVER',
                      'VOLVOINQ:AUTH',
                    ],
                    field
                  )
                ) {
                  const error = `Unknown fleet billing field: ${field}`;
                  toastr.error('Error', error);
                  console.error(error);
                  this.modelLocked = false;
                  return noRedirect;
                }
              }

              this.fleetBillingPostOp = {
                fields,
                printer, // for re-entrant modal replay
              };
              this.showFleetBillingPostOpModal = true;
            } else if (mainErrorMessages.startsWith('Hold')) {
              // Credit hold logic not supported on manual credits (should not happen)
              if (type === 'manualcredit') {
                console.error(mainErrorMessages);
                toastr.error('Error', 'Credit hold logic not supported on manual credits.');
                return noRedirect;
              }

              const fields = _.get(parsedResponse, 19, '').trim().split('ý');

              if (fields.length === 1) {
                toastr.error('Error', 'Invalid Credit Hold Fields');
                console.error('Invalid Credit Hold Fields');
                console.log(_.get(parsedResponse, 19, ''));
              }

              this.creditHoldPostOp = {
                actions: _.get(fields, 1, '').split('ü'),
                defaultScreenPrintEmail: _.get(fields, 2, ''),
                holdNumber: _.get(fields, 0, 'NO HOLD # GENERATED'),
                printer, // for re-entrant modal replay
              };
              this.showCreditHoldPostOpModal = true;
            } else {
              // Otherwise we have general errors, which may include fleet billing
              // errors such as authorization / credit limit errors.
              processErrors.push(...mainErrorMessages.split('ý'));
              processErrors.forEach(error => {
                toastr.error('Process Error', error);
              });
              console.error('Process Errors');
              console.log(processErrors);
            }
          }

          // Since adding SKIP to the PO field, we may never hit this (vestigial).
          if (nonStockErrorMessages) {
            nonStockErrors.push(...nonStockErrorMessages.split('ý'));
            nonStockErrors.forEach(error => {
              toastr.error('@Error', error);
            });
            console.error('@ Errors');
            console.log(nonStockErrors);
          }

          success = false;

          // No errors returned, we assume the invoice was accepted by DBPIP.
        } else {
          this.lastDocID = _.get(parsedResponse, 11, '').trim(); // invoice #, quote #, etc.

          switch (type) {
            case 'invoice':
              // Log missing invoice numbers (Jan 2022 QM bug which prevents loyalty proxynum->invoice updates, and CC issues)
              // Ben/Phil think this is primarily credit hold situations, so we'll filter those for now since that rules out
              // CC payment cases. Loyalty proxynum updates can be handled by Phil at credit release time.
              if (this.lastDocID === '' && !creditHoldPostOpFields) {
                console.error('Missing invoice number on process. Please alert DSI programming.');
                Api.post('/shopkeeper/logmissinginvoicenum', {
                  customerNum: this.activeCustomerID,
                  dbpip: res.data.dbpip,
                  dbpipReq: res.data.dbpipReq,
                });
              }

              // For CC sales, we'll hold off on this toaster until a payment session completes successfully
              if (!creditCardSale) {
                toastr.success('Success', 'Processed successfully.');
              }

              /* Loyalty redemptions are validated here regardless of if this is a credit card sale
                 or not.  If the sale is subsequently canceled by the terminal component, we will
                 unwind the sale in QM via a full invoice credit, which wil also void the loyalty
                 redemptions.
              */
              if (loyaltyRedemptionIDs.length > 0) {
                this.validateRedemptions(loyaltyRedemptionIDs, this.lastDocID);
              }
              break;
            case 'manualcredit':
              toastr.success('Success', 'Processed successfully.');
              break;
            case 'pickticket':
              if (this.lastDocID !== '') {
                toastr.success('Success', `Pick Ticket: ${this.lastDocID}`);
              } else {
                toastr.success('Success', 'Pick ticket created.');
              }

              // If the pick list was linked to an active customer, refresh linked pick lists.
              if (this.activeCustomerID) {
                this.loadCustomerPickLists();
              }

              break;
            case 'quote':
              if (this.lastDocID !== '') {
                toastr.success('Success', `Quote: ${this.lastDocID}`);
              } else {
                toastr.success('Success', 'Quote created.');
              }

              // If the quote was linked to an active customer, refresh linked quotes.
              if (this.activeCustomerID) {
                this.loadCustomerQuotes();
              }

              break;
            default:
              // should not occur
              break;
          }

          if (creditCardSale) {
            // Use QM actual total applied w / assumed total fallback
            const amount =
              parseFloat(_.get(parsedResponse, 18, '').split('ý')[2]) || parseFloat(this.totalInvoice.amount);

            // On some systems, COD sales result in a newly created customer # returned,
            // otherwise line 4 will be the same as this.activeCustomerID
            const customerNum = _.get(parsedResponse, 3, '').trim();

            const invoiceNum = this.lastDocID;

            // This is a tagged union type
            type PaymentSession =
              | { resType: 'RESUMELATER' }
              | { resType: 'CANCEL'; resData: PaymentSessionCancelResponse }
              | {
                  resType: 'SUCCESS';
                  resData: PaymentSessionSuccessResponse;
                };

            const paymentSession: PaymentSession = await new Promise(res => {
              launchPaymentSession({
                amount,
                customerNum,
                invoiceNum,
                printerId: printer!.id,
                onCancel: resData => res({ resType: 'CANCEL', resData }),
                onResumeLater: () => res({ resType: 'RESUMELATER' }),
                onSuccess: resData => res({ resType: 'SUCCESS', resData }),
                ppc: this.ppcParams.ppc,
              });
            });

            if (this.logging) {
              console.warn('Payment Session Result');
              console.log(paymentSession);
            }

            try {
              if (paymentSession.resType === 'SUCCESS') {
                this.lastCCInvoiceValidateReq = {
                  customerNum,
                  invoiceNum,
                  ppc: this.ppcParams.ppc,
                  printerID: printer!.id,
                  transactions: paymentSession.resData.transactions,
                };

                this.isProcessing = true;
                const res = await Api.post('/shopkeeper/ccinvoicevalidate', this.lastCCInvoiceValidateReq);
                this.isProcessing = false;
                this.lastCCInvoiceValidateRes = res;

                if (this.logging) {
                  console.warn('/ccinvoicevalidate Response');
                  console.log(this.lastCCInvoiceValidateRes);
                }

                if (res.status !== 200) {
                  throw new Error('200 not received from ccinvoicevalidate.');
                }

                parsedResponse = res.data.dbpip.split('\n');
                mainErrorMessages = _.get(parsedResponse, 12, '').trim();

                if (mainErrorMessages) {
                  const processErrors = [];
                  processErrors.push(...mainErrorMessages.split('ý'));
                  processErrors.forEach(error => {
                    toastr.error('Validate Invoice Error', error);
                  });
                  console.error('Validate Invoice Errors');
                  console.log(processErrors);
                  throw new Error('Legacy error observed while validating invoice.');
                } else {
                  toastr.success('Success', 'Processed successfully.');
                  // flow will continue to screen print / signature capture below

                  // We have a successful QM validation here, mark it so in the payment session
                  Api.put(`/payment/session/${paymentSession.resData.sessionId}/complete`).catch((err: any) => {
                    console.error(err);
                    toastr.error('Payment session failed to complete.');
                  });
                }

                // CANCEL or RESUMELATER
              } else {
                if (paymentSession.resType === 'CANCEL') {
                  this.lastCCInvoiceVoidReq = {
                    customerNum,
                    invoiceNum,
                    ppc: this.ppcParams.ppc,
                  };

                  if (this.logging) {
                    console.warn('/ccinvoicevoid Request:');
                    console.log(this.lastCCInvoiceVoidReq);
                  }

                  this.isProcessing = true;
                  const res = await Api.post('/shopkeeper/ccinvoicevoid', this.lastCCInvoiceVoidReq);
                  this.isProcessing = false;
                  this.lastCCInvoiceVoidRes = res;

                  if (this.logging) {
                    console.warn('/ccinvoicevoid Response');
                    console.log(this.lastCCInvoiceVoidRes);
                  }

                  if (res.status !== 200) {
                    throw new Error('200 not received from ccinvoicevoid.');
                  }

                  parsedResponse = res.data.dbpip.split('\n');
                  mainErrorMessages = _.get(parsedResponse, 12, '').trim();

                  if (mainErrorMessages) {
                    const processErrors = [];
                    processErrors.push(...mainErrorMessages.split('ý'));
                    processErrors.forEach(error => {
                      toastr.error('Cancel Invoice Error', error);
                    });
                    console.error('Cancel Invoice Errors');
                    console.log(processErrors);
                    throw new Error('Legacy error observed while canceling invoice.');
                  } else {
                    toastr.error('Canceled', 'Transaction Canceled');

                    // We have a successful QM cancelation here, mark it so in the payment session
                    Api.put(`/payment/session/${paymentSession.resData.sessionId}/cancel`).catch((err: any) => {
                      console.error(err);
                      toastr.error('Payment session failed to cancel.');
                    });
                  }

                  // otherwise nothing to do but clear the screen and reset the model
                }

                if (paymentSession.resType === 'RESUMELATER') {
                  toastr.error('Resume Later', 'Resume later from UPY5');
                  // otherwise nothing to do but clear the screen and reset the model
                }

                this.modelLocked = false;
                return {
                  success: true,
                  redirect: '',
                };
              }

              // In the case of an API error when VALIDATING or CANCELING an invoice, the user will be directed to UPY5 to try again.
            } catch (err: any) {
              console.error(err.message);
              toastr.error(
                'Error',
                `API error while ${
                  paymentSession.resType === 'SUCCESS' ? 'applying payment to invoice' : 'canceling sale'
                }. Try again from UPY5.`
              );
              this.isProcessing = false;
              this.modelLocked = false;
              return {
                success: true,
                redirect: '',
              };
            }
          }

          // If this was a screen print, pop tabs of the returned documents.
          // This will include a link for signature capture if set on this system.
          if (printer && printer.name === 'Screen') {
            // Phil's response is IDýDOCTITLEýURLýDOCTITLEýURL...
            const docInfo = _.get(parsedResponse, 10, '').trim().split('ý').slice(1);

            if (docInfo.length > 0) {
              this.screenPrintDocs = []; // clear previous last screen print save
              for (let i = 0; i < docInfo.length; i += 2) {
                this.screenPrintDocs.push({
                  title: docInfo[i],
                  url: docInfo[i + 1],
                });
              }

              if (this.logging) {
                console.info('Screen Print Information');
                console.log(this.screenPrintDocs);
              }

              // A manual credit is considered a "credit invoice" and should be signable.
              this.screenPrintIsInvoice = ['invoice', 'manualcredit'].includes(type);
              this.showScreenPrintModal = true;
            } else {
              console.error('Error', 'No document URLs returned.');
            }

            // otherwise not a screen print - check for sig capture follow up
          } else if (['invoice', 'manualcredit'].includes(type) && this.sysParams.signatureCapture) {
            if (this.lastDocID) {
              modal.confirm({
                title: 'Signature Capture',
                message: 'Capture a signature for this invoice?',
                callback: () => {
                  window.open(`${window.location.origin}/UCS1/${this.lastDocID}`, '_blank');
                },
              });
            }
          }

          success = true;
        }
      } catch (err: any) {
        console.error(err.message);
        success = false;
        toastr.error('Error', 'Unable to process.  Try again in a moment.');

        // Clear auth on error in case manager walked away.
        if (type === 'manualcredit') {
          this.lastManualCreditAuth = null;
        }
      }

      this.isProcessing = false;
      this.modelLocked = false;

      return {
        success,
        redirect: '',
      };
    }

    // otherwise, nothing to process
    return noRedirect;
  }

  async processBackorder(boReq: IBackorderSubmission, hideBoModal: () => void): Promise<Boolean> {
    // Cache lockoutChanges setting and disable since this routine relies on possibly disabled functions
    const lockoutChangesSave = this.lockoutChanges;
    this.lockoutChanges = false;

    // These should flow through to the confirmation as well for easy replay later (CRTS)
    if (this.shipToAccount) {
      boReq.varianceShipToAccount = this.shipToAccount.customerID;
    } else if (this.shipToAddress) {
      boReq.varianceShipToAddress = this.shipToAddress;
    }

    try {
      this.lastBackorderProcessReq = boReq;
      const res: IApiResponse = await Api.post('/shopkeeper/boprocess', boReq);
      this.lastBackorderProcessRes = res;

      if (this.logging) {
        console.warn('Process Backorder Response:');
        console.log(this.lastBackorderProcessRes);
      }

      if (res.status !== 200) {
        throw new Error('200 not received from backorderprocess.');
      }

      // { dbpip, dbpipReq } DBPIP response/request dialog files
      const dbpip = res.data.dbpip.split('\n');

      const confirmationNumber: string = _.get(dbpip, 60, '').trim();
      const dbpipError: string = _.get(dbpip, 12, '');

      // We hit a case in test where the confirmation was created although errors were also returned.
      // These cases need to be weeded out by Phil should they occur again. Errors should imply no confirmation.
      if (confirmationNumber && dbpipError) {
        console.error('Confirmation Number returned with DBPIP errors.');
        console.error(`Confirmation: ${confirmationNumber} Errors: ${dbpipError}`);
        throw new Error('Confirmation created with errors. Please contact DSI (see console).');
      }

      if (dbpipError) {
        // Legacy unexpectedly returns multiple errors sometimes.
        const errors = dbpipError.split('ý');
        console.error(errors); // expand on console
        throw new Error(errors[0]); // show the first error (there SHOULD only be 1, but legacy)
      }

      if (confirmationNumber === '') {
        throw new Error('No confirmation number (nor error) returned.');
      }

      // Extract PO numbers from the response
      const poNumbers: string[] = [];

      switch (boReq.mode) {
        case 'DAILY':
          // The "Prebill" PO is the first position of the DAILY order parameters
          const poPrebillCandidate = _.get(dbpip, 70, '').split('ý')[0].trim();
          if (poPrebillCandidate !== '') {
            poNumbers.push(poPrebillCandidate);
          }
          break;
        case 'NOW':
          // First position of each PO header, per the spec
          for (let i = 100; i < dbpip.length; i++) {
            if (dbpip[i] === '>>>POSTART<<<') {
              const poNumberCandidate = _.get(dbpip, i + 1, '')
                .split('ý')[0]
                .trim();
              if (poNumberCandidate !== '') {
                poNumbers.push(poNumberCandidate);
              }
            }
          }
          break;
        default:
          break;
      }

      // Save last created Confirmation / PO # data for print routines
      this.lastBackorderData = { boType: boReq.mode, confirmationNumber, poNumbers };

      // See if this backorder process created a new customer (COD/Cash Sales at some clients)
      const customerID = _.get(dbpip, 3, '').trim();
      if (customerID !== '' && customerID !== this.activeCustomerID) {
        if (this.logging) {
          console.warn(`New customer ID returned. CURRENT:${this.activeCustomerID} NEW:${customerID}`);
        }

        toastr.success('New Customer Created', `CUST #: ${customerID}`, { autoClose: false });

        /* The COD account on screen will NOT change, as it would both jolt the user, and cause all
           line items to be re-evaluated.  We only care that any ex-post actions such as invoicing qty
           on hand be attributed to the newly created customer account.  This should support that.
           This also sets the backorder confirmation data (status) to the new account's data to prevent
           selling off general COD account confirmations under the newly created account number
           (which, in theory should not happen if new customers are always created on COD sales).
        */
        this.activeCustomerID = customerID;
        this.loyaltyInvoiceNumber = null;

        // In theory, if the auto-customer creation feature is turned on, no confirmations
        // should be tied to the COD accounts anyways.
        this.confirmationPartsAddedToInvoice.clear();
      }

      // We're in a "success" state here, so close the backorder modal
      hideBoModal();

      // Fire up the printer selector routine while ex-post actions run in the background
      this.showBackorderPrinterSelectionModal = true;

      this.modelLocked = true;

      // Used to uniquely match prebills to confirmation status detail items ex-post (which use all strings (QM))
      const prebillParts: Array<{
        partNum: string;
        poNumber: string;
        qty: string;
      }> = [];

      // Update quantities on "Invoice on Hand" lines in place
      // Additionally, take note of "prebill" (pre-sell) items during po walk (if any)
      let invoiceOnHandExists = false;
      const maxPO = boReq.purchaseOrders.length;
      for (let poIndex = 0; poIndex < maxPO; poIndex++) {
        const po = boReq.purchaseOrders[poIndex];
        for (const part of po.parts) {
          // typed union discriminator
          if (!('poComment' in part)) {
            if (part.boAction === 'INVOICE_ON_HAND') {
              invoiceOnHandExists = true;

              if (part.supercedenceEscalationFactor) {
                // Maintain the supercedence trail if at all possible (factor evenly divides on hand qty)
                if (part.oh % part.supercedenceEscalationFactor === 0) {
                  await this.editLineItemQuantity(
                    part.invoiceIndex,
                    part.oh / part.supercedenceEscalationFactor,
                    false
                  );
                } else {
                  // decompose the constituent on non-divisible quantities in place then invoice what you have
                  // we also temporarily disable combining duplicate parts with other existing lines (edge case)
                  const x = this.sysParams.combineDuplicateParts;
                  this.sysParams.combineDuplicateParts = false;

                  const constitutentPartNum = this.lineItems[part.invoiceIndex].supercedenceDetails![0].partNum;
                  await this.editLineItemPartNum(part.invoiceIndex, constitutentPartNum, part.oh);

                  this.sysParams.combineDuplicateParts = x;
                }
              } else {
                await this.editLineItemQuantity(part.invoiceIndex, part.oh, false);
              }

              // inject informational "bo" column on the line (print display purposes only)
              this.lineItems[part.invoiceIndex].backorderedQuantity = part.req - part.oh;
            }

            // Track prebill parts to pair with ex-post confirmation result based on these match details
            if (part.prebill) {
              prebillParts.push({
                partNum: part.partNum,
                poNumber: poNumbers[poIndex],
                qty: (part.boAction === 'INVOICE_ON_HAND' ? part.req - part.oh : part.req).toString(),
              });
            }
          }
        }
      }

      /* If we're invoicing something on hand, apply the backorder customer PO to the main
         invoice screen if it is not already within the PO # input.  Pre-billing lines will do
         this automatically (checking for dupes) via addConfirmationPartLineItemsFromBOStatus()
      */
      const boCustomerPO = boReq.confirmationDetail.customerPO;
      if (boCustomerPO !== '' && invoiceOnHandExists) {
        if (this.po === null) {
          this.po = boCustomerPO;
          this.uiBridge.poInputRef.value = this.po;
        } else {
          const invoicePOs = this.po.split(' ');
          if (!invoicePOs.includes(boCustomerPO)) {
            invoicePOs.push(boCustomerPO);
            this.suspendPORules = true;
            this.po = invoicePOs.join(' ');
            this.uiBridge.poInputRef.value = this.po;
          }
        }
      }

      // Remove part lines marked as Order Full Qty / Hold on Hand
      const partsToRemove = [];
      for (const po of boReq.purchaseOrders) {
        for (const part of po.parts) {
          if (!('poComment' in part) && (part.boAction === 'HOLD_ON_HAND' || part.boAction === 'ORDER_FULL_QUANTITY')) {
            partsToRemove.push(part.invoiceIndex);
          }
        }
      }
      // Sort the removal indexes to make the execution simple
      // (These may have been placed on POs in completely random order.)
      partsToRemove.sort((a, b) => a - b);

      let linesRemoved = 0;
      for (const partIndex of partsToRemove) {
        this.removeLineItem(partIndex - linesRemoved, false);
        linesRemoved++;
      }

      // loadConfirmationStatusDetail will refresh confirmation status to include this confirmation

      // Inject prebill lines once you have refreshed confirmation data
      if (prebillParts.length > 0) {
        await this.loadConfirmationStatusDetail(); // need refreshed data to process prebills
        const partsOnBackorder = this.confirmationDetail.partsOnBackorder;

        if (this.confirmationDetailLazyLoaded && partsOnBackorder.length > 0) {
          const partsToAdd: Array<IPartOnBackorder & { action: 'P' }> = [];
          const dupeCheck = new Set<string>();

          for (const prebillPart of prebillParts) {
            // Consider the unlikely (but possible) scenario where you have duplicate prebill part detail po lines
            const confirmationPartMatch = _.find(partsOnBackorder, o => {
              const partNumOnBackorder = o.part.toUpperCase();
              const partNumPrebill = prebillPart.partNum.toUpperCase();
              return (
                (partNumOnBackorder === partNumPrebill ||
                  (o.nonStocking && partNumOnBackorder === `X:${partNumPrebill}`)) &&
                o.poNumber === prebillPart.poNumber &&
                o.qty === prebillPart.qty &&
                !dupeCheck.has(o.uniqueID)
              );
            });

            if (confirmationPartMatch) {
              dupeCheck.add(confirmationPartMatch.uniqueID);
              partsToAdd.push({ ...confirmationPartMatch, action: 'P' });
            } else {
              const error = 'Unable to prebill: part not found in backorder status';
              console.error(error);
              console.log(prebillPart);
              toastr.error('Error', error);
            }
          }

          if (partsToAdd.length > 0) {
            this.addLineItemComment('-=-=-=- PRESELL B/O PARTS -=-=-=-');
            await this.addConfirmationPartLineItemsFromBOStatus(partsToAdd);
          }
        } else {
          const error = 'Unable to prebill: confirmation status unavailable or returned no parts on backorder';
          console.error(error);
          toastr.error('Error', error);
        }
      }

      if (this.runMode === 'PIN1') {
        /* If all that's left on our screen (unissued in PRO1) is comments, those comments were likely added to a backorder
         and there's nothing left on the screen to give the comments context (no remaining parts to be sold or issued).
         In this case, it makes sense to go ahead and clear the line items entirely rather than leave the comment lines
         lingering.
      */
        const onlyCommentsRemaining = this.lineItems.every(lineItem => lineItem.partNum === 'LINEITEM_COMMENT');
        if (onlyCommentsRemaining) {
          this.removeAllLineItems();
        } else {
          // calculate new taxes for revised line items
          await this.updateTaxes();
        }

        // Used for customers that want B/O info printed on invoices
        this.confirmationsCreatedThisSession.push(confirmationNumber);
      }

      // important to release the lock here before the PRO1/SMM3 check
      this.modelLocked = false;

      if (['PRO1', 'SMM3', 'SRE2'].includes(this.runMode)) {
        const onlyCommentsRemaining = this.lineItems.every(
          lineItem => lineItem.issuedToRO || lineItem.partNum === 'LINEITEM_COMMENT'
        );
        if (onlyCommentsRemaining) {
          this.refreshActiveROData({ retainIssueQueue: 'NONE' });
        }
      }

      // If we didn't refresh confirmation data due to a prebill, do it now (async lazy load is fine)
      if (prebillParts.length === 0) {
        this.loadConfirmationStatusDetail();
      }

      this.lockoutChanges = lockoutChangesSave;
      return true;
    } catch (err: any) {
      console.error(err.message);
      toastr.error('Error', err.message);

      this.lockoutChanges = lockoutChangesSave;
      return false;
    }
  }

  async processCreditInvoice(printer: { id: string; name: string } | null = null) {
    // PPC Auth required means the clerk requires an additional PW, or we need a manager override
    if (this.creditPPCAuthRequired) {
      const creditPPC = this.lastCreditInvoiceState.creditPPC;
      const creditPPCPassword = this.lastCreditInvoiceState.creditPPCPassword;

      // Credit modal should enforce this
      if (!!creditPPC && !!creditPPCPassword) {
        const validated = await this.validatePPCCreditAuth(creditPPC, creditPPCPassword);
        if (!validated) {
          // validation routine will toast specific errors
          return;
        }
      } else {
        toastr.error('Error', 'PPC Credit Auth Details Required');
        return;
      }
    }

    if (!printer) {
      // Modal will call back with printer info
      this.showCreditInvoicePrinterSelectionModal = true;
    } else {
      this.isCreditInvoiceProcessing = true;

      // Process credit
      const dbpipReq = {
        customerID: this.activeCustomerID,
        ppc: this.ppcParams.ppc,
        printer: printer.id,
        ...this.lastCreditInvoiceState,
      };

      const creditCardCredit = this.lastCreditInvoiceState.creditCardCredit;

      try {
        if (this.logging) {
          console.warn('/processcreditinvoice Request');
          console.log(dbpipReq);
        }

        const res = await Api.post('/shopkeeper/processcreditinvoice', dbpipReq);
        if (res.status !== 200) {
          throw new Error('200 not received from processcreditinvoice');
        }

        if (this.logging) {
          console.warn('/processcreditinvoice Response');
          console.log(res.data);
        }

        let dbpipRows = _.get(res, 'data.dbpip', '').split('\n');

        // check for errors
        if (_.get(dbpipRows, 12, '') !== '') {
          const errors = dbpipRows[12].split('ý');
          console.log('Process Credit Legacy Errors:');
          console.log(errors);
          throw new Error(errors[0]);
        }

        this.isCreditInvoiceProcessing = false;

        if (!creditCardCredit) {
          toastr.success('Success', 'Credit processed successfully.');
          this.showCreditInvoiceModal = false;
          this.loadCustomerHistory(); // refresh history to pick up just issued credits
        }

        this.lastDocID = _.get(dbpipRows, 11, '').trim(); // new credit invoice #

        if (creditCardCredit) {
          // Use QM actual total credit applied (no fallback to presume since we don't have tax calc)
          const amount = parseFloat(_.get(dbpipRows, 18, '').split('ý')[2]);
          const customerNum = this.activeCustomerID as string;
          const creditInvoiceNum = this.lastDocID;
          const originalInvoiceNum = this.lastCreditInvoiceState.invoiceNum;

          // This is a tagged union type
          type CreditSession =
            | { resType: 'CANCEL'; resData: PaymentSessionCancelResponse }
            | { resType: 'RESUMELATER' }
            | {
                resType: 'SUCCESS';
                resData: PaymentSessionSuccessResponse;
              };

          const creditSession: CreditSession = await new Promise(res => {
            launchCreditSession({
              amount,
              customerNum,
              creditInvoiceNum,
              originalInvoiceNum,
              onCancel: resData => res({ resType: 'CANCEL', resData }), // unused - cancel may be possible if no cc refunds processed
              onResumeLater: () => res({ resType: 'RESUMELATER' }),
              onSuccess: resData => res({ resType: 'SUCCESS', resData }),
              ppc: this.ppcParams.ppc,
              printerId: printer!.id,
            });
          });

          if (this.logging) {
            console.warn('Credit Session Debug');
            console.log(creditSession);
          }

          try {
            if (creditSession.resType === 'SUCCESS') {
              this.lastCCInvoiceValidateReq = {
                customerNum,
                invoiceNum: creditInvoiceNum,
                ppc: this.ppcParams.ppc,
                printerID: printer!.id,
                transactions: creditSession.resData.transactions,
              };

              this.isCreditInvoiceProcessing = true;
              const res = await Api.post('/shopkeeper/ccinvoicevalidate', this.lastCCInvoiceValidateReq);
              this.isCreditInvoiceProcessing = false;

              this.lastCCInvoiceValidateRes = res;

              if (this.logging) {
                console.warn('/ccinvoicevalidate Response');
                console.log(this.lastCCInvoiceValidateRes);
              }

              if (res.status !== 200) {
                throw new Error('200 not received from ccinvoicevoid.');
              }

              dbpipRows = res.data.dbpip.split('\n');
              const mainErrorMessages = _.get(dbpipRows, 12, '').trim();

              if (mainErrorMessages) {
                const processErrors = [];
                processErrors.push(...mainErrorMessages.split('ý'));
                processErrors.forEach(error => {
                  toastr.error('Validate Invoice Error', error);
                });
                console.error('Validate Invoice Errors');
                console.log(processErrors);
                throw new Error('Legacy error observed while validating invoice.');
              } else {
                // flow will continue to screen print / signature capture below

                // We have a successful QM validation here, mark it so in the payment session
                Api.put(`/payment/session/${creditSession.resData.sessionId}/complete`).catch((err: any) => {
                  console.error(err);
                  toastr.error('Credit payment session failed to complete.');
                });
              }
              // Currently the only other resType possibility is CANCEL
            } else {
              // TODO: resume CCPENDING invoice with QM? should we supoprt Resume Later here?
              toastr.error('Important', `Complete or void credit invoice ${creditInvoiceNum} from UPY6.`);
              this.showCreditInvoiceModal = false;
              this.isCreditInvoiceProcessing = false;
              this.loadCustomerHistory(); // forces reload of pending cc credits data
              return;
            }
          } catch (err: any) {
            console.error(err.message);
            toastr.error('Error', 'API error while validating credit invoice. Try again from UPY6.');
            this.showCreditInvoiceModal = false;
            this.isCreditInvoiceProcessing = false;
            this.loadCustomerHistory();
            return;
          }

          toastr.success('Success', 'Processed Successfully.');
          this.showCreditInvoiceModal = false;
          this.isCreditInvoiceProcessing = false;
          this.loadCustomerHistory(); // refresh history to pick up just issued credits
        }

        // If this was a screen print, pop tabs of the returned documents.
        // This will include a link for signature capture if set on this system.
        if (printer.name === 'Screen') {
          // Phil's response is IDýDOCTITLEýURLýDOCTITLEýURL...
          const docInfo = _.get(dbpipRows, 10, '').trim().split('ý').slice(1);

          if (docInfo.length > 0) {
            this.screenPrintDocs = []; // clear previous last screen print save
            for (let i = 0; i < docInfo.length; i += 2) {
              this.screenPrintDocs.push({
                title: docInfo[i],
                url: docInfo[i + 1],
              });
            }

            if (this.logging) {
              console.info('Screen Print Information');
              console.log(this.screenPrintDocs);
            }

            this.screenPrintIsInvoice = true; // flag used for sign capture
            this.showScreenPrintModal = true;
          } else {
            console.error('Error', 'No document URLs returned.');
          }

          // otherwise not a screen print - check for sig capture follow up
        } else if (this.sysParams.signatureCapture) {
          if (this.lastDocID) {
            modal.confirm({
              title: 'Signature Capture',
              message: 'Capture a signature for this invoice?',
              callback: () => {
                window.open(`${window.location.origin}/UCS1/${this.lastDocID}`, '_blank');
              },
            });
          }
        }
      } catch (err: any) {
        this.isCreditInvoiceProcessing = false;
        this.loadCustomerHistory(); // refresh history to pick up possible CCPENDING in progress

        console.warn(err.message);
        toastr.error('Error', err.message);
      }
    }
  }

  /* Process any offer that hasn't been redeemed.
     Launches PACCAR Loyalty process modal for process status, cancelability, retry redemptions, etc.
  */
  async processLoyaltyRedemptions() {
    /* Create unique symbol for each batch process request
       Modal cancel/escape cancels the symbol via processLoyaltyRedemptionsCancel() in case the
       request ever comes back later (i.e. don't bind redemptions after a cancel request).
       Modal tracks observable "loyaltyProcessStatus" for UX feedback / ex post actions
    */
    this.loyaltyProcessStatus = 'PROCESSING';
    this.showProcessLoyaltyRedemptionsModal = true;

    /* API call to redeem offers

       Assign unique API call promise symbol per closure.  It's possible the API call can hang or take longer than
       the clerk wishes to wait.  If the closure symbol is cancelled, throw away the response and do nothing
       (UI would have already entered "ERROR" state for possible try again selection, etc.).

       If 1:1 success (at least 1 redemption per offer indicates 1:1 in this case), re-entrant process()/processRO() call
       (via UI bridge in the PIN1 case) w/ redemption flags sent to QM.

       Upon any partial redemptions (qty req < qty redeemed) (still under the 1:1 success),
       updates taxes in sync first, then process()/processRO() call (via UI bridge in the PIN1 case).

       Any completely missing redemptions sets observable status to 'ERROR' mode allowing try again, remove and process,
       modal close, etc..
    */
    try {
      const invoiceNumProxy = await this.getLoyaltyInvoiceNumber(); // cached per invoice / issue
      const promiseID = Symbol(Math.floor(Math.random() * 1000000));
      this.processLoyaltyRedemptionsPromiseIDs.set(promiseID, true);

      // We only care about previously unredeemed offers (this is the 99% default case anyways)
      // This data structure caches some stuff to make the ex-post model binding walk faster/simpler
      const lineItemOffersTrackingCache: Array<{
        lineItemIndex: number;
        lineItemOfferIndex: number; // potential for multiple offers per line
        qty: number; // each gets a different redemption request
        redeemed: number;
        CouponCode: string;
        OfferUniqueId: string;
        PartNumber: string; // leading division stripped
      }> = [];

      for (let i = 0; i < this.lineItems.length; i++) {
        const lineItem = this.lineItems[i];

        // Any loyalty offers on this line item? (i)
        if (Array.isArray(lineItem.loyaltyOffers) && lineItem.loyaltyOffers.length > 0) {
          for (let j = 0; j < lineItem.loyaltyOffers.length; j++) {
            const offer = lineItem.loyaltyOffers[j];

            // Has this offer been redeemed?  If not, attempt to do so now.
            if (!offer.Redemptions) {
              lineItemOffersTrackingCache.push({
                lineItemIndex: i,
                lineItemOfferIndex: j,
                qty: lineItem.quantity,
                redeemed: 0,
                CouponCode: offer.CouponCode,
                OfferUniqueId: offer.OfferUniqueId,
                PartNumber: lineItem.partNum.slice(2), // PACCAR doesn't need leading dealer divisions
              });
            }
          }
        }
      }

      // API redemption request data structure
      const redemptionCandidates: Array<{
        InvoiceNumber: string;
        Void: boolean; // always false here
        CouponCode: string;
        OfferUniqueId: string;
        PartNumber: string;
      }> = [];

      // PACCAR expects a separate redemption request when qty of a line item > 1
      for (const offer of lineItemOffersTrackingCache) {
        for (let i = 0; i < offer.qty; i++) {
          redemptionCandidates.push({
            InvoiceNumber: invoiceNumProxy,
            Void: false,
            CouponCode: offer.CouponCode,
            OfferUniqueId: offer.OfferUniqueId,
            PartNumber: offer.PartNumber,
          });
        }
      }

      // Helpful debugging information
      // Note you can enable this.loyaltyLogging as well for coupon checks (it's noisy though)
      if (this.logging) {
        console.warn('LineItem Offers (tracking cache)');
        console.log(lineItemOffersTrackingCache);
        console.warn('Redemption Candidates (sent to API)');
        console.log(redemptionCandidates);
      }

      // Unfortunately errors from PACCAR are swallowed by the API service, which throws
      // You can however check the network tab for the response body
      const res = await Api.post(`loyalty/customers/${this.activeLoyaltyCustomerID}/redemptions`, redemptionCandidates);

      if (this.logging) {
        console.warn('PACCAR Loyalty Redemption Response');
        console.log(res.data);
      }

      if (this.processLoyaltyRedemptionsPromiseIDs.has(promiseID)) {
        const redemptions = _.get(res, 'data.Redemptions', []) as IRedemption[];
        if (!Array.isArray(redemptions)) {
          throw new Error('Redemptions response is not an array.');
        }

        Api.post(`loyalty/customers/${this.activeLoyaltyCustomerID}/link`, {
          ...this.loyaltyCustomerDetails,
          customerNum: this.activeCustomerID,
        });

        // Pair each redemption to a line item
        runInAction(() => {
          // All we care about here is UniqueId & OfferUniqueId
          for (const redemption of redemptions) {
            const UniqueId = redemption.UniqueId;
            const OfferUniqueId = redemption.OfferUniqueId;

            if (!!UniqueId && !!OfferUniqueId) {
              // Find the first line item in the tracking cache this offer satisfies
              for (let i = 0; i < lineItemOffersTrackingCache.length; i++) {
                const x = lineItemOffersTrackingCache[i];

                // On clients who do not combine parts, the potential exists for the same offer to apply
                // to multiple part lines (in multiple qty). Redemptions are applied first come first served.
                if (x.OfferUniqueId === OfferUniqueId && x.redeemed < x.qty) {
                  const lineItemOffer = this.lineItems[x.lineItemIndex].loyaltyOffers![x.lineItemOfferIndex];
                  if (lineItemOffer.Redemptions) {
                    lineItemOffer.Redemptions.push(redemption);
                  } else {
                    lineItemOffer.Redemptions = [redemption];
                  }
                  x.redeemed++;
                  break; // break tracking cache search, proceed to next redemption
                }
              }

              // Wack redemption result, log it for debugging
            } else {
              if (this.logging) {
                console.warn('Invalid Redemption Response Detected');
                console.log(`UniqueId: ${UniqueId}`);
                console.log(`OfferUniqueId: ${OfferUniqueId}`);
              }
            }
          }
        });

        // Was any offer left fully unredeemed? (partial redemptions are accepted as ok (possible maxRedemptions enforcement))
        // If so, error state for try again, remove and proceed, etc.
        if (lineItemOffersTrackingCache.some(o => o.redeemed === 0)) {
          this.loyaltyProcessStatus = 'ERROR';

          // Otherwise proceed to process invoice, with a possible edgecase of partial redemption tax re-calculation
        } else {
          this.showProcessLoyaltyRedemptionsModal = false;

          // did we have any partial redemptions?  line items / subtotal will auto update,
          // tax calc update is always explicit
          if (lineItemOffersTrackingCache.some(o => o.redeemed < o.qty)) {
            this.isProcessing = true;
            await this.updateTaxes();
            this.isProcessing = false;
          }

          if (this.runMode === 'PIN1') {
            // jump through UI bridge to emulate the user manually processing the invoice / issuing
            // (for proper ex-post UI actions (reset, etc.), see ShopKeeper.tsx:process()
            this.uiBridge.process('invoice');
          } else {
            this.processRO({ type: this.lastProcessROType });
          }
        }
      } else {
        if (this.logging) {
          console.warn(`Pending Redemptions Call (${promiseID.toString()}) Cancelled`);
          // discard response, any redemptions which might show up here will be auto-voided later
        }
      }
    } catch (e) {
      console.log(e);
      this.loyaltyProcessStatus = 'ERROR'; // try again, etc.
    }
  }

  // Cancel any pending/hung request (hung PACCAR API, clerk loses patience, etc.).
  // IF redemptions eventually come back, they will logically never be validated and will eventually auto-void.
  processLoyaltyRedemptionsCancel() {
    this.processLoyaltyRedemptionsPromiseIDs.clear();
    this.loyaltyProcessStatus = 'ERROR'; // show options: retry, remove and process, back to invoice
  }

  // If PTB2 Prompt for Fuel is Y/F, show the fuel control.  Unlike freight, this control is hidden when N.
  // Individual customers can be configured to skip this feature if fuel is never needed.
  @computed
  get promptForFuel() {
    return (
      this.runMode === 'PIN1' &&
      ['Y', 'F'].includes(this.sysParams.promptForFuel) &&
      this.activeCustomerID &&
      !this.customerParams.skipFuelPrompt
    );
  }

  // Explicit record lost sale request from parts person.
  // We only care about recording lost stocking parts here.
  async recordLostSale(parts: Array<{ partNum: string; qty: number }>, reasonCode: string) {
    if (!this.ppcValidated) {
      toastr.error('Error', 'A validated PPC must be entered.');
      return false;
    }

    if (parts.length > 0) {
      try {
        const res = await Api.post('/shopkeeper/recordlostsale', {
          ppc: this.ppcParams.ppc,
          isRO: this.runMode === 'PRO1', // not supported in SRE2/SMM3 modes
          customerID: this.activeCustomerID || '', // PIN1
          reasonCode,
          roNum: this.activeRONumber || '', // PRO1
          parts,
        });

        if (this.logging) {
          console.warn('/recordlostsale Response');
          console.log(res);
        }

        const parsedResponse = res.data.dbpip.split('\n');
        const mainErrorMessages = _.get(parsedResponse, 12, '').trim();

        if (mainErrorMessages) {
          const processErrors: string[] = mainErrorMessages.split('ý');
          processErrors.forEach(error => {
            toastr.error('Process Error', error);
          });
          console.error('Process Errors');
          console.log(processErrors);
        } else {
          toastr.success('Saved', 'Lost sale recorded.');
        }
      } catch (e) {
        toastr.error('Error', 'Unable to record lost sale.  Try again.');
      }
    } else {
      toastr.error('Error', 'No parts to record.');
    }
  }

  // Processes 1 or more standalone POs.  Used by PRO1/SMM3 for sublets/unpriced parts.
  // Beware if this is ever used in PIN1 (outstanding issue), you'll need to pay attention to disable lockoutChanges
  async processStandalonePO(poReq: IStandalonePOSubmission, hideSubletUnpricedPOModal: () => void): Promise<Boolean> {
    const isPSO3 = ['PSO3'].includes(this.runMode);

    if (isPSO3) {
      poReq.isPSO3 = true;
    }

    try {
      this.lastStandalonePOProcessReq = poReq;
      const res: IApiResponse = await Api.post('/shopkeeper/standalonepoprocess', poReq);
      this.lastStandalonePOProcessRes = res;

      if (this.logging) {
        console.warn('Process Standalone POs Response:');
        console.log(this.lastStandalonePOProcessRes);
      }

      if (res.status !== 200) {
        throw new Error('200 not received from standalonepoprocess.');
      }

      // { dbpip, dbpipReq } DBPIP response/request dialog files
      const dbpip = res.data.dbpip.split('\n');
      const dbpipError: string = _.get(dbpip, 12, '');

      if (dbpipError) {
        // Legacy unexpectedly returns multiple errors sometimes.
        const errors = dbpipError.split('ý');
        console.error(errors); // expand on console
        throw new Error(errors[0]); // show the first error (there SHOULD only be 1, but legacy)
      }

      // Extract PO numbers from the response for printing
      const poNumbers: string[] = [];

      // First position of each PO header, per the spec
      for (let i = 100; i < dbpip.length; i++) {
        if (dbpip[i] === '>>>POSTART<<<') {
          const poNumberCandidate = _.get(dbpip, i + 1, '')
            .split('ý')[0]
            .trim();
          if (poNumberCandidate !== '') {
            poNumbers.push(poNumberCandidate);
          }
        }
      }

      // Save PO# data for print routines
      this.lastStandalonePOData = { poNumbers };

      // We're in a "success" state here, so close the sublet/unpriced PO modal
      hideSubletUnpricedPOModal();

      if (poNumbers.length === 0) {
        console.error('No purchase order #s received from QM.  Alert DSI programming.');
      }

      // Fire up the printer selector routine while ex-post actions run in the background
      this.showStandalonePOPrinterSelectionModal = true;

      if (isPSO3) {
        // Clear the line items, ready for next PO (no need to re-enter PPC)
        this.removeAllLineItems();

        // PRO1/SMM3
      } else {
        // Remove sublet/unpriced items we just issued (via PO) from the queue (and comments),
        // save the remaining pre-issue queue, then refresh.
        const partsToRemove = [];
        for (const po of poReq.purchaseOrders) {
          for (const part of po.parts) {
            if (!('poComment' in part)) {
              partsToRemove.push(part.invoiceIndex);
            }
          }
        }
        // Sort the removal indexes to make the execution simple
        // (These may have been placed on POs in completely random order.)
        partsToRemove.sort((a, b) => a - b);

        let linesRemoved = 0;
        for (const partIndex of partsToRemove) {
          this.removeLineItem(partIndex - linesRemoved, false);
          linesRemoved++;
        }

        const onlyCommentsRemaining = this.lineItems.every(
          lineItem => lineItem.issuedToRO || lineItem.partNum === 'LINEITEM_COMMENT'
        );

        if (onlyCommentsRemaining) {
          this.refreshActiveROData({ retainIssueQueue: 'NONE' });
        } else {
          // Cache the pending issue queue, refresh the RO, then restore the queue
          this.refreshActiveROData({ retainIssueQueue: 'ALLOPS' });
        }
      }

      return true;
    } catch (err: any) {
      console.error(err.message);
      toastr.error('Error', err.message);
      return false;
    }
  }

  // PRO1 Issue / Decsiv Issue / Picklist - very similar to PIN1 invoice process
  async processRO({
    printer = null,
    type,
  }: {
    printer?: { id: string; name: string } | null;
    type: 'issue' | 'decisivissue' | 'negativeissue' | 'pickticket' | 'decisivpickticket';
  }) {
    if (!this.ppcValidated) {
      toastr.error('Error', 'A validated PPC must be entered.');
      return;
    }

    if (!['pickticket'].includes(type) && !this.activeRONumber) {
      toastr.error('Error', 'A repair order must be loaded.');
      return;
    }

    if (this.isROProcessing || this.isDecisivROProcessing) {
      toastr.error('Wait', 'Processing.  Please wait.');
      return;
    }

    if (this.modelLocked) {
      toastr.error('Wait', 'Action in process. Please try again in a moment.');
      return;
    }

    if (this.lineItemsQueued.length > 0) {
      toastr.error('Wait', 'Queued items pending fetch. Please try again in a moment.');
      return;
    }

    for (const lineItem of this.lineItems) {
      if (!lineItem.fetched) {
        toastr.error('Wait', 'Fetch action in process. Please try again in a moment.');
        return;
      }
    }

    // Make sure all line items requiring serial numbers match on quantity requested
    if (['decisivissue', 'issue'].includes(type)) {
      for (const lineItem of this.lineItems) {
        if (
          !lineItem.issuedToRO &&
          lineItem.detail &&
          lineItem.detail.serialNumberRequired &&
          (!lineItem.serialNumbersSelected || lineItem.serialNumbersSelected.length !== lineItem.quantity)
        ) {
          toastr.error('Error', 'Serial numbers required.  Please inspect line items.');
          return;
        }
      }
    }

    // decisiv checks
    if (['decisivissue', 'decisivpickticket'].includes(type)) {
      // UI currently prevents this from occurring
      if (this.activeOperation === 0) {
        toastr.error('Error', 'Decisiv functions work per operation only.  Please select an operation.');
        return;
      }

      // Make sure this operation exists in the decisiv case as well.
      // It may have been added to DSI ex-post.
      if (!_.get(this.activeRepairOrderDetails, `operationDetails.${this.activeOperation}.isDecisivOperation`, false)) {
        toastr.error('Error', 'Operation does not exist within the Decisiv case.');
        return;
      }
    }

    if (type === 'pickticket' && !printer) {
      if (this.sysParams.roPickTicketPrinters.length > 0) {
        if (this.sysParams.roPickTicketPrinters.length === 1) {
          printer = this.sysParams.roPickTicketPrinters[0];
          // otherwise, pop modal for printer selection
        } else {
          this.showPickTicketPrinterSelectionModal = true;
          return;
        }
      } else {
        // no "default" RO pick ticket printer exists, must be defined in QM setup
        toastr.error('Error', 'No RO Pick Ticket Printers Defined.');
        return;
      }
    }

    // If there's any SUBL/SUBP/Unpriced parts not yet issued via the standalone PO route, warn the clerk.
    if (this.subletOrUnpricedItemPendingIssue && this.runMode !== 'SRE2' && !this.isEstimate) {
      const message =
        this.subletOrUnpricedItemPendingIssue === 'Unpriced'
          ? "You're about to issue an unpriced item without first generating a purchase order.  Are you sure?"
          : `You're about to issue ${this.subletOrUnpricedItemPendingIssue} or an ATB3 PO part without first generating a purchase order.  Are you sure?`;

      const continueWithoutStandalonePOs: boolean = await new Promise(resolve => {
        modal.confirm({
          title: 'Are you sure?',
          message,
          confirmLabels: ['Yes', 'No'],
          callback: () => resolve(true),
          onClose: () => resolve(false),
        });
      });

      if (!continueWithoutStandalonePOs) {
        this.uiBridge.showSubletUnpricedPOModal = true;
        return;
      }
    }

    /* Process PACCAR Loyalty redemptions (if any non-redeemed offers still exist in model).
       Only allow process pass through if all lines with bound PACCAR offers have been redeemed
       or otherwise handled (via the clerk removing failed redemptions, etc.).  The PACCAR loyalty
       modal should make ex-post actions such as "remove all failed offers" seamless and fast.
       Also note lines with qty > 1 will automatically demote the coupon quantity to whatever amount
       is actually redeemed by PACCAR so that our numbers reconcile.

       Note on the service side, the loyalty customer is bound when the RO is opened.

       Prevoiusly redeemed *and issued* offers are bound as lineItem.loyaltyCouponsApplied,
       and are not consulted here.  They are consulted when returning parts on the service side.
    */
    if (['decisivissue', 'issue'].includes(type) && this.sysParams.loyalty && this.activeLoyaltyCustomerID !== null) {
      let pendingRedemptions = false;

      for (const lineItem of this.lineItems) {
        if (Array.isArray(lineItem.loyaltyOffers) && lineItem.loyaltyOffers.length > 0) {
          for (const offer of lineItem.loyaltyOffers) {
            if (!offer.Redemptions) {
              pendingRedemptions = true;
            }
          }
        }
      }

      if (pendingRedemptions) {
        /* Here we process any remaining offers that do not have a redemption attached.
           Note this could be due to a first pass, or a retry attempt.  An issue will only
           be processed if all selected offers have been redeemed or removed.

           Do not await this call. The method will pop a modal and handle ex-post actions
           (such as invalid offers, partial redemptions, etc.), then re-entrantly call processRO()
           again after any necessary action resolution.

           Unlike PIN1, we need to track the processRO type here for various re-entrant logic.
        */
        this.lastProcessROType = type;
        this.processLoyaltyRedemptions();
        return;
      }
    }

    /* The linesToProcess logic is almost 1:1 with the front counter logic. However, some features
       don't exist on the back counter (such as core and fee suppression), and operation numbers need
       to be included on RO issue.  There's a strong potential for a necessary divergence in the future,
       so I'm purposefully repeating the similar logic here when building out the linesToProcess in order
       to prevent future conflations/breakages between PIN1 and PRO1/SMM3 in the future.
    */
    const linesToProcess: Array<{
      partNum: string;
      qty: number;
      operationNum: number;
      comment?: string;
      forcedDescription?: string;
      forcedPricing?: {
        forcedNetPrice: number;
        forcedListPrice: number;
        forcedTradePrice: number;
      };
      priceSymbol?: string;
      stockClass?: string;
      serialNumbersIssued?: string[];
      skipPOCheck?: boolean;
      confirmationComment?: ILineItem['confirmationComment'];
      confirmationSale?: ILineItem['confirmationSale'];
      loyaltyRedemptionTracking?: {
        couponCode: string;
        couponDesc: string;
        invoiceNumProxy: string;
        offerID: string;
        loyaltyCustomerID: string;
        redemptionIDs: string[]; // if qty > 1
        redemptionApprovalCodes: string[]; // if qty > 1
      };
    }> = [];

    // Used for ex-post validation on successful issue
    const loyaltyRedemptionIDs: string[] = [];

    for (const lineItem of this.lineItems) {
      if (!lineItem.issuedToRO) {
        // decisiv functions are limited to 1 operaton at a time, the UI enforces this as well
        if (
          ['decisivissue', 'decisivpickticket'].includes(type) &&
          lineItem.roDetail!.operationNumber !== this.activeOperation
        ) {
          continue;
        }

        const operationNum = lineItem.roDetail!.operationNumber;

        // Is this line item a comment?
        if (lineItem.partNum === 'LINEITEM_COMMENT') {
          linesToProcess.push({
            partNum: 'LINEITEM_COMMENT',
            qty: 1,
            comment: lineItem.comment,
            confirmationComment: ['pickticket', 'decisivpickticket'].includes(type)
              ? undefined
              : lineItem.confirmationComment, // undefined if not a confirmation comment
            operationNum,
          });
        } else if (lineItem.partNum === 'LINEITEM_DEFAULTPART') {
          // Note confirmationSale data isn't necessary on LINEITEM_DEFAULTPART since this line type
          // would never occur when selling off of a confirmation.  They would be converted to non-stocking/non-tracked lines.

          linesToProcess.push({
            partNum: `${lineItem.unpriced ? '$' : '@'}X:${lineItem.defaultPartNum}`,
            qty: lineItem.quantity,
            forcedDescription: lineItem.defaultPartDescription,
            // DBPIP accounts for reverse signs
            forcedPricing: {
              forcedNetPrice: Math.abs(lineItem.defaultPartNetPrice!),
              forcedListPrice: Math.abs(lineItem.defaultPartListPrice!),
              forcedTradePrice: Math.abs(lineItem.defaultPartTradePrice!),
            },
            priceSymbol: lineItem.priceSymbol,
            stockClass: lineItem.stockClass,
            skipPOCheck: true,
            operationNum,
          });

          /* We keep sublet comments attached to the line item within the data structure so that
             the sublet default part and sublet comment are tightly coupled on drags.  When we process it
             via DBPIP, the attached comment simply becomes its own line item comment.
          */
          if (lineItem.defaultPartSubletComment !== '') {
            linesToProcess.push({
              partNum: 'LINEITEM_COMMENT',
              qty: 1,
              comment: lineItem.defaultPartSubletComment!,
              operationNum,
            });
          }
        } else if (lineItem.partNum === 'LINEITEM_NONSTOCKINGPART') {
          const nsType = lineItem.unpriced ? '$' : '@'; // Only PRO1/SMM3 would set the unpriced flag

          // If user included a division, use it, otherwise, apply a default division of X.
          // The non-tracked (non-stocking) part entry modal auto-prepends the default division if not specified.
          const partNum = /^[A-Za-z]:/.test(lineItem.nonStockingPartNum!)
            ? `${nsType}${lineItem.nonStockingPartNum}`
            : `${nsType}X:${lineItem.nonStockingPartNum}`;

          linesToProcess.push({
            partNum,
            qty: lineItem.quantity,
            forcedDescription:
              lineItem.nonStockingPartDescription === '' ? partNum : lineItem.nonStockingPartDescription,
            forcedPricing: {
              forcedNetPrice: Math.abs(lineItem.nonStockingPartNetPrice!),
              forcedListPrice: Math.abs(lineItem.nonStockingPartListPrice!),
              forcedTradePrice: Math.abs(lineItem.nonStockingPartTradePrice!),
            },
            priceSymbol: lineItem.priceSymbol,
            stockClass: lineItem.stockClass,
            skipPOCheck: true,
            confirmationSale: ['pickticket', 'decisivpickticket'].includes(type)
              ? undefined
              : lineItem.confirmationSale, // undefined if not a confirmation sale (issue)
            operationNum,
          });

          /* Is this a supercedence base part for non-fleet billing? (no forces will apply)
             See subTotal() for explanation of the lack of per line core/fee suppression support.
             Like default parts, confirmationSale data isn't necessary on superceded parts since this line type would
             never occur when selling off of a confirmation.  They would be converted to the constiuent part lines.
          */
        } else if (lineItem.supercedenceDetails) {
          linesToProcess.push({
            partNum: lineItem.partNum,
            qty: lineItem.quantity,
            operationNum,
          });

          // Standard line item eligible for force overrides and core/fee suppressions
        } else if (lineItem.detail) {
          const forcedPricing = lineItem.forcedPricing
            ? {
                forcedNetPrice: lineItem.forcedNetPrice!,
                forcedListPrice: lineItem.forcedListPrice!,
                forcedTradePrice: lineItem.forcedTradePrice!,
              }
            : undefined;

          let serialNumbersIssued;
          if (['issue', 'decisivissue'].includes(type) && lineItem.serialNumbersSelected) {
            serialNumbersIssued = lineItem.serialNumbersSelected;
          }

          linesToProcess.push({
            partNum: lineItem.partNum,
            qty: lineItem.quantity,
            operationNum,
            // It's possible the next fields are undefined if not set, which simply won't be sent to the API.
            forcedDescription: lineItem.forcedDescription,
            forcedPricing,
            serialNumbersIssued,
            confirmationSale: ['pickticket', 'decisivpickticket'].includes(type)
              ? undefined
              : lineItem.confirmationSale, // undefined if not a confirmation sale (issue)
          });
        }

        // Are there any bound coupons on this part? (standard stocking parts & supercedence cases)
        // Pre-2020 Big Query coupons DO get saved with pick tickets and quotes.  Loyalty Bond Offer Redemptions do not.
        if (Array.isArray(lineItem.couponDetail)) {
          lineItem.couponDetail.forEach(coupon => {
            linesToProcess.push({
              partNum: `@X:${coupon.defaultPartNum}`,
              qty: coupon.quantity,
              forcedDescription: coupon.defaultPartDescription,
              // DBPIP accounts for reverse signs
              forcedPricing: {
                forcedNetPrice: Math.abs(coupon.defaultPartNetPrice),
                forcedListPrice: Math.abs(coupon.defaultPartListPrice),
                forcedTradePrice: Math.abs(coupon.defaultPartTradePrice),
              },
              priceSymbol: coupon.priceSymbol,
              stockClass: coupon.stockClass,
              operationNum,
            });
          });
        }

        // Are there any bound PACCAR Loyalty Bond offers on this part?
        if (
          ['decisivissue', 'issue'].includes(type) &&
          Array.isArray(lineItem.loyaltyOffers) &&
          lineItem.loyaltyOffers.length > 0
        ) {
          // This was cached during redemption and will match here.  It will reset between successful issues.
          const invoiceNumProxy = await this.getLoyaltyInvoiceNumber();

          lineItem.loyaltyOffers.forEach(offer => {
            // Logically if we get here within processRO(), all offers should all be at least partially redeemed (or removed).
            if (offer.Redemptions) {
              // DBPIP accounts for reverse signs
              const amount = Math.abs(offer.Amount);
              const couponCode = offer.CouponCode;
              const couponDesc = typeof offer.Title === 'string' ? offer.Title.replace(/ /g, '_') : '';
              const loyaltyCustomerID = this.activeLoyaltyCustomerID!;
              const offerID = offer.OfferUniqueId;
              const redemptionIDs = offer.Redemptions.map(r => r.UniqueId);
              const redemptionApprovalCodes = offer.Redemptions.map(r => r.RedeemApprovalCode);

              // used for validation after issue success
              loyaltyRedemptionIDs.push(...redemptionIDs);

              linesToProcess.push({
                partNum: `@X:${this.loyaltyDefaultPart.partNum}`,
                qty: offer.Redemptions.length,
                forcedDescription: 'PACCAR LOYALTY COUPON',
                forcedPricing: {
                  forcedNetPrice: amount,
                  forcedListPrice: amount,
                  forcedTradePrice: amount,
                },
                priceSymbol: this.loyaltyDefaultPart.priceSymbol,
                stockClass: this.loyaltyDefaultPart.stockClass,
                operationNum,
                loyaltyRedemptionTracking: {
                  couponCode,
                  couponDesc,
                  invoiceNumProxy,
                  loyaltyCustomerID,
                  offerID,
                  redemptionIDs,
                  redemptionApprovalCodes,
                },
              });
            }
          });
        }
      }
    }

    if (linesToProcess.length > 0) {
      this.modelLocked = true;
      let success = false;

      // activate the correct processing spinner
      if (['decisivissue', 'decisivpickticket'].includes(type)) {
        this.isDecisivROProcessing = true;
      } else {
        this.isROProcessing = true;
      }

      this.lastProcessROReq = {
        customerID: this.activeCustomerID,
        ppc: this.ppcParams.ppc,
        roNum: this.activeRONumber,
        linesToProcess,
        printer: printer ? printer.id : null,
        processByBinLocation: this.processByBinLocation,
        type,
      };

      if (this.logging) {
        console.warn('/processro Request:');
        console.log(this.lastProcessROReq);
      }

      try {
        const res = await Api.post('/shopkeeper/processro', this.lastProcessROReq);
        this.lastProcessRORes = res;

        if (this.logging) {
          console.warn('/processro Response:');
          console.log(this.lastProcessRORes);
        }

        // Extract any errors that occurred during issuing.
        const parsedResponse = res.data.dbpip.split('\n');
        const mainErrorMessages = _.get(parsedResponse, 12, '').trim();
        const nonStockErrorMessages = _.get(parsedResponse, 72, '').trim();

        // If any errors occurred, do not clear the screen, pop the error.
        if (mainErrorMessages || nonStockErrorMessages) {
          const processErrors = [];
          const nonStockErrors = [];

          if (mainErrorMessages) {
            processErrors.push(...mainErrorMessages.split('ý'));
            processErrors.forEach(error => {
              toastr.error('Process Error', error);
            });
            console.error('Process Errors');
            console.log(processErrors);
          }

          // Since adding SKIP to the PO field, we may never hit this (vestigial).
          if (nonStockErrorMessages) {
            nonStockErrors.push(...nonStockErrorMessages.split('ý'));
            nonStockErrors.forEach(error => {
              toastr.error('@Error', error);
            });
            console.error('@ Errors');
            console.log(nonStockErrors);
          }

          success = false;

          // Here we check for the specific edgecase where the only error returned on a decisiv issue
          // is a decisiv error. In this case, DSI still adds the part to the internal RO and a refresh
          // is still in order.
          if (
            type === 'decisivissue' &&
            processErrors.length === 1 &&
            processErrors[0].startsWith('Decisiv Function')
          ) {
            success = true;
          }

          // No errors returned, we assume the RO issue was accepted by DBPIP.
        } else {
          this.lastDocID = _.get(parsedResponse, 11, '').trim();

          switch (type) {
            case 'decisivissue':
            case 'negativeissue':
            case 'issue':
              toastr.success('Success', 'Issued successfully.');
              // TODO: loyalty validations
              break;
            case 'pickticket':
              if (this.lastDocID !== '') {
                toastr.success('Success', `Pick Ticket: ${this.lastDocID}`);
              } else {
                toastr.success('Success', 'Pick ticket created.');
              }

              // If the pick list was linked to an active RO, refresh linked pick lists.
              if (this.activeRONumber) {
                this.loadROPickLists();
              }

              break;
            case 'decisivpickticket':
              toastr.success('Success', 'Decisiv pick ticket created.');
              break;
            default:
              break;
          }

          // If this was a screen print, pop tabs of the returned documents.
          if (printer && printer.name === 'Screen') {
            // Phil's response is IDýDOCTITLEýURLýDOCTITLEýURL...
            const docInfo = _.get(parsedResponse, 10, '').trim().split('ý').slice(1);

            if (docInfo.length > 0) {
              this.screenPrintDocs = []; // clear previous last screen print save
              for (let i = 0; i < docInfo.length; i += 2) {
                this.screenPrintDocs.push({
                  title: docInfo[i],
                  url: docInfo[i + 1],
                });
              }

              if (this.logging) {
                console.info('Screen Print Information');
                console.log(this.screenPrintDocs);
              }

              this.screenPrintIsInvoice = false;
              this.showScreenPrintModal = true;
            } else {
              console.error('Error', 'No document URLs returned.');
            }
          }

          success = true;
        }
      } catch (err: any) {
        console.error(err.message);
        success = false;
        toastr.error('Error', 'Unable to process.  Try again in a moment.');
      }

      // Release any potential locks and refresh RO data on success
      runInAction(() => {
        this.modelLocked = false;
        this.isDecisivROProcessing = false;
        this.isROProcessing = false;
        if (success && ['issue', 'decisivissue', 'negativeissue'].includes(type)) {
          if (loyaltyRedemptionIDs.length > 0) {
            this.validateRedemptions(loyaltyRedemptionIDs, this.activeRONumber as string);
            this.loyaltyInvoiceNumber = null; // next issue cycle will use a new proxy invoice num for loyalty
          }

          this.refreshActiveROData({ retainIssueQueue: type === 'decisivissue' ? 'ALLNONACTIVEOPS' : 'NONE' });
        }
      });
    }
  }

  // Re-evaluates all line items.  This is done upon customer change.
  async reEvalInvoice() {
    // First remove any previously added confirmation sales which wouldn't apply to a new customer
    const linesToRemove: number[] = [];
    this.lineItems.forEach((lineItem, itemIndex) => {
      if (lineItem.confirmationSale || lineItem.confirmationComment) {
        linesToRemove.push(itemIndex);
      }
    });

    let linesRemoved = 0;
    for (const partIndex of linesToRemove) {
      this.removeLineItem(partIndex - linesRemoved, false);
      linesRemoved++;
    }

    // Then reset all non-comment / default part line items to a fetching status.
    // This runs in an action first to quickly reset the reactive UI.
    runInAction(() => {
      this.lineItems.forEach((lineItem, itemIndex) => {
        if (!_.includes(['LINEITEM_COMMENT', 'LINEITEM_DEFAULTPART', 'LINEITEM_NONSTOCKINGPART'], lineItem.partNum)) {
          lineItem.fetched = false;
          delete lineItem.detail;
          delete lineItem.detailSupplemental;
          delete lineItem.incompleteDetailErrors;
          delete lineItem.supercedenceDetails;
          delete lineItem.supercedenceDetailsSupplemental;

          /* When changing customers, it's okay to maintain manually applied price forces and
             line item suppressions since the clerk thoughtfully changed the line item pricing.
             We need not worry about a supercedence case splitting ex-post since price forces are
             not allowed on superceded line items anyways.

             However, in the rare event a quote or pick list with forced prices is currently loaded,
             a customer change should revert to standard pricing for that customer.
          */
          if (lineItem.forcedPricingFromQuoteOrConfirmation) {
            this.clearForcedPricing(itemIndex);
          }
        }
      });

      this.gstTax = 0.0;
      this.salesTax = 0.0;
    });

    // Re-eval each non comment / default part / nonstocking part line item
    // TODO: Decide what to do on API errors
    this.modelLocked = true;
    this.showToasters = false;
    for (let i = 0; i < this.lineItems.length; i++) {
      if (
        !_.includes(
          ['LINEITEM_COMMENT', 'LINEITEM_DEFAULTPART', 'LINEITEM_NONSTOCKINGPART'],
          this.lineItems[i].partNum
        ) &&
        !this.lineItems[i].fetched
      ) {
        const lengthPreEval = this.lineItems.length;

        await this.evalLineItem(i);

        // evalLineItem could potentially modify the line item count (splits, etc.)
        // if so, start the re-eval back at 0 (already fetched items will be skipped)
        if (lengthPreEval !== this.lineItems.length) {
          i = -1; // post iteration i++ will reset to 0
        }
      }
    }
    this.modelLocked = false;
    this.showToasters = true;

    this.updateTaxes();
  }

  @action
  removeAllLineItems() {
    if (this.runMode === 'PIN1' && this.lockoutChanges) {
      toastr.error('Error', 'Changes locked out.');
      return;
    }

    this.lineItems.clear();
    this.lastPIN1PickListID = null;
    this.confirmationPartsAddedToInvoice.clear();
    this.salesTax = 0.0;
    this.gstTax = 0.0;
    this.updateTaxPromiseIDs.clear();
  }

  /* Used by UI methods to delete a line item from the invoice.
   */
  @action
  removeLineItem(itemIndex: number, updateTaxes = true) {
    if (this.runMode === 'PIN1' && this.lockoutChanges) {
      toastr.error('Error', 'Changes locked out.');
      return;
    }

    if (this.isLineFetching()) {
      toastr.error('Wait', 'Fetch action in process. Please try again in a moment.');
      return;
    }

    // Unmark a possible confirmation sale as added to the active invoice
    const x = this.lineItems[itemIndex];
    if (x.confirmationComment || x.confirmationSale) {
      const uniqueID = x.confirmationComment ? x.confirmationComment.uniqueID : x.confirmationSale!.uniqueID!;
      this.confirmationPartsAddedToInvoice.delete(uniqueID);
    }

    this.lineItems.splice(itemIndex, 1);

    if (updateTaxes) {
      this.updateTaxes();
    }
  }

  /* Used by UI methods to delete a bound copuon off of a line item.
     At one point in the history of the loyalty program, a single line could have multiple coupons attached.
   */
  @action
  removeLineItemCoupon(itemIndex: number, couponIndex: number, couponType: 'BIGQUERY' | 'BONDLOYALTY') {
    switch (couponType) {
      case 'BIGQUERY':
        const couponDetail = this.lineItems[itemIndex].couponDetail;
        if (couponDetail && couponIndex < couponDetail.length) {
          couponDetail.splice(couponIndex, 1);
        }
        break;
      case 'BONDLOYALTY':
        const loyaltyOffers = this.lineItems[itemIndex].loyaltyOffers;
        if (loyaltyOffers && couponIndex < loyaltyOffers.length) {
          loyaltyOffers.splice(couponIndex, 1);
        }

        // The clerk is explicitly removing the loyalty offer from the line item,
        // so there's no reason for the background offer checker to put it back on the next cycle.
        this.lineItems[itemIndex].skipLoyaltyOfferAutoAttach = true;
        break;
      default:
        console.error('Invalid or missing coupon type specified.');
        break;
    }

    this.updateTaxes();
  }

  /* Removes (returns to inventory) a previously issued line item from an existing repair order (PRO1/SMM3).
   */
  async removeROLineItem(lineItem: ILineItem, qtyToRemove: number = 1, dirtyCoreReturn = false): Promise<boolean> {
    if (!this.ppcValidated) {
      toastr.error('Error', 'A validated PPC must be entered.');
      return false;
    }

    // No matter what, comments always have qty of 1
    if (lineItem.comment) {
      qtyToRemove = 1;
    }

    let success = false;

    this.lastRemoveROLineItemReq = {
      customerID: this.activeCustomerID,
      ppc: this.ppcParams.ppc,
      roNum: this.activeRONumber,
      roKey: dirtyCoreReturn ? lineItem.roDetail!.coreRoKey : lineItem.roDetail!.roKey,
      qty: qtyToRemove || lineItem.quantity,
    };

    if (!dirtyCoreReturn && lineItem.detail && lineItem.detail.loyaltyCouponsApplied) {
      this.lastRemoveROLineItemReq['loyaltyRoKey'] = lineItem.detail.loyaltyCouponsRoKey;

      // It's (hypothetically) possible PACCAR gave us fewer redemptions than part qty issued
      this.lastRemoveROLineItemReq['loyaltyQty'] = Math.min(
        this.lastRemoveROLineItemReq['qty'],
        lineItem.detail.loyaltyCouponsQuantity
      );
    }

    if (this.logging) {
      console.warn('/removerolineitem Request:');
      console.log(this.lastRemoveROLineItemReq);
    }

    try {
      const res = await Api.post('/shopkeeper/removerolineitem', this.lastRemoveROLineItemReq);
      this.lastRemoveROLineItemRes = res;

      if (this.logging) {
        console.warn('/removerolineitem Response:');
        console.log(this.lastRemoveROLineItemRes);
      }

      // Extract any errors that occurred during removing/returning.
      const parsedResponse = res.data.dbpip.split('\n');
      const mainErrorMessages = _.get(parsedResponse, 12, '').trim();

      // If any errors occurred, do not clear the screen, pop the error.
      if (mainErrorMessages) {
        const processErrors = [];

        processErrors.push(...mainErrorMessages.split('ý'));
        processErrors.forEach(error => {
          toastr.error('Process Error', error);
        });
        console.error('Process Errors');
        console.log(processErrors);

        success = false;

        // No errors returned, we assume the RO remove/return was accepted by DBPIP.
      } else {
        toastr.success('Success', 'Item(s) removed/returned.');
        success = true;
        this.refreshActiveROData({ retainIssueQueue: 'ALLOPS' });
      }
    } catch (err: any) {
      console.error(err.message);
      success = false;
      toastr.error('Error', 'Unable to process.  Try again in a moment.');
    }

    return success;
  }

  async removeAllLineItemsFromActiveOperation() {
    if (!this.ppcValidated) {
      toastr.error('Error', 'A validated PPC must be entered.');
      return false;
    }

    let success = false;

    const req = {
      customerID: this.activeCustomerID,
      ppc: this.ppcParams.ppc,
      roNum: this.activeRONumber,
      operationNum: this.activeOperation,
    };

    if (this.logging) {
      console.warn('/removeallfromoperation Request:');
      console.log(req);
    }

    try {
      const res = await Api.post('/shopkeeper/removeallfromoperation', req);

      if (this.logging) {
        console.warn('/removeallfromoperation Response:');
        console.log(res);
      }

      // Extract any errors that occurred during removing/returning.
      const parsedResponse = res.data.dbpip.split('\n');
      const mainErrorMessages = _.get(parsedResponse, 12, '').trim();

      // If any errors occurred, do not clear the screen, pop the error.
      if (mainErrorMessages) {
        const processErrors = [];

        processErrors.push(...mainErrorMessages.split('ý'));
        processErrors.forEach(error => {
          toastr.error('Process Error', error);
        });
        console.error('Process Errors');
        console.log(processErrors);

        success = false;

        // No errors returned, we assume the RO remove/return was accepted by DBPIP.
      } else {
        toastr.success('Success', 'Item(s) removed/returned.');
        success = true;
        this.refreshActiveROData({ retainIssueQueue: 'ALLOPS' });
      }
    } catch (err: any) {
      console.error(err.message);
      success = false;
      toastr.error('Error', 'Unable to process.  Try again in a moment.');
    }

    return success;
  }

  // remove un-redeemed offers, update taxes, process invoice (or issue on the service side)
  async removeUnredeemedAndProcessInvoice() {
    // do these in one render / @compute (subtotal) batch
    runInAction(() => {
      for (const lineItem of this.lineItems) {
        // are there any offers?
        if (Array.isArray(lineItem.loyaltyOffers) && lineItem.loyaltyOffers.length > 0) {
          // only keep offers with existing successful redemptions
          lineItem.loyaltyOffers = lineItem.loyaltyOffers.filter(offer => offer.Redemptions);
        }
      }
    });

    // one of the only cases we DO want to synchronously wait for a tax update
    this.isProcessing = true;
    await this.updateTaxes();
    this.isProcessing = false;

    if (this.runMode === 'PIN1') {
      // jump through UI bridge to emulate the user manually processing the invoice
      // (for proper ex-post UI actions (reset, etc.), see ShopKeeper.tsx:process()
      this.uiBridge.process('invoice');
    } else {
      this.processRO({ type: this.lastProcessROType });
    }
  }

  @action
  reset() {
    clearInterval(this.loyaltyOfferBackgroundCheckIntervalID); // inert in many cases
    this.activeCustomerID = null;
    this.activeLoyaltyCustomerID = null;
    this.activeDecisivCase = false;
    this.activeOperation = 0;
    this.activeRONumber = null;
    this.confirmationDetailLazyLoaded = false;
    this.confirmationPartsAddedToInvoice.clear();
    this.confirmationsCreatedThisSession = [];
    this.controlNum = null;
    this.controlNumRequired = false;
    this.glNum = null;
    this.customerHistoryLazyLoaded = false;
    this.customerPickListsLazyLoaded = false;
    this.customerVehiclesLazyLoaded = false;
    this.suspendPORules = false;
    this.shipToAccount = null;
    this.shipToAddress = null;
    this.shipToEmail = false;
    this.routingMethodOverride = null;
    this.fleetBillingPostOpFieldCache = {};
    this.fleetBillingCCVariance = null;
    this.freight = this.sysParams.setFreightToZero ? 0.0 : null;
    this.fuelCharge = null;
    this.freightPromptDisplayed = false;
    this.fuelPromptDisplayed = false;
    this.fuelManuallyEntered = false;
    this.invoiceEmailsOverride = null;
    this.lastBackorderData = { boType: 'DAILY', confirmationNumber: null, poNumbers: [] };
    this.lastBOMLoad = null;
    this.lastManualCreditAuth = null;
    this.lastPIN1PickListID = null;
    this.lastROLoyaltyCustomerID = null;
    this.lastStockingPartEntry = '';
    this.lineItems.clear();
    this.lineItemsQueued.clear();
    this.lineItemDragInProcess = false;
    this.lockoutChanges = false;
    this.loyaltyInvoiceNumber = null;
    this.loyaltyOfferBackgroundCheckTracker = null;
    this.loyaltyOfferCount = 0;
    this.managerOverridePersistedForSession = false;
    this.modelLocked = false;
    this._moveOperationsModeActive = false;
    this.moveOperationsInProgress = false;
    this.moveOperationsInProgressRepricing = false;
    this.moveOperationsQueue.clear();
    this.noHistorySale = false;
    this.nonTaxReasonCode = null;
    this.OPCdata = null;
    this.OPCtag = null;
    this.processByBinLocation = false;
    this.po = null;
    this.ppcAttribution = null;
    this.ppcForcePasswordValidated = false;
    this.ppcPasswordNeeded = false;
    this.ppcValidated = false;
    this.quoteEmailsOverride = null;
    this.ppcAttributionReplayActive = false;
    this.receivedBy = null;
    this.receivedByPromptDisplayed = false;
    this.roAttachments.clear();
    this.roPickListsLazyLoaded = false;
    this.skipEmails = false;
    this.forceCashSale = false;
    this._forceSalesTax = false;
    this._suppressCoreCharges = this.sysParams.coreChargeSuppression && this.sysParams.suppressCoreChargeByDefault;
    this._suppressFees = false;
    this._suppressSalesTax = false;
    this.salesTax = 0.0;
    this.gstTax = 0.0;
    this.transferConditionNum = null;
    this.transferRoNum = null;
    this.unitAttachments.clear();
    this.updateTaxPromiseIDs.clear();

    // It's possible PPC permissions changed between resets, so don't cache this.
    if (this.sysParams.autoLoadBlazePPC) {
      this.autoLoadBlazePPC();
    }
  }

  setControlNum(controlNum: null | string) {
    this.controlNum = controlNum;
  }

  // PIN1 email override feature
  setEmailOptions({ invoiceEmails, quoteEmails, skipEmails }: IEmailOptions) {
    // No reason to override if the resultant value is what QM aleady has on file
    this.invoiceEmailsOverride = invoiceEmails.join(',') !== this.customerParams.invoiceEmails ? invoiceEmails : null;
    this.quoteEmailsOverride = quoteEmails.join(',') !== this.customerParams.quoteEmails ? quoteEmails : null;
    this.skipEmails = skipEmails === 'Y';
  }

  setGlNum(glNum: null | string) {
    if (glNum === this.glNum) {
      // no need to re-validate
      return;
    }

    this.glNum = glNum;
    this.controlNumRequired = false; // clear existing req until re-validated / checked

    // Validate it / determine control # requirements for informational purposes only
    if (glNum !== null) {
      // Track these in case the async check is slow and the user moves on
      const currentCustomerID = this.activeCustomerID;
      const currentGlNum = this.glNum;

      const validate = async () => {
        let validated = false;
        let controlNumRequired = false;

        // Matches within the vehicle inventory GL list always require a control #.
        // The two wildcard cases follow precisely how QM implements these wildcard cases.
        const wcFirstThree = glNum.length >= 3 ? glNum.substr(0, 3) : null;
        const wcFirstFour = glNum.length >= 4 ? glNum.substr(0, 4) : null;
        const vglList = this.sysParams.vehicleInventoryGLAccountList;

        if (vglList.includes(glNum)) {
          validated = true;
          controlNumRequired = true;
        } else if (
          (wcFirstThree && vglList.includes(`${wcFirstThree}*`)) ||
          (wcFirstFour && vglList.includes(`${wcFirstFour}*`))
        ) {
          validated = true;
          controlNumRequired = true;
        } else {
          // Check AMF1 - slowest test
          try {
            const res = await Api.post('/shopkeeper/validateglnum', { glNum });
            if (this.logging) {
              console.warn('AMF1 GL# Validation');
              console.log(res.data);
            }

            validated = res.data.validated;
            controlNumRequired = res.data.controlNumRequired;
          } catch (e) {
            console.error('API Error. Unable to validate GL # against AMF1.');
          }
        }

        if (currentCustomerID === this.activeCustomerID && currentGlNum === this.glNum) {
          if (!validated) {
            toastr.error('Error', 'Unable to locate GL#. Try Again.');
          } else {
            this.controlNumRequired = controlNumRequired;
          }
        }
      };

      validate();
    }
  }

  setFreight(freight: number | null) {
    this.freight = freight;
    this.updateTaxes();
  }

  setFuelCharge(fuelCharge: number | null) {
    if (this.fuelCharge === fuelCharge) {
      return;
    }

    this.fuelManuallyEntered = fuelCharge !== null;
    this.fuelCharge = fuelCharge;
    this.updateTaxes();
  }

  setNonTaxReasonCode(code: string) {
    this.nonTaxReasonCode = code;
  }

  // Invoice PO - the backordering system has its own PO management system
  setPo(po: string | null) {
    if (!this.validatePo(po)) {
      toastr.error('Error', 'Invalid Customer PO format.', { toastId: 'invalidPo' });
      return;
    }

    // If the user purposefully clears the field, trust their intention
    if (po === null) {
      this.suspendPORules = false;
    }

    this.po = po;
  }

  setReceivedBy(by: string | null) {
    this.receivedBy = by;
  }

  // Allow the user to override the customer's default routing method.
  // D3/QM sends sysParams shippingCodes and customer route fields uppercased.
  // In case this is called from the console (debugging), we'll force upper case.
  setRoutingMethod(method: string): boolean {
    method = method.toUpperCase();

    if (method === this.activeRoutingMethod) {
      return true;
    }

    // make sure this method exists in the system codes before changing
    const shippingCode = this.sysParams.shippingCodes.find(o => o.code === method);
    if (shippingCode) {
      this.routingMethodOverride = method;

      if (this.promptForFuel) {
        if (shippingCode.fuelDefault) {
          // If the clerk manually entered a fuel charge, confirm he wishes to override it with a default value.
          if (this.fuelManuallyEntered) {
            modal.confirm({
              title: 'Update Fuel Charge',
              message: `You previously manually entered a fuel charge.  Do you wish to overwrite it with the default fuel charge for ${
                shippingCode.desc
              } of $${shippingCode.fuelDefault.toFixed(2)}?`,
              confirmLabels: ['Yes', 'No'],
              callback: () => {
                this.fuelManuallyEntered = false;
                this.fuelCharge = shippingCode.fuelDefault!;
                this.uiBridge.fuelChargeInputRef.value = this.fuelCharge.toFixed(2);
              },
            });

            // Otherwise just apply the default value.
          } else {
            this.fuelCharge = shippingCode.fuelDefault;
            this.uiBridge.fuelChargeInputRef.value = this.fuelCharge.toFixed(2);
          }

          // No default fuel charge set for this route type.  Clear the fuel charge input unless it was manually specified.
        } else if (!this.fuelManuallyEntered) {
          this.fuelCharge = null;
          this.uiBridge.fuelChargeInputRef.value = '';
        }
      }

      this.updateTaxes();
      return true;
    } else {
      return false;
    }
  }

  /* Variance option to override the ship to address with a linked ship-to account on file.
     Note, DBPIP only cares about the customerID, but we need to display the details in the
     reactive UI.  Clear shipToAccount on reset or alternate customer load.
  */
  @action
  setShipToAccount(customerID: string, details: ICustomerParams, shipToEmail: boolean) {
    this.shipToAccount = {
      customerID,
      ...details,
    };
    this.shipToEmail = shipToEmail;
    this.shipToAddress = null;
    this.updateTaxes();
  }

  /* Variance option to override the ship to address with a free form address entry.
     Note, the DBPIP spec states the requisite fields which enable this are for cash only
     customers, but it works for non-cash standard customers as well (ostensibly).
  */
  @action
  setShipToAddress(shipToAddress: IShipToAddress) {
    this.shipToAddress = shipToAddress;
    this.shipToAccount = null;
    this.shipToEmail = false;
    this.updateTaxes();
  }

  setTransferRoNum(roNum: null | string) {
    this.transferRoNum = roNum;
  }

  setTransferConditionNum(conditionNum: null | string) {
    this.transferConditionNum = conditionNum;
  }

  // Does the loaded customer have stocking part price forces disallowed via price code?
  @computed
  get stdpForceSuppression(): boolean {
    return this.activeCustomerID !== null && this.customerParams.stdpForceSuppression;
  }

  /* This tells us if we have any sublets (SUBL/SUBP) or unpriced parts added to the issue queue,
     but not yet issued / PO created.  This is useful for configuring various UI controls such as the
     main screen standalone PO button, as well as the optional service/body selector control on sublet POs.

     Feb 2022 we added support for faux-sublets.  These are non-SUBL/SUBP default parts with the
     "PO" flag checked in ATB3.  We do not collect comments for these faux-sublets.  We include both
     the true SUBL/SUBP sublets and the faux-sublets in subletCandidates, extracted during model load.

     Unpriced default parts which are not faux sublets should not be subject to stand alone PO creation.
     As an example, a COMPCHG, the price of which is not known until later.
  */
  @computed
  get subletOrUnpricedItemPendingIssue(): 'Sublets/Unpriced' | 'Sublets' | 'Unpriced' | false {
    const subletFound = this.lineItems.some(o => !o.issuedToRO && this.subletCandidates.includes(o.defaultPartNum!));
    const unpricedFound = this.lineItems.some(
      o => !o.issuedToRO && o.unpriced && !this.defaultParts.has(o.defaultPartNum!)
    );

    if (subletFound && unpricedFound) {
      return 'Sublets/Unpriced';
    } else if (subletFound) {
      return 'Sublets';
    } else if (unpricedFound) {
      return 'Unpriced';
    } else {
      return false;
    }
  }

  // TODO: Verify in model that core charge / fee suppression is enabled at this site

  set suppressCoreCharges(value: boolean) {
    if (value !== this._suppressCoreCharges) {
      this._suppressCoreCharges = value;
      this.updateTaxes();
    }
  }

  @computed
  get suppressCoreCharges() {
    return this._suppressCoreCharges;
  }

  set suppressFees(value: boolean) {
    if (value !== this._suppressFees) {
      this._suppressFees = value;
      this.updateTaxes();
    }
  }

  @computed
  get suppressFees() {
    return this._suppressFees;
  }

  suppressLineItemCoreCharge(itemIndex: number, value: boolean) {
    if (
      _.isUndefined(this.lineItems[itemIndex].suppressCoreCharge) ||
      this.lineItems[itemIndex].suppressCoreCharge !== value
    ) {
      this.lineItems[itemIndex].suppressCoreCharge = value;
      this.updateTaxes();
    }
  }

  suppressLineItemFee(itemIndex: number, value: boolean) {
    if (_.isUndefined(this.lineItems[itemIndex].suppressFee) || this.lineItems[itemIndex].suppressFee !== value) {
      this.lineItems[itemIndex].suppressFee = value;
      this.updateTaxes();
    }
  }

  set suppressSalesTax(value: boolean) {
    // This only applies if there is a loaded customer who is both:
    // 1. Taxable
    // 2. ACU1 settings are such to enable tax suppression for this customer
    if (
      this.activeCustomerID &&
      this.customerParams.taxable &&
      this.customerParams.suppressSalesTax &&
      value !== this._suppressSalesTax
    ) {
      this._suppressSalesTax = value;

      // setting suppress sales tax
      if (value && this.sysParams.requireNonTaxReason) {
        // default reason code to first entry
        this.nonTaxReasonCode = _.get(this.sysParams.nonTaxReasonCodes, '0.code', null);

        // clearing suppress sales tax or non tax reason not required
      } else {
        this.nonTaxReasonCode = null;
      }

      this.updateTaxes();
    }
  }

  get suppressSalesTax() {
    return this._suppressSalesTax;
  }

  /* Includes tradePrice (sell price), core charges, and fees.
     Does NOT include taxes, freight, or s&h.
  */
  subTotal(activeOperationOnly = false): { due: number; fetchingItemsPresent: boolean } {
    let due = 0.0;
    let fetchingItemsPresent = false;

    this.lineItems.forEach(lineItem => {
      if (['PRO1', 'SMM3', 'SRE2'].includes(this.runMode) && activeOperationOnly) {
        if (this.activeOperation !== lineItem.roDetail!.operationNumber) {
          return;
        }
      }

      if (lineItem.fetched) {
        // Do we have a fetched supercedence chain/split?
        // Note: while global variances for core charge / fee suppression work on these items,
        // it is not currently possible to suppress a per line supercedence constituent
        // core charge / fee due to DBPIP format limitations.  If customers eventually need
        // this, we will discuss with Phil to add support to NOCORECHARGE/NOFEE syntax.
        if (lineItem.supercedenceDetails) {
          lineItem.supercedenceDetails.forEach(supercedenceDetail => {
            due +=
              supercedenceDetail.quantity *
              (supercedenceDetail.tradePrice +
                (this._suppressCoreCharges ? 0.0 : supercedenceDetail.coreTradePrice) +
                (this._suppressFees ? 0.0 : supercedenceDetail.feeTradePrice));
          });

          // Otherwise, do we have a standard (stocking) part?
        } else if (lineItem.detail) {
          const tradePrice = lineItem.forcedPricing ? lineItem.forcedTradePrice! : lineItem.detail.tradePrice;
          due +=
            lineItem.quantity *
            (tradePrice +
              (this._suppressCoreCharges || lineItem.suppressCoreCharge ? 0.0 : lineItem.detail.coreTradePrice) +
              (this._suppressFees || lineItem.suppressFee ? 0.0 : lineItem.detail.feeTradePrice));

          // Are there any bound BIGQUERY coupons on this part?
          if (Array.isArray(lineItem.couponDetail)) {
            lineItem.couponDetail.forEach(coupon => {
              due += coupon.quantity * coupon.defaultPartTradePrice;
            });
          }

          // Are there any bound PACCAR LOYALTY offers on this part?
          // On service side screens, these are not yet issued (but possibly redeemed pending issue)
          if (Array.isArray(lineItem.loyaltyOffers)) {
            lineItem.loyaltyOffers.forEach(offer => {
              // if the offer has already been redeemed, the actual redemption count dominates
              const quantity = offer.Redemptions ? offer.Redemptions.length : lineItem.quantity;
              due += quantity * offer.Amount;
            });
          }

          // Previously issued loyalty coupons - these will only exist in service screen scenarios
          if (lineItem.detail.loyaltyCouponsApplied) {
            due += lineItem.detail.loyaltyCouponsQuantity * lineItem.detail.loyaltyCouponsPrice;
          }

          // Is this a line issued to a RO with previously returned cores (partial)?
          if (
            lineItem.issuedToRO &&
            lineItem.detail.coreAttached &&
            lineItem.quantity !== lineItem.detail.coreQuantity
          ) {
            due -= (lineItem.quantity - lineItem.detail.coreQuantity) * lineItem.detail.coreTradePrice;
          }

          // Is this a non-stocking part line?
        } else if (lineItem.partNum === 'LINEITEM_NONSTOCKINGPART') {
          due += lineItem.quantity * lineItem.nonStockingPartTradePrice!;

          // Finally, is this a default part?
        } else if (lineItem.partNum === 'LINEITEM_DEFAULTPART') {
          due += lineItem.quantity * lineItem.defaultPartTradePrice!;
        }
      } else {
        fetchingItemsPresent = true;
      }
    });
    return {
      due,
      fetchingItemsPresent,
    };
  }

  // Reference validatePo for rules
  transpileD3QMRegex(input: string): string {
    const inputParts = input.toUpperCase().split("'"); // literal support
    const literalSpecialChars = /[|\\{}()[\]^$+*?.]/g;
    let jsRegexStr = '';

    for (let i = 0; i < inputParts.length; i++) {
      // even parts are format specifiers (by deduction splitting on ')
      if (i % 2 === 0) {
        // TODO: we may need to catch improper PCU2 input formats here (if users don't follow the rules)

        // convert D3/QM format specifiers to js regex format
        const formatSpecifiers = inputParts[i].match(/\d+[A|N|X]/g);
        // could be null in 'literal'-'literal' case
        if (formatSpecifiers) {
          formatSpecifiers.forEach(formatSpecifier => {
            const iterations = formatSpecifier.slice(0, -1); // preceeding number
            const type = formatSpecifier.slice(-1); // A|N|X

            let formatSpecifierJS = '';
            switch (type) {
              case 'A': // letters
                formatSpecifierJS = '[A-Za-z]';
                break;
              case 'N': // numbers
                formatSpecifierJS = '\\d';
                break;
              case 'X': // letters or numbers
                formatSpecifierJS = '[A-Za-z\\d]';
                break;
              default:
                // NO-OP (shouldn't be logically possible)
                break;
            }

            // in D3/QM, an iterator of 0 means 1 or more (because whynot?)
            formatSpecifierJS += iterations === '0' ? '+' : `{${iterations}}`;

            // replace the formatSpecifier with the JS version
            inputParts[i] = inputParts[i].replace(formatSpecifier, formatSpecifierJS);
          });

          // all the format specifiers in this chunk have been converted, add to final regex
          jsRegexStr += inputParts[i];
        }

        // odd parts are literals within 's which need escaping for js regexes
      } else {
        jsRegexStr += inputParts[i].replace(literalSpecialChars, '\\$&');
      }
    }

    return `^${jsRegexStr}$`; // anchor the generated regex on both ends
  }

  // PSO3 is concerned with purchasing costs, not selling prices
  @computed
  get totalCost(): { amount: string; calcInProcess: boolean } {
    let due = 0.0;
    let fetchingItemsPresent = false;

    this.lineItems.forEach(lineItem => {
      if (lineItem.fetched) {
        // Do we have a fetched supercedence chain/split?
        // PSO3 does not enable variance selection for cores/fees as of 5/5/22, but may one day.
        // Per line core/fee suppression is not available for supercedences in any run mode.
        if (lineItem.supercedenceDetails) {
          lineItem.supercedenceDetails.forEach(supercedenceDetail => {
            due +=
              supercedenceDetail.quantity *
              (supercedenceDetail.publishedNetPrice +
                (this._suppressCoreCharges ? 0.0 : supercedenceDetail.corePublishedNetPrice) +
                (this._suppressFees ? 0.0 : supercedenceDetail.feePublishedNetPrice));
          });

          // Otherwise, do we have a standard (stocking) part?
        } else if (lineItem.detail) {
          const netPrice = lineItem.forcedPricing ? lineItem.forcedNetPrice! : lineItem.detail.publishedNetPrice;
          due +=
            lineItem.quantity *
            (netPrice +
              (this._suppressCoreCharges || lineItem.suppressCoreCharge ? 0.0 : lineItem.detail.corePublishedNetPrice) +
              (this._suppressFees || lineItem.suppressFee ? 0.0 : lineItem.detail.feePublishedNetPrice));

          // Is this a non-stocking part line?
        } else if (lineItem.partNum === 'LINEITEM_NONSTOCKINGPART') {
          due += lineItem.quantity * lineItem.nonStockingPartNetPrice!;

          // Finally, is this a default part?
        } else if (lineItem.partNum === 'LINEITEM_DEFAULTPART') {
          due += lineItem.quantity * lineItem.defaultPartNetPrice!;
        }
      } else {
        fetchingItemsPresent = true;
      }
    });

    return {
      amount: due.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      calcInProcess: fetchingItemsPresent,
    };
  }

  // PIN1
  // Same as totalParts(), but includes taxes calculated async in bulk via DBPIP for an exact amount.
  @computed
  get totalInvoice(): { amount: string; amountNumeric: number; calcInProcess: boolean } {
    const subTotal = this.subTotal();
    let due = subTotal.due;

    if (this.freight) {
      due += this.freight;
    }

    if (this.fuelCharge) {
      due += this.fuelCharge;
    }

    // add in observed taxes (inclusive of freight/fuel tax)
    due += this.salesTax + this.gstTax;

    return {
      amount: due.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      amountNumeric: due, // used for testing credit limits
      calcInProcess: subTotal.fetchingItemsPresent || this.taxCalculationInProcess,
    };
  }

  // PRO1, SRE2, SMM3
  @computed
  get totalOperationParts(): { amount: string; calcInProcess: boolean } {
    const subTotal = this.subTotal(true);
    const due = subTotal.due;
    return {
      amount: due.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      calcInProcess: subTotal.fetchingItemsPresent,
    };
  }

  @computed
  get totalOverCreditLimit() {
    return (
      this.runMode === 'PIN1' &&
      this.activeCustomerID &&
      this.customerParams.creditLimit &&
      !this.totalInvoice.calcInProcess &&
      this.totalInvoice.amountNumeric > this.customerParams.creditLimit
    );
  }

  // PIN1, PRO1, SRE2, SMM3
  @computed
  get totalParts(): { amount: string; calcInProcess: boolean } {
    const subTotal = this.subTotal();
    const due = subTotal.due;
    return {
      amount: due.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      calcInProcess: subTotal.fetchingItemsPresent,
    };
  }

  async updateConfirmation(detail: IConfirmationPlaybackDetail & { notifyEmail: string | null }) {
    const {
      confirmationNumber,
      shipIn,
      shipOut,
      customerPo,
      contactName,
      contactPhone,
      contactFax,
      contactEmail,
      purchasingComments,
    } = detail;

    let { freightFormatted, fuelFormatted } = detail;

    // Request to zero out an unsold confirmation charge via clearing the input control
    if (
      this.activeConfirmation!.details.freight &&
      !this.activeConfirmation!.details.freightSold &&
      freightFormatted === ''
    ) {
      freightFormatted = '0.00';
    }
    if (this.activeConfirmation!.details.fuel && !this.activeConfirmation!.details.fuelSold && fuelFormatted === '') {
      fuelFormatted = '0.00';
    }

    try {
      const req: IConfirmationPlaybackDetail & { isRO: boolean; ppc: string } = {
        isRO: this.runMode !== 'PIN1',
        ppc: this.ppcParams.ppc,
        confirmationNumber,
        shipIn,
        shipOut,
        customerPo,
        contactName,
        contactPhone,
        contactFax,
        contactEmail,
        purchasingComments,
        freightFormatted,
        fuelFormatted,
      };

      if (this.logging) {
        console.warn('/updateconfirmation Request:');
        console.log(req);
      }

      const res = await Api.post('/shopkeeper/updateconfirmation', req);

      if (this.logging) {
        console.warn('/updateconfirmation Response');
        console.log(res.data);
      }

      const parsedResponse = res.data.dbpip.split('\n');
      const legacyError = _.get(parsedResponse, 12, '').trim();
      if (legacyError) throw new Error(legacyError);

      // If this is the "active" confirmation, update any changes just applied
      if (this.activeConfirmation && confirmationNumber === this.activeConfirmation.details.confirmationNumber) {
        runInAction(() => {
          this.activeConfirmation!.details = {
            ...this.activeConfirmation!.details,
            shipIn,
            shipOut,
            customerPo,
            contactName,
            contactPhone,
            contactFax,
            contactEmail,
            purchasingComments,
            freight: parseFloat(freightFormatted!) || undefined,
            fuel: parseFloat(fuelFormatted!) || undefined,
          };
        });
      }
    } catch (err: any) {
      toastr.error('Error', 'Try again momentarily.');
      console.error(err.message);
      throw err;
    }
  }

  /* In order to get an exact tax calculation (based on %s), all parts must be eval'd at once by DBPIP.
     Call updateTaxes any time line items are updated (cannot be @computed since the network call
     is a side-effect, shouldn't be under autorun() either since too many other observables would cause
     needless tax recalcs.
  */
  async updateTaxes() {
    // Service screens don't need tax calcs, nor does PSO3 stock orders nor PIN4
    if (['PIN4', 'PRO1', 'PSO3', 'SMM3', 'SRE2'].includes(this.runMode)) {
      return;
    }

    this.updateTaxPromiseIDs.clear(); // cancel any pending async tax calcs
    this.showManualTaxCalc = false; // clear any API-error induced manual tax calc links

    let salesTax = 0.0;
    let gstTax = 0.0;

    const partsToCalc: Array<{
      partNum: string;
      qty: number;
      forcedTradePrice?: string;
      priceSymbol?: string;
      stockClass?: string;
      suppressCoreCharge?: boolean;
      suppressFee?: boolean;
    }> = [];

    this.lineItems.forEach(lineItem => {
      // Only consider lines that require tax calcs
      if (
        lineItem.fetched &&
        (lineItem.detail ||
          lineItem.supercedenceDetails ||
          _.includes(['LINEITEM_DEFAULTPART', 'LINEITEM_NONSTOCKINGPART'], lineItem.partNum))
      ) {
        let partNum, qty, forcedTradePrice, priceSymbol, stockClass, suppressCoreCharge, suppressFee;

        /* The inquiry program searches for @DEFAULTPART, therefore, it is not required to submit the price symbol and stock class.
           However, when it's time to actually process the invoice, a different program is used on the legacy side which DOES need
           the price symbol and stock class.  Additionally, inquiry does NOT require a faux-division on @ parts, while
           the invoicing program does (such as @X:DEFAULTPART).  I did not make these rules. ¯\_(ツ)_/¯

           We don't use the DBPIP response part lines on tax inquiry, but default parts playback in the response as @ATB3INVOICETITLE
           rather than @ATB3PARTNAME, which is what we submit.  Additionally there's a bug where @FREON plays back as @FRFREON,
           which is probably related to the missing division.  For now, no reason to fix this since we don't use the response other
           than to grab the tax totals. 
        */
        if (lineItem.partNum === 'LINEITEM_DEFAULTPART') {
          partNum = `@${lineItem.defaultPartNum!}`;
          qty = lineItem.quantity;
          forcedTradePrice = Math.abs(lineItem.defaultPartTradePrice!).toFixed(2); // DBPIP applies reverse sign when necessary (oddly)
          // The inquiry program associates the right price symbol and stock class here.
        } else if (lineItem.partNum === 'LINEITEM_NONSTOCKINGPART') {
          partNum = `@${lineItem.nonStockingPartNum!}`;
          qty = lineItem.quantity;
          forcedTradePrice = Math.abs(lineItem.nonStockingPartTradePrice!).toFixed(2);
          priceSymbol = lineItem.priceSymbol;
          stockClass = lineItem.stockClass;
        } else {
          partNum = lineItem.partNum;
          qty = lineItem.quantity;

          if (lineItem.forcedPricing) {
            forcedTradePrice = lineItem.forcedTradePrice!.toFixed(2);
          }

          // line-item level core/fee suppressions, possibly undefined if not set (ok)
          suppressCoreCharge = lineItem.suppressCoreCharge;
          suppressFee = lineItem.suppressFee;
        }

        partsToCalc.push({
          partNum,
          qty,
          forcedTradePrice,
          priceSymbol,
          stockClass,
          suppressCoreCharge,
          suppressFee,
        });

        // Are there any bound pre-2020 Big Query coupons on this part?
        if (Array.isArray(lineItem.couponDetail)) {
          lineItem.couponDetail.forEach(coupon => {
            partsToCalc.push({
              partNum: `@${coupon.defaultPartNum!}`,
              qty: coupon.quantity,
              forcedTradePrice: Math.abs(coupon.defaultPartTradePrice).toFixed(2),
              priceSymbol: coupon.priceSymbol,
              stockClass: coupon.stockClass,
            });
          });
        }

        // Are there any bound PACCAR Loyalty Bond offers on this part?
        if (Array.isArray(lineItem.loyaltyOffers) && lineItem.loyaltyOffers.length > 0) {
          lineItem.loyaltyOffers.forEach(offer => {
            // if the offer has already been redeemed, the actual redemption count dominates
            const quantity = offer.Redemptions ? offer.Redemptions.length : lineItem.quantity;

            partsToCalc.push({
              partNum: `@${this.loyaltyDefaultPart.partNum}`,
              qty: quantity,
              forcedTradePrice: Math.abs(offer.Amount).toFixed(2),
              priceSymbol: this.loyaltyDefaultPart.priceSymbol,
              stockClass: this.loyaltyDefaultPart.stockClass,
            });
          });
        }
      }
    });

    // DBPIP bulk calc taxes here
    if (partsToCalc.length > 0) {
      // We add freight/fuel here to prevent tax calcs on an otherwise empty invoice builder
      if (this.promptForFuel && this.fuelCharge) {
        // The same default part syntax applies here as above (which is peculiarly different from invoice process)
        partsToCalc.push({
          partNum: `@${this.sysParams.fuelChargeDefaultPartName}`,
          qty: 1,
          forcedTradePrice: Math.abs(this.fuelCharge).toFixed(2),
        });
      }

      let res;
      this.lastCalcTaxReq = {
        customerID: this.activeCustomerID || '',
        freight: this.freight,
        fuelCharge: this.fuelCharge,
        ppc: this.ppcValidated ? this.ppcParams.ppc : '',
        partsToCalc,
        variances: {
          // Only variances which would effect tax calculation need be specified here.
          forceSalesTax: this._forceSalesTax,
          // The UI / model logic should enforce only one of these being set.
          // The API will prioritize shipToAccount in the event both are erroneously provided.
          shipToAccount: this.shipToAccount ? this.shipToAccount.customerID : null,
          shipToAddress: this.shipToAddress ? this.shipToAddress : null,
          shipToRoute: this.activeRoutingMethod,
          suppressCoreCharges: this._suppressCoreCharges,
          suppressFees: this._suppressFees,
          suppressSalesTax: this._suppressSalesTax,
        },
      };

      try {
        const promiseID = Symbol(Math.floor(Math.random() * 1000000));
        this.updateTaxPromiseIDs.set(promiseID, true);

        if (this.logging) {
          console.warn(`/calculatetaxes Request (${promiseID.toString()}):`);
          console.log(this.lastCalcTaxReq);
        }

        this.taxCalculationInProcess = true;
        res = await Api.post('/shopkeeper/calculatetaxes', this.lastCalcTaxReq);

        // was this async action cancelled?
        if (!this.updateTaxPromiseIDs.has(promiseID)) {
          if (this.logging) {
            console.warn(`Pending Tax Calc (${promiseID.toString()}) Cancelled By New Tax Calc Request `);
          }
          return;
        }

        this.lastCalcTaxRes = res; // debugging - will be available even if parse error below

        if (this.logging) {
          console.warn(`/calculatetaxes Response (${promiseID.toString()}):`);
          console.log(this.lastCalcTaxRes);
        }

        if (res.status !== 200) {
          throw new Error('200 not received from calculatetaxes.');
        }

        const dbpipRows = _.get(res, 'data.dbpip', '').split('\n');
        if (dbpipRows.length < 19) {
          throw new Error('Invalid Calculate Taxes Response');
        }

        // Tease out total taxes from line 19 [salesTax, gstTax]
        const taxes = dbpipRows[18].split('ý');
        salesTax = parseFloat(taxes[0]) || 0.0;
        gstTax = parseFloat(taxes[1]) || 0.0;
      } catch (err: any) {
        console.error(err.message);
        toastr.error('Error', 'Unable to calculate taxes.');

        /* Taxes revert to default of $0.00 on API failure.
           Show manual "Retry Tax Calc" link (in case of max users or some other API issue),
           otherwise, taxes will try to update again on the next (non-comment) line item change.
        */
        this.showManualTaxCalc = true;
      }
    } // else taxes are $0.00 as initiated above

    runInAction(() => {
      this.taxCalculationInProcess = false;
      this.salesTax = salesTax;
      this.gstTax = gstTax;
    });
  }

  /* Customers can have their PO format configured within PCU2.

     model.customerParams.purchaseOrder contains the PO requirements:
     - # by itself means a PO is required, but can be anything
     - Any string without leading #/* means the PO must start with a literal "blanket PO", such as DSIPO.
       DBPIP auto prepends this blanket PO literal on save, so all we have to do is display it in the interface.
       If the user enters 123 in this case, for example, DBPIP will resolve the PO to DSIPO123 on save.
     - *'DSI'3A-2N is the D3/QM pattern (regex) format
        - Begin with * for regex matches
        - Literal parts must be within single quotes
        - 3A = 3 letters, 2N = 2 numbers, 2X = 2 letter or number. 0 means >= 1
        - The pattern will be stored in purchaseOrderPatternReq (without the leading *)
  */
  validatePo(po: string | null): boolean {
    // process *pattern formats if specified in PCU2 for this customer
    if (this.customerParams.purchaseOrderPatternReq && !this.suspendPORules && po !== null) {
      const jsRegexStr = this.transpileD3QMRegex(this.customerParams.purchaseOrderPatternReq);
      // Test the PO against the newly produced js compatible regex
      if (!new RegExp(jsRegexStr).test(po.toUpperCase())) {
        return false;
      }
    }
    return true;
  }

  // Supports checking currently loaded PPC as well as manager overrides on credits.
  async validatePPCCreditAuth(ppc: string, password: string): Promise<boolean> {
    // Clerk is using his/her PPC (i.e. credit PPC credentials already cached)
    if (ppc.toUpperCase() === this.ppcParams.ppc.toUpperCase()) {
      if (!this.canClerkPPCIssueCredit) {
        toastr.error('Error', `PPC (${this.ppcParams.ppc}) does not have permission to issue credits.`);
        return false;
      }

      // Note we only require auth on the clerk's PPC for credits if a credit password is set
      if (this.ppcParams.permissions.issueCreditsPassword !== hash(password)) {
        toastr.error('Error', 'Incorrect PPC Credit Password.');
        return false;
      }

      // Otherwise a manager or 3rd party has stepped over to enter credit PPC authorization
    } else {
      let res;
      try {
        res = await Api.post('/shopkeeper/ppcauth', { ppc });
        if (res.status !== 200) {
          toastr.error('Error', '200 not received from ppcauth');
          return false;
        }
      } catch (err: any) {
        console.error(err.message);
        toastr.error('Error', 'Unable to load PPCAuth response.');
        return false;
      }

      if (this.logging) {
        console.warn(`/ppcauth Request (PPC:${ppc}):`);
        console.log(res.data);
      }

      const creditPpcParams = res.data as IPPCParams;

      if (!creditPpcParams.authenticated) {
        toastr.error('Error', `PPC (${ppc}) not recognized.`);
        return false;
      }

      if (!creditPpcParams.permissions.issueCredits) {
        toastr.error('Error', `PPC (${ppc}) does not have permission to issue credits.`);
        return false;
      }

      if (creditPpcParams.permissions.issueCreditsPassword !== hash(password)) {
        toastr.error('Error', 'Incorrect PPC Credit Password.');
        return false;
      }
    }

    // No issues detected with this auth.
    return true;
  }

  // This will only be called logically if forcePartsPassword exists and is used to force prices/descriptions.
  validatePPCForcePassword(password: string): boolean {
    if (this.ppcParams.permissions.forcePartsPassword === hash(password)) {
      this.ppcForcePasswordValidated = true;
    } else {
      this.ppcForcePasswordValidated = false;
      toastr.error('Error', 'Incorrect PPC force password.');
    }
    return this.ppcForcePasswordValidated;
  }

  // This password is used when initially loading a PPC.
  validatePPCPassword(password: string): boolean {
    if (this.ppcParams.password === hash(password)) {
      this.ppcValidated = true;
      this.autoLoadSeed();
    } else {
      this.ppcValidated = false;
      toastr.error('Error', 'Incorrect PPC password.');
    }
    return this.ppcValidated;
  }

  // Issuing returns from the service side with PPC pw required.
  validatePPCReturnPassword(password: string): boolean {
    const validated = this.ppcParams.permissions.issueReturnsPassword === hash(password);
    if (!validated) {
      toastr.error('Error', 'Incorrect PPC Return Password');
    }
    return validated;
  }

  // This is utilized by the manager override feature for forcing prices/descriptions.
  async validateManagerOverride({
    ppc,
    forcePartsPassword,
    persist,
  }: {
    ppc: string;
    forcePartsPassword: string;
    persist: boolean;
  }): Promise<boolean> {
    if (ppc.trim() === '') {
      toastr.error('Error', 'PPC required.');
      return false;
    }
    if (forcePartsPassword.trim() === '') {
      toastr.error('Error', 'Force Parts Password required to use this feature.');
      return false;
    }

    let res;
    try {
      res = await Api.post('/shopkeeper/ppcauth', { ppc });
      if (res.status !== 200) {
        throw new Error('200 not received from ppcauth');
      }
    } catch (err: any) {
      console.error(err.message);
      toastr.error('Error', 'Unable to load PPCAuth response.');
      return false;
    }

    if (this.logging) {
      console.warn(`/ppcauth Request (PPC:${ppc}):`);
      console.log(res.data);
    }

    const managerPpcParams = res.data as IPPCParams;

    if (!managerPpcParams.authenticated) {
      toastr.error('Error', `PPC (${ppc}) not recognized.`);
      return false;
    }

    if (!managerPpcParams.permissions.forceParts) {
      toastr.error('Error', `PPC (${ppc}) does not have permission to force parts.`);
      return false;
    }

    if (managerPpcParams.permissions.forcePartsPassword !== hash(forcePartsPassword)) {
      toastr.error('Error', 'Invalid Force Parts Password.');
      return false;
    }

    // All good, persist maanger override for the remainder of this session if requested.
    this.managerOverridePersistedForSession = persist;
    return true;
  }

  /* VOID from the "active" (loaded) confirmation

     No param provided: voids entire confirmation
     If items provided:
       - any object with lineIDs property not provided voids the entire PO
       - voidLineIDs voids specific lines for a specific PO, per QM VOIDPO's definition of a line id
         - confirmation sale: po line number
         - confirmation comments: C#, derived from PO.C# unique ID
         - two different POs could (and probably will) use the same void line ids
       - uniqueIDs must be provided 1:1 if "void" lineIDs are provided (always unique among POs)

     This method supports voiding multiple POs / multiple lines at a granular level at the same time,
     but is not currently exposed in the UI as such.  The UI currently exposes voiding the entire
     confirmation, voiding a single PO, or voiding a single line item.

     TODO: reinstate supercedences flag (prob very rarely used)
  */
  async voidFromActiveConfirmation(
    items: Array<{
      poNum: string;
      cancelUnordered?: 'Y' | 'N';
      voidLineIDs?: string[];
      uniqueIDs?: string[];
    }> | null = null
  ): Promise<boolean> {
    if (!this.activeConfirmation) {
      return false;
    }

    const isRO = ['PRO1', 'SMM3', 'SRE2'].includes(this.runMode);
    const confirmationNumber = this.activeConfirmation.details.confirmationNumber;
    let voidFullConfirmation = false;

    // If granular void parameters are not provided, void the entire confirmation (all POs)
    if (!items) {
      voidFullConfirmation = true;
      items = _.uniq(
        this.activeConfirmation.lineItems.map(x =>
          x.confirmationSale ? x.confirmationSale.poNum : x.confirmationComment!.poNum
        )
      ).map(poNum => ({ poNum }));
    }

    /* Here we determine if we need to ask the clerk whether or not to cancel unordered parts.
       This is only viable for PREorder daily orders, and NOW POs (a non-PRE po where stockOrder is false).

       Per Kevin, canceling a daily stock order after it's already been converted from a PREorder
       and transmitted to the vendor creates problems when the parts inevitably arrive.  So we
       simply will always default that case to not cancel, despite what other POs in the void batch
       might be set to by the clerk's decision.

       PREorders and NOW pos can be marked for cancellation.  We decide for that subset of POs here in
       an all or nothing form.  If the clerk needs to make different cancellation decisions for different
       NOW POs, for example (perhaps when voiding an entire confirmation of 3 NOW pos), he may cancel them
       one PO at a time.

       Phil only marks a line as a stock order after it's been converted from a PRE order to a PO.

       Note, the legacy system asks the antithetical "Keep parts on order?" rather than "Cancel unordered?".
       We will continue to ask the former for training's sake, and flip it behind the scenes to work with
       the format VOIDPO expects.
    */
    let promptForCancelUnordered = false;

    for (const item of items) {
      // Keep these checks in this order

      // Are only comments being individually voided here (not via full PO void)?
      // No reason to prompt for keeping parts on order on this item.
      // Note the JS trivia: .every returns true for an empty array.
      if (
        Array.isArray(item.voidLineIDs) &&
        item.voidLineIDs.length > 0 &&
        item.voidLineIDs.every(o => o.startsWith('C'))
      ) {
        item.cancelUnordered = 'N';
        continue;
      }

      // This is a "pre" order stocking order, which can still be potentially cancelled.
      if (item.poNum.startsWith('PRE')) {
        promptForCancelUnordered = true;
        continue;
      }

      // If it's not a PRE-order, determine if it's a stocking order or "now" PO, the latter of which can
      // be cancelled. We need an actual part to determine this, not a comment/memo.
      const lineItemPart = this.activeConfirmation.lineItems.find(
        o => o.confirmationSale && o.confirmationSale.poNum === item.poNum
      );
      if (lineItemPart) {
        // We already know this PO isn't a PREorder (first test), so check if it's a stock (daily) order.
        // This instance cannot be cancelled, per Kevin.  It's already been sent to the vendor.
        // It is assumed if any 1 line item on a PO is a stock order, the entire PO is a stock order.
        if (lineItemPart.confirmationSale!.stockOrder) {
          item.cancelUnordered = 'N';

          // Otherwise this is a NOW po, which can still be potentially cancelled.
          // In both individual line void and full PO void cases, we only prompt for cancel
          // unordered if at least 1 part line in the respective set hasn't been fully received.
        } else {
          // Individual items being voided from a NOW PO
          // Recall voidLineIDs are paired in index order 1:1 with uniqueIDs
          if (Array.isArray(item.voidLineIDs)) {
            // If at least one part line item hasn't been fully received from the voided line items
            // Make sure caller provided uniqueIDs too.  If not, assume it's best to prompt.
            if (
              !Array.isArray(item.uniqueIDs) ||
              this.activeConfirmation.lineItems.some(
                o =>
                  o.confirmationSale &&
                  item.uniqueIDs!.includes(o.confirmationSale.uniqueID!) &&
                  o.confirmationSale.poNum === item.poNum &&
                  !o.poFreight &&
                  o.confirmationSale.receivedQty! < o.confirmationSale.confirmationQty
              )
            ) {
              promptForCancelUnordered = true;
            } else {
              item.cancelUnordered = 'N';
            }

            // Void full NOW PO
          } else {
            // if at least one part line item hasn't been fully received from this PO
            if (
              this.activeConfirmation.lineItems.some(
                o =>
                  o.confirmationSale &&
                  o.confirmationSale.poNum === item.poNum &&
                  !o.poFreight &&
                  o.confirmationSale.receivedQty! < o.confirmationSale.confirmationQty
              )
            ) {
              promptForCancelUnordered = true;
            } else {
              item.cancelUnordered = 'N';
            }
          }
        }

        // Must be a comment/memo line only PO, from deduction, pre-fill cancelUnordered
      } else {
        item.cancelUnordered = 'N';
      }
    }

    if (promptForCancelUnordered) {
      const cancelUnordered: 'Y' | 'N' = await new Promise(resolve => {
        modal.confirm({
          title: 'Keep parts on order?',
          message: 'Keep parts on order?', // note the flipped logic to match legacy
          confirmLabels: ['Yes', 'No'],
          callback: () => resolve('N'),
          onClose: () => resolve('Y'),
        });
      });

      // This answer only applies to POs that were not already deterministically set per business rules.
      for (const item of items) {
        if (!item.cancelUnordered) {
          item.cancelUnordered = cancelUnordered;
        }
      }
    }

    // This block removes any item previously added to the invoice builder on void (probabaly rare)
    const fullPOsToVoid = new Set<string>();
    const specificLinesToVoid = new Set<string>();
    let itemsRemoved = 0;

    for (const item of items) {
      if (Array.isArray(item.uniqueIDs)) {
        // voiding at line item level
        item.uniqueIDs.forEach(uniqueID => specificLinesToVoid.add(uniqueID));
      } else {
        // voiding at confirmation or whole PO level
        fullPOsToVoid.add(item.poNum);
      }
    }

    // If this is a full confirmation void and fuel/freight was added from the confirmation, remove it.
    // Note, if this is a granular confirmation void, confirmation fuel/freight added to the screen will remain (unlikely).
    if (voidFullConfirmation) {
      specificLinesToVoid.add(`${confirmationNumber}.FREIGHT`);
      specificLinesToVoid.add(`${confirmationNumber}.FUEL`);
    }

    const indexesToRemove: number[] = [];
    this.lineItems.forEach((lineItem, index) => {
      if (lineItem.confirmationSale || lineItem.confirmationComment) {
        const poNum = this.confirmationLineItemPoNum(lineItem);
        const uniqueID = this.confirmationLineItemUniqueID(lineItem);

        if (fullPOsToVoid.has(poNum) || specificLinesToVoid.has(uniqueID)) {
          indexesToRemove.push(index);
        }
      }
    });

    for (const index of indexesToRemove) {
      this.removeLineItem(index - itemsRemoved, false);
      itemsRemoved++;
    }

    if (itemsRemoved > 0) {
      this.updateTaxes(); // sync here - we don't need to wait
    }
    // End invoice builder update

    const dbpipReq = {
      customerNum: this.activeCustomerID,
      confirmationNumber,
      isRO,
      items,
      ppc: this.ppcParams.ppc,
      roNum: this.activeRONumber || '', // only used by PRO1/SMM3
    };
    let success = true;

    try {
      if (this.logging) {
        console.warn('/voidconfirmation Request');
        console.log(dbpipReq);
      }

      const res = await Api.post('/shopkeeper/voidconfirmation', dbpipReq);
      if (res.status !== 200) {
        throw new Error('200 not received from voidconfirmation');
      }

      if (this.logging) {
        console.warn('/voidconfirmation Response');
        console.log(res.data);
      }

      const dbpipRows = _.get(res, 'data.dbpip', '').split('\n');

      /* Possible errors:
         1. The supplied customer # does not match the confirmation customer #
         2. PO/confirmation already voided? The UI should not give a path to void
           multiple times, but in the event it occurs, the error line will be SVM
           errors for each PO that can't be voided.
         In any case, we'll only toast the first error and log the rest.
      */
      if (_.get(dbpipRows, 12, '') !== '') {
        const errors = dbpipRows[12].split('ý');
        console.log('Confirmation Void Legacy Errors:');
        console.log(errors);
        throw new Error(errors[0]);
      }
      toastr.success('Success', 'Void Successful');
    } catch (err: any) {
      success = false;
      console.warn(err.message);
      toastr.error('Error Voiding Conirmation', err.message);
    }

    return success;
  }

  /* When redemptions are created, they are held in a "to be automatically voided" table
     until they are validated.  This occurs because we must redeem offers before hitting
     crediting, fleet billing, and other routines, which may cause the invoice to never
     actually process.  In such cases, those redemptions will be automatically voided after
     24 hrs during the 1am "sweep".  If an invoice successful processes, this routine
     removes those redemptions from the "to be automatically voided" table.  The table
     is called "LoyaltyRedemptions".

     No reason for this to run synchronously.
  */
  validateRedemptions(redemptionIds: string[], invoiceNum: string) {
    Api.post('/loyalty/redemptions/validateredemptions', { invoiceNum, redemptionIds });
  }

  /**** Utility Methods ****/

  /* Parses raw DBPIP detail row into IDbpipResponse object when inquiring on a single part.
   *
   * Types: Error, Split (fleet supercedence split), Standard, Supercedence (non-fleet chained/split)
   * The order of part response type detection matters, here, so beware if refactoring.
   * See DBPIPResponse.ts for return documenation.
   *
   * This is utilized by individual part inquiries prompted by the end user and checks for far more
   * scenarios than extractDbpipMultiPart(), which "trusts" system loads such as pick lists, quotes, etc.
   */
  extractDbpipSinglePart(dbpip: string): IDbpipResponse {
    const dbpipRows = dbpip.split('\n');

    // Remove trailing empty dbpipRow if it exists (almost always)
    if (dbpipRows[dbpipRows.length - 1] === '') {
      dbpipRows.pop();
    }

    // We should receive at least 1 part row starting at 100
    if (dbpipRows.length < 101) {
      return {
        responseType: DBPIPResponseType.Error,
        errors: ['Invalid Response'],
      };
    }

    // Used in the next 2 response tests
    const firstLineParsed = dbpipRows[100].split('ý');
    const firstLineErrors = _.get(firstLineParsed, 20, '').trim();
    let errors: string[] = [];

    if (firstLineErrors !== '') {
      errors = firstLineErrors.split('ü');
    }

    /* Check for the common case of a single bad part specified.
      If so, tease out any errors (potential for multiple errors)
      < 35 part detail fields indicates incomplete detail, so toast errors.
      Note, the bad part num is typically (so far observed) parroted back in [0].
    */
    if (dbpipRows.length === 101) {
      if (firstLineParsed[0] === '' || firstLineParsed.length < 35) {
        // If we aren't given an error for this case, add one.
        if (firstLineErrors === '') {
          errors.push('BAD PART NUMBER');
        }
        return {
          responseType: DBPIPResponseType.Error,
          errors,
        };
      }
    }

    /* Check for another wack case in which line 1 has an error text at pos 21 including
       'DOES NOT EXIST - CHECK PART' - this could be due to a missing core / fee part.
       Subsequent lines would be malformed such as: ýýýýýýýýýýýýýýýýýýýýBAD PART NUMBER
    */
    if (firstLineParsed[20].includes('DOES NOT EXIST - CHECK PART')) {
      return {
        responseType: DBPIPResponseType.Error,
        errors: ['MISCONFIGURED PART'],
      };
    }

    // Check for invalid responses in which the first part returned is a core or fee
    // such as during Phil's legacy refactor 3/26/18 - this is a worthwhile check
    if (firstLineParsed[0].endsWith('#') || firstLineParsed[0].endsWith('&')) {
      return {
        responseType: DBPIPResponseType.Error,
        errors: ['ERROR: Core/Fee returned in place of part detail'],
      };
    }

    const couponDetail: ICouponDetail[] = [];

    // Detect if this is a forced supercedence response (implied by USE or SUPER in the error text)
    // For non-fleet billing, a "USE" trail must appear on invoice
    let supercedenceResponse = false;
    errors.forEach(error => {
      if (/^(USE |SUPER )/.test(error)) {
        supercedenceResponse = true;
      }
    });

    if (supercedenceResponse) {
      // Build and return a supercedence response for rendering (USE A, USE B, etc.)
      // Note this may be a super A->B supercedence, or a more complicated scenario such
      // as a chained supercedence, a split supercedence, or a mixture of both, possibly
      // in succession (chain -> split -> chain -> split, etc.).

      const partLinesDetails: IPartLineDetail[] = [];
      for (let i = 100; i < dbpipRows.length; i++) {
        // Skip any empty rows between valid response rows or barf happens (Phil bug 3/16)
        if (dbpipRows[i] === '') {
          continue;
        }

        const detail = dbpipRows[i].split('ý');

        // when building a supercedence response, cores/fees are automatically
        // scanned for and parsed by extractPartLineDetail below, so skip here
        if (detail[0].endsWith('#') || detail[0].endsWith('&')) {
          continue;
        }

        // The only @parts logically possible in a stocking part supercedence response would be coupons.
        if (detail[0].startsWith('@')) {
          /* Verify this @ part is indeed in our ATB3 price symbol table.  Different clients can
             use different part @name for coupons (and different price symbols).
          */
          const partNum = detail[0].slice(1).trim();
          if (partNum === '') {
            // we'll skip instead of bomb here (possible coupon bug, etc.)
            continue;
          }

          if (this.defaultParts.has(partNum)) {
            const defaultPart = this.defaultParts.get(partNum)!;
            couponDetail.push({
              quantity: parseInt(detail[1], 10) || 1,
              defaultPartNum: partNum,
              defaultPartNetPrice: parseFloat(detail[5]) || 0.0,
              defaultPartListPrice: parseFloat(detail[6]) || 0.0,
              defaultPartTradePrice: parseFloat(detail[7]) || 0.0,
              defaultPartDescription: detail[4] || defaultPart.desc, // support forced descriptions
              defaultPartSupplementalDetails: detail[24] ? detail[24].split('ü') : undefined,
              stockClass: detail[3] || defaultPart.stockClass,
              priceSymbol: detail[2] || defaultPart.priceSymbol,
            });
          }

          continue;
        }

        // If a part number is missing or fields < 35 per line, this NASA response is busted
        if (detail[0] === '' || detail.length < 35) {
          return {
            responseType: DBPIPResponseType.Error,
            errors: ['Malformed supercedence response.'],
          };
        }

        const extractedDetail = this.extractPartLineDetail(dbpipRows, i);

        // Missing "USE" detection in the supercedence response (NASA bugs)
        // Does not solve missing USE chain problems, but better than no USE detail at all
        if (!~_.findIndex(extractedDetail.errorText, o => /^USE /.test(o))) {
          extractedDetail.errorText.push(`USE ${extractedDetail.partNum}`);
        }

        partLinesDetails.push(extractedDetail);
      }

      // This case is extremely unlikely to happen, but will catch
      // catawampus NASA such as only returning core / fee lines co-mingling USE
      if (partLinesDetails.length === 0) {
        return {
          responseType: DBPIPResponseType.Error,
          errors: ['No valid part lines detected in supercedence response.'],
        };
      }

      return {
        responseType: DBPIPResponseType.Supercedence,
        partLinesDetails,
        couponDetail,
      };
    }

    /* Check for fleet billing forced supercedence split by detecting > 1 part line returned
       in a NON non-fleet billed supercedence case.

       This scan may result in finding only 1 part, which is simply a non-split, valid response.
       This scan also finds any "bad part" lines in the split.  Responses with a single bad
       part line _should_ have been identified above already.

       This scan also detects bound coupons (no subsequent scans required for coupons).
    */
    const splitList = [];
    for (let i = 100; i < dbpipRows.length; i++) {
      // Skip any empty rows between valid response rows or barf happens (Phil bug 3/16)
      if (dbpipRows[i] === '') {
        continue;
      }

      const detail = dbpipRows[i].split('ý');

      /* When building a possible split set, ignore cores/fee lines in determining the
         split set status - these lines will be picked up later when the split is decomposed
         and re-evaluated on each split constituent.
      */
      if (detail[0].endsWith('#') || detail[0].endsWith('&')) {
        continue;
      }

      /* If a part number is missing here, this NASA response is busted.

         It is possible a < 35 "bad" part line will be returned as part of the split.
         The subsequent eval will pick that up accordingly. (Perhaps the part was
         removed from inventory since the split supercedence was created, etc.).
         However, a missing part number here is always bad.
      */
      if (detail[0] === '') {
        return {
          responseType: DBPIPResponseType.Error,
          errors: ['Malformed inquiry response.'],
        };
      }

      /* Skip any coupon lines when determining if the response might be a split set.
         The only @parts logically possible in a stocking part response would be coupons.
         If coupons exist for the split set (in a fleet-billed scenario), they would be
         found on the next (split parts) evaluation iteration.
      */
      if (detail[0].startsWith('@')) {
        /* For perf, if we encounter a coupon on this scan, we can pre-emptively build out the
           coupon array to prevent a subsequent part line scan below.

           Verify this @ part is indeed in our ATB3 price symbol table.  Different clients can
           use different part @name for coupons (and different price symbols).

           Again, this is pre-emptive for perf and not applied to an identfied split set.
        */
        const partNum = detail[0].slice(1).trim();
        if (partNum === '') {
          // we'll skip instead of bomb here (possible coupon bug, etc.)
          continue;
        }

        if (this.defaultParts.has(partNum)) {
          const defaultPart = this.defaultParts.get(partNum)!;
          couponDetail.push({
            quantity: parseInt(detail[1], 10) || 1,
            defaultPartNum: partNum,
            defaultPartNetPrice: parseFloat(detail[5]) || 0.0,
            defaultPartListPrice: parseFloat(detail[6]) || 0.0,
            defaultPartTradePrice: parseFloat(detail[7]) || 0.0,
            defaultPartDescription: detail[4] || defaultPart.desc, // support forced descriptions
            defaultPartSupplementalDetails: detail[24] ? detail[24].split('ü') : undefined,
            stockClass: detail[3] || defaultPart.stockClass,
            priceSymbol: detail[2] || defaultPart.priceSymbol,
          });
        }

        continue;
      }

      splitList.push({
        partNum: detail[0],
        quantity: parseInt(detail[1], 10) || 1, // Important as A may be corrected to N(B)
      });
    }

    /* We should have at least 1 part found, otherwise the response is bad (only cores, fees,
       or coupons found without any corresponding parts).
    */
    if (splitList.length === 0) {
      return {
        responseType: DBPIPResponseType.Error,
        errors: ['No valid part lines detected in response.'],
      };
    }

    /* With a fleet-billing forced split, all we care about is the resultant parts/quantities, which
       we'll ultimately re-eval anyways (to support the case of line item combining for quantity price
       level breaks), so tell the caller to remove the original part and add the resultant "split" parts
       as if it were done manually.

       We would need to see more than 1 part in the collection to satisfy a split condition.
    */
    if (splitList.length > 1) {
      return {
        responseType: DBPIPResponseType.Split,
        splitList,
      };
    }

    // Otherwise we have a 'STANDARD' response which means the response resolved to 1 part
    // The split set scan already checked for "bad" part lines with missing partNums and/or fields < 35
    const partDetail = this.extractPartLineDetail(dbpipRows, 100);

    // COUPON DEMO: Uncomment to emulate attached coupon on part K:2429537 - DEMOS ONLY
    // if (partDetail.partNum === 'K:2429537') {
    //   couponDetail.push({
    //     quantity: partDetail.quantity,
    //     defaultPartNum: 'COUPON',
    //     defaultPartNetPrice: -10.00,
    //     defaultPartListPrice: -10.00,
    //     defaultPartTradePrice: -10.00,
    //     defaultPartDescription: 'MANUFACTURER COUPON',
    //     defaultPartSupplementalDetails: ['GOOD 1/1/19 - 6/1/19'],
    //     stockClass: '9999',
    //     priceSymbol: '163',
    //   });
    // }
    // END COUPON DEMO

    /* A single part may have 1 or more coupons attached (PACCAR feature 6/2018)
       We already scanned all part lines when determining if this was a split supercedence.
       We pre-emptively built the coupon list out at that time.
    */
    return {
      responseType: DBPIPResponseType.Standard,
      partLinesDetails: [partDetail],
      couponDetail,
    };
  }

  /* Parses raw DBPIP detail into pre-evaluated line items ready to be displayed.
   * This is used by "trusted" operations such as loading pick lists, quotes, and bill of materials
   * which load an entire part list at once, wherein QM has already applied inter-line considerations.
   *
   * Basic checks for bad data are applied which can occur on part deletion or part corruption.
   *
   * It enables the loading of a high volume of parts in exactly 2 API calls to D3/QM:
   * - 1 DBPIP call (done external to this method)
   * - 1 DBPIPHELPER call to get supplemental information for all line items.
   *
   * Caller is responsible for adding the line items to the model.
   *
   * Note: extractedLineItems in the response object links each part to its dbpipHelper supplemental
   * information already.  The reason the dbpipHelper map (for all parts) is also returned from this
   * method is due to ex post supercedence inferences.  We do supplemental info (dbpipHelper)
   * lookup for all parts in batch, and scan "error" strings ahead of time for each part line seeking
   * possible forthcoming supercedence chains which we'll inevitably need helper info for, so those
   * supercedence parts are added to the batch for supplemental info call for performance.
   */
  async extractDbpipMultiPartTrusted(
    dbpipRows: string[],
    confirmationExtraction: boolean = false,
    repairOrderExtraction: boolean = false
  ): Promise<{ attachments: IAttachment[][]; dbpipHelper: IDetailSupplemental[]; extractedLineItems: ILineItem[] }> {
    const extractedLineItems: ILineItem[] = [];
    const dbpipHelperBatchItems: string[] = [];
    let dbpipHelper: IDetailSupplemental[] = [];
    let attachments: IAttachment[][] = [];

    // We should receive at least 1 part / memo row starting at 100
    for (let i = 100; i < dbpipRows.length; i++) {
      const row = dbpipRows[i].trim();

      // skip empty rows (tend to be trailing rows, if present)
      if (row === '') {
        continue;
      }

      // is this the -: bill of material line being parroted back? (essentially a bad part error)
      if (row.startsWith('-:')) {
        continue;
      }

      // skip loyalty coupons during RO extraction, they'll be picked up and linked by extractPartLineDetail
      // short circuiting should keep this check from affecting perf parsing other lines
      if (
        repairOrderExtraction &&
        row.startsWith(`@X:${this.sysParams.couponDefaultPartName}`) &&
        row.includes('PACCAR LOYALTY COUPON') &&
        row.includes('LOFFERID')
      ) {
        continue;
      }

      // is this a memo row?
      if (row[0] === '#') {
        // non empty memo only
        if (row.length > 1) {
          if (confirmationExtraction) {
            const rowParts = row.split('ý');
            extractedLineItems.push({
              comment: rowParts[0].slice(1),
              fetched: true,
              quantity: 0,
              partNum: 'LINEITEM_COMMENT',
              confirmationComment: {
                confirmationNum: rowParts[16] || '',
                poNum: rowParts[17] || '',
                uniqueID: rowParts[15] || '',
              },
              confirmationLineSold: (rowParts[19] || '').includes('SOLD'),
              confirmationLineVoided: (rowParts[19] || '').includes('VOIDED'),
            });
          } else if (repairOrderExtraction) {
            const rowParts = row.split('ý');
            const roKey = rowParts[14];
            if (!roKey) {
              console.error(`Part line ${i - 99} (DBPIP line ${i + 1}) missing P.WIPP key.`);
              continue;
            }
            const [, operation, lineNumber] = roKey.split('.');

            extractedLineItems.push({
              comment: rowParts[0].slice(1),
              fetched: true,
              quantity: 0,
              partNum: 'LINEITEM_COMMENT',
              issuedToRO: true,
              roDetail: {
                lineNumber: parseInt(lineNumber, 10) || 0,
                operationNumber: parseInt(operation, 10) || 0,
                roKey,
              },
            });
          } else {
            extractedLineItems.push({
              comment: row.slice(1),
              fetched: true,
              quantity: 0,
              partNum: 'LINEITEM_COMMENT',
            });
          }
        }

        // is this an ATB3 default part / non-stocking part?
      } else if (row[0] === '@' || (repairOrderExtraction && row[0] === '?')) {
        const rowParts = row.split('ý');
        const partNum = rowParts[0].slice(1).trim();
        const unpriced = row[0] === '?';

        // RO Load (PRO1/SMM3): default parts come through as @X:DEFAULTPART
        // Pick Lists & Quotes (PIN1/PRO1/SMM3: default parts come through as @DEFAULTPART
        const defaultPartNumCandidate = (repairOrderExtraction ? partNum.slice(2) : partNum).toUpperCase();

        // malformed data check
        if (partNum === '') {
          continue;
        }

        // Is this an ATB3 default part? load it intelligently with arity knowledge and such.
        // Note, confirmations do not consider default parts
        if (!confirmationExtraction && this.defaultParts.has(defaultPartNumCandidate)) {
          const defaultPart = this.defaultParts.get(defaultPartNumCandidate)!;
          const unpricedOpts = unpriced ? { unpriced: true } : {};

          /* DBPIP provides 1s column of the price symbol which is more specific than what we
             have in our price symbol map (PSU * 10 always leads to a 0).  For now, all we need
             is the 10s version anyways, but one day this may come in handy.  Fallback if necessary.
          */
          const priceSymbol = rowParts[2] || defaultPart.priceSymbol;
          const stockClass = rowParts[3] || defaultPart.stockClass;

          let defaultPartSubletComment = ''; // only used by SUBL/SUBP

          const roOpts: any = {};
          if (repairOrderExtraction) {
            const roKey = rowParts[14];
            if (!roKey) {
              console.error(`Part line ${i - 99} (DBPIP line ${i + 1}) missing P.WIPP key.`);
              continue;
            }
            const [, operation, lineNumber] = roKey.split('.');
            roOpts.issuedToRO = true;
            roOpts.roDetail = {
              lineNumber: parseInt(lineNumber, 10) || 0,
              operationNumber: parseInt(operation, 10) || 0,
              roKey,
            };
          }

          if (defaultPart.desc.toUpperCase().includes('SUBLET')) {
            // See if a PO has been attached to this sublet line (SUBL/SUBP)
            if (repairOrderExtraction && rowParts[9]) {
              roOpts.roDetail.subletPONum = rowParts[9];
            }

            // The next line should be a #memo attached to this sublet line item,
            // but make sure it exists first.
            const defaultPartSubletCommentCandidate = _.get(dbpipRows, i + 1, '');
            if (defaultPartSubletCommentCandidate.startsWith('#')) {
              if (repairOrderExtraction) {
                const subletCommentRowParts = defaultPartSubletCommentCandidate.split('ý');
                defaultPartSubletComment = subletCommentRowParts[0].slice(1);

                const subletCommentRoKey = subletCommentRowParts[14];
                if (!subletCommentRoKey) {
                  console.error(`Part line ${i - 99} (DBPIP line ${i + 1}) missing P.WIPP key.`);
                  // unlike the main part line, we can press on here, the comment just won't update
                }
                roOpts.roDetail.subletCommentRoKey = subletCommentRoKey;
              } else {
                defaultPartSubletComment = defaultPartSubletCommentCandidate.slice(1);
              }

              i++; // skip the row in the next iteration, we've already added it here
            }
          }

          extractedLineItems.push({
            fetched: true,
            partNum: 'LINEITEM_DEFAULTPART',
            quantity: parseInt(rowParts[1], 10) || 1, // most likely only coupons would ever be > 1
            defaultPartNum: defaultPartNumCandidate,
            defaultPartNetPrice: parseFloat(rowParts[5]) || 0.0,
            defaultPartListPrice: parseFloat(rowParts[6]) || 0.0,
            defaultPartTradePrice: parseFloat(rowParts[7]) || 0.0,
            defaultPartDescription: rowParts[4] || defaultPart.desc, // support forced descriptions,
            defaultPartSubletComment,
            defaultPartSupplementalDetails: rowParts[24] ? rowParts[24].split('ü') : undefined,
            stockClass,
            priceSymbol,
            ...roOpts,
            ...unpricedOpts,
          });

          // otherwise @nonstockingpart that isn't a default part
        } else {
          // PRO1/SMM3 unpriced parts will not appear on a confirmation
          if (confirmationExtraction) {
            const confirmationQty = parseInt(rowParts[1], 10) || 1;
            const poFreight = /FREIGHT$/.test(partNum);

            const confirmationNum = rowParts[16] || '';
            const poParts = rowParts[15].split('.');
            const poNum = poParts[0] || '';
            const lineNum = poParts[1] || '';

            const description = poFreight ? `FREIGHT: ${poNum}` : rowParts[4] || partNum;

            // The following works when the spec is correct.  Currently, DBPIP is wack on position 17.
            // const poNum = detail[17] || '';
            // let lineNum = '';

            // const separatorPos = detail[15] ? detail[15].lastIndexOf('.') : -1;
            // if (~separatorPos) {
            //   lineNum = detail[15].slice(separatorPos + 1);
            // }

            // @X:FREIGHT lines truncate at position 17, so special support for bad QM programming.
            let boQty: number, receivedQty: number;
            if (rowParts.length >= 20) {
              boQty = parseInt(_.get(rowParts[19].match(/BOQTY:(\d+)/), 1, '0'), 10) || 0;
              receivedQty = parseInt(_.get(rowParts[19].match(/RECQTY:(\d+)/), 1, '0'), 10) || 0;
            } else {
              boQty = 0;
              receivedQty = 0;
            }

            extractedLineItems.push({
              fetched: true,
              partNum: 'LINEITEM_NONSTOCKINGPART',
              quantity: confirmationQty,
              nonStockingPartNum: partNum,
              nonStockingPartNetPrice: parseFloat(rowParts[5]) || 0.0,
              nonStockingPartListPrice: parseFloat(rowParts[6]) || 0.0,
              nonStockingPartTradePrice: parseFloat(rowParts[7]) || 0.0,
              nonStockingPartDescription: description,
              priceSymbol: rowParts[2],
              stockClass: rowParts[3],
              poFreight,
              confirmationSale: {
                action: 'R', // @parts always use R
                confirmationNum,
                confirmationQty,
                lineNum,
                poNum,
                boQty,
                receivedQty,
                uniqueID: `${confirmationNum}-${poNum}-${lineNum}`,
              },
              confirmationLineSold: (rowParts[19] || '').includes('SOLD'),
              confirmationLineVoided: (rowParts[19] || '').includes('VOIDED'),
            });
          } else if (repairOrderExtraction) {
            const roKey = rowParts[14];
            if (!roKey) {
              console.error(`Part line ${i - 99} (DBPIP line ${i + 1}) missing P.WIPP key.`);
              continue;
            }
            const [, operation, lineNumber] = roKey.split('.');
            const unpricedOpts = unpriced ? { unpriced: true } : {};

            extractedLineItems.push({
              fetched: true,
              partNum: 'LINEITEM_NONSTOCKINGPART',
              quantity: parseInt(rowParts[1], 10) || 1,
              nonStockingPartNum: partNum,
              nonStockingPartNetPrice: parseFloat(rowParts[5]) || 0.0,
              nonStockingPartListPrice: parseFloat(rowParts[6]) || 0.0,
              nonStockingPartTradePrice: parseFloat(rowParts[7]) || 0.0,
              nonStockingPartDescription: rowParts[4] || partNum,
              priceSymbol: rowParts[2],
              stockClass: rowParts[3],
              issuedToRO: true,
              roDetail: {
                lineNumber: parseInt(lineNumber, 10) || 0,
                operationNumber: parseInt(operation, 10) || 0,
                roKey,
              },
              ...unpricedOpts,
            });
          } else {
            extractedLineItems.push({
              fetched: true,
              partNum: 'LINEITEM_NONSTOCKINGPART',
              quantity: parseInt(rowParts[1], 10) || 1,
              nonStockingPartNum: partNum,
              nonStockingPartNetPrice: parseFloat(rowParts[5]) || 0.0,
              nonStockingPartListPrice: parseFloat(rowParts[6]) || 0.0,
              nonStockingPartTradePrice: parseFloat(rowParts[7]) || 0.0,
              nonStockingPartDescription: rowParts[4] || partNum,
              priceSymbol: rowParts[2],
              stockClass: rowParts[3],
            });
          }
        }

        continue;

        // else check for a valid stocking part line and add to forthcoming DBPIPHELPER call
      } else {
        /* Replace with actual DBPIPHELPER data after all parts in the list have been scanned
           (1 batch call to DBPIPHELPER for performance).  This placeholder serves as a default
           in the event no DBPIPHELPER data is returned (future NASA bug, etc.).
        */
        const detailSupplemental: IDetailSupplemental = {
          alternateDivisions: [],
          branchInventory: [],
          cachedBranchTotals: {},
          coresOnHand: '',
          crossReferences: [],
          dirtyCoreDesc: '',
          customerInquiryMemo: [],
          inquiryMemo: [],
          invoicePartMemo: [],
          monthsNoSale: '',
          noForcedPricing: '',
          onHand: '',
          onHold: [],
          onOrder: [],
          status: 'not found',
          stockClassName: '',
          substitutions: [],
          supercedence: [],
          supercedenceComments: [],
          unitPack: '',
        };

        const rowParts = row.split('ý');

        let partNum = rowParts[0].trim();

        // malformed data check
        if (partNum === '') {
          continue;
        }

        // Forced parts are in the format *PARTNUM at position 0
        let forcedPart = false;
        if (partNum.startsWith('*')) {
          partNum = partNum.slice(1);
          forcedPart = true;
        }

        /* Check for a "bad" stocking part row (< 35 fields), most likely deleted from P.INV
           Example: 'W:16-18708-000ýW:16-18708-000ýýýýýýýýýýýýýýýýýýýBAD PART NUMBERüSUB W:TDA-85724 W:MAE-65490  '
           In the case of loading a saved list, show this as a line item whether or not there are helper alternatives.
        */
        if (rowParts.length < 35) {
          let incompleteDetailErrors: string[] = [];
          const errors = _.get(rowParts, 20, '').trim();

          if (errors === '') {
            incompleteDetailErrors.push('BAD PART NUMBER');
          } else {
            incompleteDetailErrors = errors.split('ü');
          }

          if (confirmationExtraction) {
            console.error(`Confirmation part ${partNum} loaded with incomplete detail. Any QM errors will follow.`);
            console.log(incompleteDetailErrors);
            continue;
          }

          if (repairOrderExtraction) {
            console.error(`Repair order part ${partNum} loaded with incomplete detail. Any QM errors will follow.`);
            console.log(incompleteDetailErrors);
            continue;
          }

          extractedLineItems.push({
            fetched: true,
            partNum,
            quantity: 1,
            detail: null,
            detailSupplemental,
            incompleteDetailErrors,
          });

          continue;
        }

        // skip core and fee lines, they'll be picked up by extractPartLineDetail
        if (partNum !== '' && !partNum.endsWith('#') && !partNum.endsWith('&')) {
          const detail = this.extractPartLineDetail(dbpipRows, i, repairOrderExtraction);
          // original row fields still available on rowParts

          // push it to extractedLineItems here (see LineItem.ts)
          const item: ILineItem = {
            fetched: true,
            partNum,
            quantity: detail.quantity || 1,
            detail,
            detailSupplemental,
            incompleteDetailErrors: null,
            couponDetail: [],
            extendedFetchingDetails: '',
            forcedPricing: false,
            forcedDescription: '',
            forcedEntry: false,
            initialForceModalRan: false,
            showPartAlternatives: false,
          };

          if (forcedPart || confirmationExtraction) {
            item.forcedPricing = true;
            item.forcedPricingFromQuoteOrConfirmation = true;
            item.forcedListPrice = item.detail!.listPrice;
            item.forcedNetPrice = item.detail!.netPrice;
            item.forcedTradePrice = item.detail!.tradePrice;
          }

          if (confirmationExtraction) {
            const confirmationNum = rowParts[16] || '';
            const poParts = rowParts[15].split('.');
            const sold = (rowParts[19] || '').includes('SOLD');
            const stockOrder = (rowParts[19] || '').includes('STOCKORDER:Y');
            const voided = (rowParts[19] || '').includes('VOIDED');

            const poNum = poParts[0] || '';
            const lineNum = poParts[1] || '';

            // The following works when the spec is correct.  Currently, DBPIP is wack on position 17.
            // const poNum = rowParts[17] || '';
            // let lineNum = '';

            // const separatorPos = rowParts[15] ? rowParts[15].lastIndexOf('.') : -1;
            // if (~separatorPos) {
            //   lineNum = rowParts[15].slice(separatorPos + 1);
            // }

            const boQty = parseInt(_.get(rowParts[19].match(/BOQTY:(\d+)/), 1, '0'), 10) || 0;
            const receivedQty = parseInt(_.get(rowParts[19].match(/RECQTY:(\d+)/), 1, '0'), 10) || 0;

            // Has this part been superceded since the confirmation was created?  This data enables
            // asking if you wish to restore the inventory to the new super part(s) vs. the original part num.
            let supercedences = rowParts[19].match(/SUPER:([^ ]+)/);
            if (supercedences) {
              supercedences = supercedences[1].split(',');
            }

            item.confirmationSale = {
              action: 'S', // this will need to be overridden to "R" for force receiving
              confirmationNum,
              confirmationQty: item.quantity,
              poNum,
              lineNum,
              boQty,
              receivedQty,
              stockOrder,
              supercedences,
              uniqueID: `${confirmationNum}-${poNum}-${lineNum}`,
            };

            item.confirmationLineSold = sold;
            item.confirmationLineVoided = voided;
          }

          if (repairOrderExtraction) {
            const roKey = rowParts[14];
            if (!roKey) {
              console.error(`Part line ${i - 99} (DBPIP line ${i + 1}) missing P.WIPP key.`);
              continue;
            }
            const [, operation, lineNumber] = roKey.split('.');

            item.issuedToRO = true;
            item.roDetail = {
              lineNumber: parseInt(lineNumber, 10) || 0,
              operationNumber: parseInt(operation, 10) || 0,
              roKey,
            };

            if (item.detail && item.detail.coreAttached) {
              item.roDetail['coreRoKey'] = item.detail.coreRoKey;
            }
          }

          extractedLineItems.push(item);

          dbpipHelperBatchItems.push(partNum);

          /* This is a perf hack: scan for possibly needed supercedence detail lookups and go ahead
             and fetch the base part details (supercedence comments) in the batch call to DBPIPHELPER.
             This information may or may not be used based on the fleet status of the customer (if any).
             For non-fleet billed customers, inferred supercedence chains are extracted and the supplemental
             supercedence detail will be duly attached.  This just saves a roundtrip to D3/QM.  It's largely
             possible many parts will have the same base SUPER part.  DBPIPHELPER combines duplicates.
          */
          detail.errorText.forEach(o => {
            if (/^SUPER .+/.test(o)) {
              dbpipHelperBatchItems.push(o.split(' ')[1]); // e.g. SUPER K:BASEPART
            }
          });
        }
      }
    }

    // IF this DBPIPHELPER call fails, no fallback is necessary (this is supplemental info only)
    try {
      if (this.logging) {
        console.warn('/dbpiphelper Request (Batch Mode):');
        console.log(dbpipHelperBatchItems);
      }

      // Hit QM and Postgres in parallel here
      // DBPIPHELPER will automatically combine dupes
      const [resDbpipHelper, resAttachments] = await Promise.all([
        Api.post('/shopkeeper/dbpiphelper', dbpipHelperBatchItems),
        Api.post('/shopkeeper/attachmentsmulti', dbpipHelperBatchItems),
      ]);

      if (resDbpipHelper.status !== 200 || resAttachments.status !== 200) {
        throw new Error('200 not received from dbpiphelper/attachments (batch mode)');
      }

      if (this.logging) {
        console.warn('/dbpiphelper Response (Batch Mode):');
        console.log(resDbpipHelper);
        console.warn('/attachmentsmulti (Batch Mode):');
        console.log(resAttachments);
      }

      attachments = resAttachments.data;
      dbpipHelper = resDbpipHelper.data;
      this.processBranchInventory(dbpipHelper, true);

      /* In the following two cases, dbpipHelper & attachments will only be applied to the
         "base" part in a supercedene case.  Extracting supercedence detail for constituents is done
         when extracting inferences. For example, loadSavedList (pick list, quotes, BOM) needs to
         consider supercedence cases.  Other loads such as repair order loads and confirmation loads
         (which use this method) do not need to consider supercedence inferences.
      */

      // Attach supplemental dbpipHelper info onto the extractedLineItems
      // Only stocking part line items were batched to DBPIPHELPER here
      for (const partNum of Object.keys(dbpipHelper)) {
        extractedLineItems
          .filter(o => o.partNum === partNum) // support > 1 occurrence
          .forEach(o => (o.detailSupplemental = _.cloneDeep(dbpipHelper[partNum])));
      }

      // It's possible the DBPIPHELPER response is incorrect / missing keys, so do attachments separate
      for (const partNum of Object.keys(attachments)) {
        extractedLineItems
          .filter(o => o.partNum === partNum) // support > 1 occurrence
          .forEach(o => (o.attachments = attachments[partNum]));
      }
    } catch (err: any) {
      console.error(err.message);
    }

    // We also return attachments and dbpipHelper data here for extracting inferred supercedence details
    return { attachments, dbpipHelper, extractedLineItems };
  }

  /* Extracts details from a DBPIP part line per the DBPIP spec.
     Additionally scans for and includes core charges & fees,
     (thus the need for all dbpip rows, not just a pre-split dbpip row).
  */
  extractPartLineDetail(dbpipRows: string[], index: number, repairOrderExtraction: boolean = false): IPartLineDetail {
    const detail = dbpipRows[index].split('ý');
    let coreDetail = null;
    let feeDetail = null;
    let loyaltyCouponsApplied = null;

    // support forced parts in core/fee scans
    const partNum = detail[0].startsWith('*') ? detail[0].slice(1) : detail[0];

    /* Scan immediately subsequent part lines for core / fee charges / loyalty coupons (ROs).
       In order to draw bound inferences correctly, the core/fee/loyalty lines must immediately
       follow the main part line.
    */
    for (let i = index + 1; i < dbpipRows.length; i++) {
      // core charge attached?
      if (dbpipRows[i].includes(`${partNum}#`)) {
        coreDetail = dbpipRows[i].split('ý');
        // fee charge attached
      } else if (dbpipRows[i].includes(`${partNum}&`)) {
        feeDetail = dbpipRows[i].split('ý');
        // loyalty coupons on repair order
      } else if (
        repairOrderExtraction &&
        dbpipRows[i].startsWith(`@X:${this.sysParams.couponDefaultPartName}`) &&
        dbpipRows[i].includes('PACCAR LOYALTY COUPON') &&
        dbpipRows[i].includes('LOFFERID')
      ) {
        loyaltyCouponsApplied = dbpipRows[i].split('ý');
        // else we hit a new part - stop the scan
      } else {
        break;
      }
    }

    // Beware of possible NaN on Number fields
    return {
      partNum,
      quantity: parseInt(detail[1], 10),
      priceSymbol: detail[2],
      stockClass: detail[3],
      shortDescription: detail[4],
      netPrice: parseFloat(detail[5]),
      listPrice: parseFloat(detail[6]),
      tradePrice: parseFloat(detail[7]),
      wholesaleComp: detail[8],
      binLocation: detail[9].split('ü'),
      fleetPricingFlag: detail[10],
      heldOrderKey: detail[11],
      creditQuantity: detail[12],
      creditDate: detail[13],
      errorText: detail[20].split('ü'),
      onHand: parseInt(detail[21], 10),
      longDescription: detail[22],
      sellingPrice: parseFloat(detail[23]),
      comments: detail[24].split('ü'),
      grossProfit: parseFloat(detail[25]),
      branchPickTicketQuantity: parseInt(detail[26], 10),
      receiptNetPrice: parseFloat(detail[27]),
      subletDescription: detail[29],
      trade2Price: parseFloat(detail[30]),
      trade3Price: parseFloat(detail[31]),
      trade4Price: parseFloat(detail[32]),
      serialNumberRequired: detail[33] === 'S',
      serialNumbersAvailable: detail[34].split('ü'),
      publishedNetPrice: parseFloat(detail[35]),

      coreAttached: coreDetail !== null,
      coreQuantity: parseInt(_.get(coreDetail, 1, '0'), 10),
      coreDescription: _.get(coreDetail, 4, '') || 'CORE',
      coreNetPrice: parseFloat(_.get(coreDetail, 5, '0.00')),
      coreListPrice: parseFloat(_.get(coreDetail, 6, '0.00')),
      coreTradePrice: parseFloat(_.get(coreDetail, 7, '0.00')),
      corePublishedNetPrice: parseFloat(_.get(coreDetail, 35, '0.00')),
      coreRoKey: _.get(coreDetail, 14, ''), // used by ROs only

      feeAttached: feeDetail !== null,
      feeQuantity: parseInt(_.get(feeDetail, 1, '0'), 10),
      feeDescription: _.get(feeDetail, 4, ''),
      feeNetPrice: parseFloat(_.get(feeDetail, 5, '0.00')),
      feeListPrice: parseFloat(_.get(feeDetail, 6, '0.00')),
      feeTradePrice: parseFloat(_.get(feeDetail, 7, '0.00')),
      feePublishedNetPrice: parseFloat(_.get(feeDetail, 35, '0.00')),

      // used by ROs only
      loyaltyCouponsApplied: loyaltyCouponsApplied !== null,
      loyaltyCouponsQuantity: parseInt(_.get(loyaltyCouponsApplied, 1, '0'), 10),
      loyaltyCouponsRoKey: _.get(loyaltyCouponsApplied, 14, ''),
      loyaltyCouponsPrice: parseFloat(_.get(loyaltyCouponsApplied, 7, '0.00')),
      loyaltyCouponsDisplayInfo: _.get(loyaltyCouponsApplied, 24, ''),
    };
  }

  /* This function flattens complex split or chain+split supercedence scenarios within
     branch inventory results of DBPIPHELPER.  Single part inquiry and batch mode are supported.
  */
  processBranchInventory(dbpipHelper: any, batchMode = false) {
    let x;

    // Because QM gives us a silly data structure
    const flattenBranchInventory = (branchInventory: any) => {
      if (Array.isArray(branchInventory)) {
        for (const [i, el] of branchInventory.entries()) {
          if (Array.isArray(el)) {
            branchInventory[i] = _.flattenDeep(branchInventory[i]);
          }
        }
      }
    };

    /* Supercedence part lines (within the active branch) don't show branch inventory since
       other branches likely will not have the same supercedence chains, per Tom. Thus, branch
       info is only displayed within PIN1 on standard part responses, as well as (potentially)
       errored part lines wherein a part removed from the master part list, for whatever reason,
       still exists elsewhere. Do note, however, that a "standard part" MAY be superceded at
       a destination branch (including as a split supercedence).

       The value of each branch entry total will be a quantity 0+, or NS if the only quantity
       specified for the branch is NS (including a sole split supercedence wherein the "lowest"
       count constituent is NS).  It's important to flatten the branch inventory first before
       injecting cache branch totals.

       Reference email document "New PIN1: Branch Inventory Enhancements" sent 3/28/19 for
       a real-world usage example with screenshots.
    */
    const injectCachedBranchTotals = (y: IDetailSupplemental) => {
      const cacheMap: { [branch: string]: string } = {};

      if (Array.isArray(y.branchInventory)) {
        y.branchInventory.forEach(branchEntry => {
          // If this is a split supercedence, we'll recreate branchEntry in this iteration
          // to be the cumulative results of the constituents before proceeding in our cache map
          // creation in order to DRY since all other logic remains the same.
          if (Array.isArray(branchEntry) && branchEntry.length > 0) {
            const z = branchEntry as IBranchInventory[];
            const location = z[0].location; // all entries should share this value
            let onHand = '';

            // In this case, our onHand accumulation will be the smallest onHand of the
            // constituents, wherein NS < 0.
            z.forEach(constituent => {
              // if this constituent is missing onHand info, skip it
              if (constituent.onHand === '') {
                return;
              }

              // seed to first constitutent result
              if (onHand === '') {
                onHand = constituent.onHand;

                // otherwise onHand has been seeded with something
              } else {
                // if we're already at NS, all other entries will be a no-op
                if (onHand.toUpperCase() !== 'NS') {
                  // NS always dominates
                  if (constituent.onHand.toUpperCase() === 'NS') {
                    onHand = 'NS';
                    // otherwise we should have a numeric comparison here
                  } else {
                    if (parseInt(constituent.onHand, 10) < parseInt(onHand, 10)) {
                      onHand = constituent.onHand;
                    }
                  }
                }
              }
            });

            // If this supercedence result produces a wack accumulation, just skip it
            if (location === '' || onHand === '') {
              return;
            }

            // We only care about location and the accumulated onHand on this injected "faux" result
            branchEntry = {
              bin: '',
              location,
              monthsNoSale: '',
              netPrice: '',
              onHand,
              orderControl: '',
              partNum: '',
              returnCode: '',
              stockCode: '',
            };
          }

          // Does this branch exist in the cacheMap yet?
          if (cacheMap.hasOwnProperty(branchEntry.location)) {
            // Logically, "NS" is a no-op here (won't change an existing quantity nor an initial NS seed)
            if (branchEntry.onHand.toUpperCase() !== 'NS') {
              // We know onHand is numeric here, if first entry was NS, replace with numeric quantity
              if (cacheMap[branchEntry.location].toUpperCase() === 'NS') {
                cacheMap[branchEntry.location] = branchEntry.onHand;
                // Otherwise, add to existing quantity
              } else {
                cacheMap[branchEntry.location] = (
                  (parseInt(cacheMap[branchEntry.location], 10) || 0) + (parseInt(branchEntry.onHand, 10) || 0)
                ).toString();
              }
            }

            // If branch not set in cacheMap yet, set it to whatever the first found value is, possibly NS
          } else {
            cacheMap[branchEntry.location] = branchEntry.onHand;
          }
        });
      }

      y.cachedBranchTotals = cacheMap;
    };

    // Supercedences, list loads
    if (batchMode) {
      x = dbpipHelper as { [partNum: string]: IDetailSupplemental };
      for (const key of Object.keys(x)) {
        flattenBranchInventory(x[key].branchInventory);
        injectCachedBranchTotals(x[key]);
      }
      // Single parts
    } else {
      x = dbpipHelper as IDetailSupplemental;
      flattenBranchInventory(x.branchInventory);
      injectCachedBranchTotals(x);
    }
  }

  // Errors / API failures are popped here.  Screen prints receive an empty [] response on error.
  async printConfirmation(printerID: string, printOptions: IBOPrintOptions, screenPrint: true): Promise<IScreenPrint[]>;
  async printConfirmation(printerID: string, printOptions: IBOPrintOptions, screenPrint: false): Promise<null>;
  async printConfirmation(
    printerID: string,
    printOptions: IBOPrintOptions,
    screenPrint: boolean
  ): Promise<IScreenPrint[] | null> {
    const confirmationNumber = this.showConfirmationModal
      ? this.activeConfirmation!.details.confirmationNumber
      : this.lastBackorderData.confirmationNumber;
    const emptyResponse = screenPrint ? [] : null;

    if (printerID === '' || confirmationNumber === '') {
      console.error('Missing printConfirmation parameters: ');
      console.log(`printerID:${printerID} confirmationNumber:${confirmationNumber}`);
      return emptyResponse;
    }

    const req = {
      confirmationNumber,
      customerID: this.activeCustomerID,
      printOptions,
      ppc: this.ppcParams.ppc,
      printerID,
    };

    try {
      this.lastPrintConfirmationReq = req;
      if (this.logging) {
        console.warn('/printconfirmation Request:');
        console.log(req);
      }

      const res: IApiResponse = await Api.post('/shopkeeper/printconfirmation', req);
      this.lastPrintConfirmationRes = res;
      if (this.logging) {
        console.warn('/printconfirmation Response:');
        console.log(res);
      }

      if (res.status !== 200) {
        throw new Error('200 not received from printconfirmation.');
      }

      // { dbpip, dbpipReq } DBPIP response/request dialog files
      const dbpip = res.data.dbpip.split('\n');
      const dbpipError: string = _.get(dbpip, 12, '').trim();

      if (dbpipError) {
        // Legacy unexpectedy returns multiple errors sometimes.
        const errors = dbpipError.split('ý');
        console.error(errors); // expand on console
        throw new Error(errors[0]); // show the first error (there SHOULD only be 1, but legacy)
      }

      if (screenPrint) {
        const screenPrintData: string = _.get(dbpip, 10, '').trim();
        const url = screenPrintData.split('ý')[2];
        if (url) {
          return [
            {
              title: 'Confirmation',
              url,
            },
          ];
        } else {
          console.error('No screen print URL returned (confirmation).');
          return emptyResponse;
        }
      } else {
        return emptyResponse;
      }
    } catch (err: any) {
      toastr.error('Confirmation Print Error', err.message);
      return emptyResponse;
    }
  }

  // Errors / API failures are popped here.  Screen prints receive an empty [] response on error.
  // TODO: Support per PO flags: remove customer, print by unit pack, multiple copies, e-mail
  async printPurchaseOrders(
    printerID: string,
    printOptions: IBOPrintOptions,
    screenPrint: true,
    standalonePO: boolean
  ): Promise<IScreenPrint[]>;
  async printPurchaseOrders(
    printerID: string,
    printOptions: IBOPrintOptions,
    screenPrint: false,
    standalonePO: boolean
  ): Promise<null>;
  async printPurchaseOrders(
    printerID: string,
    printOptions: IBOPrintOptions,
    screenPrint: boolean,
    standalonePO: boolean
  ): Promise<IScreenPrint[] | null> {
    const poNumbers = standalonePO ? this.lastStandalonePOData.poNumbers : this.lastBackorderData.poNumbers;
    const emptyResponse = screenPrint ? [] : null;

    if (printerID === '' || !Array.isArray(poNumbers) || poNumbers.length === 0) {
      console.error('Missing printPurchaseOrders parameters: ');
      console.log(`printerID:${printerID} poNumbers:${poNumbers}`);
      return emptyResponse;
    }

    const req = {
      poNumbers,
      customerID: this.activeCustomerID,
      ppc: this.ppcParams.ppc,
      printOptions,
      printerID,
    };

    try {
      this.lastPrintPurchaseOrdersReq = req;
      if (this.logging) {
        console.warn('/printpurchaseorders Request:');
        console.log(req);
      }

      const res: IApiResponse = await Api.post('/shopkeeper/printpurchaseorders', req);
      this.lastPrintPurchaseOrdersRes = res;
      if (this.logging) {
        console.warn('/printpurchaseorders Response:');
        console.log(res);
      }

      if (res.status !== 200) {
        throw new Error('200 not received from printpurchaseorders.');
      }

      // { dbpip, dbpipReq } DBPIP response/request dialog files
      const dbpip = res.data.dbpip.split('\n');
      const dbpipError: string = _.get(dbpip, 12, '').trim();

      if (dbpipError) {
        // Legacy unexpectedy returns multiple errors sometimes.
        const errors = dbpipError.split('ý');
        console.error(errors); // expand on console
        throw new Error(errors[0]); // show the first error (there SHOULD only be 1, but legacy)
      }

      if (screenPrint) {
        /* Phil's response is IDýDOCTITLEýURLýDOCTITLEýURL...
           Depending on the request, not all POs will be printed (some may only be e-mailed).
           Note screenPrintDocs will likely be merged with screenPrintDocs from printConfirmation,
           so we do not set the model's screenPrintDocs property directly here.
        */
        const screenPrintDocs: IScreenPrint[] = [];
        const docInfo: string[] = _.get(dbpip, 10, '').trim().split('ý').slice(1);

        // Likely a case where all POs are e-mailed (not necessarly a failure state)
        if (docInfo.length === 0) {
          console.error('No screen print URLs returned. (POs)');
          return emptyResponse;
        }

        // Otherwise we should have DOCTITLE/URL pairs
        if (docInfo.length % 2 !== 0) {
          throw new Error('Invalid screenprint data.');
        }

        for (let i = 0; i < docInfo.length; i += 2) {
          screenPrintDocs.push({
            title: docInfo[i],
            url: docInfo[i + 1],
          });
        }

        return screenPrintDocs;
      } else {
        return emptyResponse;
      }
    } catch (err: any) {
      toastr.error('PO Print Error', err.message);
      return emptyResponse;
    }
  }

  /* After parts are issued to an RO, we need to obtain updated operation/line number data and cannot
     assume we are the only user simultaneously updating the repair order. It's also possible additional operations
     have been added since the last action. So after most actions (issue, return, etc.), we'll refresh
     the data.  This is also an additional action item on the reset dropup in the footer.
  */
  async refreshActiveROData({
    retainIssueQueue,
    includeAttachments = false,
  }: {
    retainIssueQueue: 'NONE' | 'ALLOPS' | 'ALLNONACTIVEOPS';
    includeAttachments?: boolean;
  }) {
    if (this.activeRONumber) {
      await this.loadRepairOrder({
        roNumber: this.activeRONumber,
        refreshActiveROData: true,
        retainIssueQueue,
        includeAttachments,
      });
    }
  }

  // Create, Update, or Delete Bill of Materials List (PIN4)
  async saveBillOfMaterials(listID: string, listTitle: string, deleteList = false) {
    if (this.runMode !== 'PIN4') {
      return;
    }

    const linesToProcess: Array<{
      partNum: string;
      qty: number;
      comment?: string;

      // The following are for default part support only
      forcedDescription?: string;
      forcedPricing?: {
        forcedNetPrice: number;
        forcedListPrice: number;
        forcedTradePrice: number;
      };
      priceSymbol?: string;
      stockClass?: string;
    }> = [];

    if (!deleteList) {
      // Currently we only support comments and stocking parts on BOM
      for (const lineItem of this.lineItems) {
        if (lineItem.partNum === 'LINEITEM_COMMENT') {
          linesToProcess.push({
            partNum: 'LINEITEM_COMMENT',
            qty: 1,
            comment: lineItem.comment,
          });
        } else if (lineItem.partNum === 'LINEITEM_DEFAULTPART') {
          linesToProcess.push({
            partNum: `@X:${lineItem.defaultPartNum}`,
            qty: lineItem.quantity,
            forcedDescription: lineItem.defaultPartDescription,
            // DBPIP accounts for reverse signs
            forcedPricing: {
              forcedNetPrice: Math.abs(lineItem.defaultPartNetPrice!),
              forcedListPrice: Math.abs(lineItem.defaultPartListPrice!),
              forcedTradePrice: Math.abs(lineItem.defaultPartTradePrice!),
            },
            priceSymbol: lineItem.priceSymbol,
            stockClass: lineItem.stockClass,
          });

          // I can't imagine why anyone would put a SUBL/SUBP on a Bill of Materials, but just in case
          if (lineItem.defaultPartSubletComment !== '') {
            linesToProcess.push({
              partNum: 'LINEITEM_COMMENT',
              qty: 1,
              comment: lineItem.defaultPartSubletComment!,
            });
          }
        } else if (lineItem.supercedenceDetails || lineItem.detail) {
          linesToProcess.push({
            partNum: lineItem.partNum,
            qty: lineItem.quantity,
          });
        }
      }

      this.lastBOMLoad = {
        listID,
        listTitle,
      };
    }

    this.modelLocked = true;

    let res: IApiResponse;

    try {
      this.lastSaveBillOfMaterialsReq = {
        ppc: this.ppcParams.ppc,
        deleteList,
        linesToProcess,
        listID,
        listTitle,
      };

      if (this.logging) {
        console.warn('/savebillofmaterials Request:');
        console.log(this.lastSaveBillOfMaterialsReq);
      }

      res = await Api.post('/shopkeeper/savebillofmaterials', this.lastSaveBillOfMaterialsReq);
      this.lastSaveBillOfMaterialsRes = res;

      if (res.status !== 200) {
        throw new Error('200 not received from savebillofmaterials');
      }
    } catch (err: any) {
      this.modelLocked = false;
      console.error(err.message);
      toastr.error('Error', 'Error');
      return;
    }

    if (this.logging) {
      console.warn('/savebillofmaterials Response:');
      console.log(this.lastSaveBillOfMaterialsRes);
    }

    const dbpipRows = res.data.dbpip.split('\n');
    const legacyError = _.get(dbpipRows, 12, '').trim();
    if (legacyError) {
      toastr.error('Error', legacyError);
    } else {
      toastr.success('Success', deleteList ? 'Deleted' : 'Saved');
    }

    this.modelLocked = false;
  }

  setActiveOperationNumber(operationNumber: number) {
    if (operationNumber === 0 || this.activeRepairOrderDetails.operationDetails[operationNumber]) {
      this.activeOperation = operationNumber;
    } else {
      toastr.error('Error', 'Operation does not exist.');
    }
  }

  /**** TESTING SEED - prevent needing to manually key in / scan parts: sk.model.seed() ****/

  async seed() {
    if (this.modelLocked) {
      toastr.error('Wait', 'Fetch action in process. Please try again in a moment.');
      return;
    }

    this.modelLocked = true;
    await this.addStockingPartLineItem('2404863', 'K', 1); // complex split supercedence
    await this.addStockingPartLineItem('WEL-742902', 'X', 1); // core & fee charge
    await this.addStockingPartLineItem('8200308', 'A', 1); // subs / alt divs
    await this.addStockingPartLineItem('16-18708-000', 'W', 1); // bad part with subs
    await this.addStockingPartLineItem('sp-5-280x', 'W', 1); // alt divs / alt branches
    await this.addStockingPartLineItem('3018068RX', 'N', 1); // core charge
    await this.addStockingPartLineItem('FF5632', 'C', 1); // fee charge
    await this.addStockingPartLineItem('5974', 'I', 1); // 0 on hand with subs
    await this.addStockingPartLineItem('3991624', 'C', 1); // inquiry memo
    this.modelLocked = false;

    scrollInvoiceBuilder();
  }
}

export default ShopKeeperModel;
